import { translateApiMessage } from "./apiMessages";
import type {
  DemoRequestCounts,
  DemoRequestItem,
  DemoRequestPayload,
  DemoRequestStatus,
} from "./demoRequest";
import { resolveLiveToken, SESSION_EXPIRED_EVENT } from "./sessionToken";

/**
 * Minimal API client for the Cymergold Tenis V2 backend.
 * Base URL comes from VITE_API_BASE_URL; otherwise it follows the Vite app base
 * path so subpath deployments like /tenis/ call /tenis/api/v2 instead of
 * localhost.
 */

const appBasePath = import.meta.env.BASE_URL.replace(/\/$/, "");
const DEFAULT_BASE_URL = `${appBasePath}/api/v2`;

export const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ||
  DEFAULT_BASE_URL;

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;

  constructor(params: {
    status: number;
    message: string;
    code?: string;
    details?: Record<string, unknown>;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.status = params.status;
    this.code = params.code ?? "http_error";
    this.details = params.details ?? {};
  }
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions extends Omit<RequestInit, "method" | "body"> {
  method?: HttpMethod;
  body?: unknown;
  token?: string | null;
}

function formatValidationError(details: Record<string, unknown>): string | null {
  const errors = details.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const first = errors[0] as Record<string, unknown>;
  const loc = Array.isArray(first.loc)
    ? first.loc.filter((part) => part !== "body").join(".")
    : "campo";
  const msg = typeof first.msg === "string" ? first.msg : "valor inválido";
  return `${loc}: ${msg}`;
}

async function parseError(response: Response): Promise<never> {
  let code = "http_error";
  let message = response.status === 413
    ? "El archivo o la inscripción completa excede el límite permitido. Intenta subir imágenes más ligeras o PDFs comprimidos."
    : `Request failed with status ${response.status}`;
  let details: Record<string, unknown> = {};
  try {
    const body = await response.json();
    if (body && typeof body === "object" && "error" in body) {
      const err = (body as { error?: Record<string, unknown> }).error ?? {};
      code = typeof err.code === "string" ? err.code : code;
      message = typeof err.message === "string" ? err.message : message;
      details =
        typeof err.details === "object" && err.details !== null
          ? (err.details as Record<string, unknown>)
          : details;
      if (code === "validation_error") {
        message = formatValidationError(details) ?? message;
      }
    }
  } catch {
    // Non-JSON error body — fall back to defaults.
  }
  // Mensajes heredados en inglés del backend → español (lib/apiMessages.ts).
  throw new ApiError({ status: response.status, message: translateApiMessage(message), code, details });
}

/**
 * Open an authenticated file (e.g. a club membership receipt) in a new tab.
 * Receipts are served only through the bearer-authenticated API, so a plain
 * anchor won't work — we fetch the bytes with the token and hand the tab a blob
 * URL. The window is opened synchronously first to survive popup blockers.
 */
export async function openAuthenticatedFile(path: string, token: string): Promise<void> {
  const win = window.open("", "_blank");
  try {
    const response = await fetch(
      `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`,
      { headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` } },
    );
    if (!response.ok) {
      await parseError(response);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (win) {
      win.location.href = url;
    } else {
      window.open(url, "_blank", "noopener");
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    if (win) win.close();
    throw error;
  }
}

/** `POST /public/demo-requests`: nunca repite los datos enviados. */
export interface DemoRequestCreateResponse {
  ok: true;
}

export interface DemoRequestListParams {
  status?: DemoRequestStatus | null;
  limit?: number;
  offset?: number;
}

export interface DemoRequestListResponse {
  ok: boolean;
  data: {
    items: DemoRequestItem[];
    /** Cuántas hay en cada estado (sin importar el filtro). */
    counts: DemoRequestCounts;
    /** Cuántas coinciden con el filtro (para paginar). */
    total: number;
  };
}

export interface DemoRequestUpdateResponse {
  ok: boolean;
  data: { item: DemoRequestItem };
}

export interface SupportTicket {
  id: number;
  category: string;
  summary: string;
  detail: string;
  status: "open" | "resolved" | string;
  resolution_note: string | null;
  context_page: string | null;
  tournament_id: number | null;
  tournament_name: string | null;
  created_by_name: string | null;
  created_by_email: string | null;
  created_at: string | null;
  resolved_at: string | null;
}

/** Un 401 por TOKEN rechazado (vencido o revocado) = la sesión caducó. Otros
 * 401 (contraseña actual incorrecta, etc.) no lo son: se distingue por el
 * mensaje del backend, leyendo una copia para no consumir la respuesta. */
async function notifyIfSessionExpired(response: Response, token: string | null | undefined): Promise<void> {
  if (response.status !== 401 || !token || typeof window === "undefined") return;
  let message = "";
  try {
    const payload = (await response.clone().json()) as { error?: { message?: string } };
    message = payload?.error?.message ?? "";
  } catch {
    message = "";
  }
  if (/token/i.test(message)) {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

// Aviso «No puedes avanzar…» del wizard (Eduardo 2026-10-14): la barra de
// pasos carga el readiness al montar y se quedaba vieja tras asignar grupos,
// generar draws, etc. Cualquier cambio exitoso (POST/PUT/PATCH/DELETE) bajo
// /admin/tournaments/{id}/ avisa con este evento para que se recargue sola.
export const TOURNAMENT_MUTATED_EVENT = "t123:tournament-mutated";

export function notifyTournamentMutated(path: string, method: string): void {
  if (method === "GET" || typeof window === "undefined") return;
  const match = /^\/?admin\/tournaments\/(\d+)\/(.*)$/.exec(path);
  if (!match) return;
  // Registrar el paso visitado no cambia el readiness (y evita doble carga).
  if (match[2].startsWith("setup/progress")) return;
  window.dispatchEvent(
    new CustomEvent(TOURNAMENT_MUTATED_EVENT, { detail: { tournamentId: Number(match[1]) } }),
  );
}

/**
 * Club de UNA llamada al panel del club (cuentas con más de un club). No hay
 * un «club actual» global: cada llamada se hace dentro de
 * `runWithClubScope(clubId, () => api.clubs.x(...))` (ver lib/clubScope.ts)
 * con el club DE LOS DATOS sobre los que actúa. apiFetch lee el valor de forma
 * síncrona al empezar, antes de cualquier await, así que no se mezcla entre
 * peticiones simultáneas.
 */
let scopedClubId: number | null = null;

export function runWithClubScope<T>(clubId: number | null, fn: () => T): T {
  const previous = scopedClubId;
  scopedClubId = clubId;
  try {
    return fn();
  } finally {
    scopedClubId = previous;
  }
}

/** Club de la llamada en curso (solo válido durante runWithClubScope). */
export function currentClubScope(): number | null {
  return scopedClubId;
}

/** Agrega `club_id` a una ruta `/club/...` (explícito > alcance de la llamada). */
export function withClubId(path: string, clubId: number | null = scopedClubId): string {
  if (clubId == null || !path.startsWith("/club/") || path.startsWith("/club/my-clubs")) return path;
  if (/[?&]club_id=/.test(path)) return path;
  return `${path}${path.includes("?") ? "&" : "?"}club_id=${encodeURIComponent(String(clubId))}`;
}

export async function apiFetch<T = unknown>(
  rawPath: string,
  options: RequestOptions = {},
): Promise<T> {
  const path = withClubId(rawPath);
  const { body, token, headers, method = "GET", ...rest } = options;
  const finalHeaders = new Headers(headers ?? {});
  if (body !== undefined && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }
  if (token) {
    finalHeaders.set("Authorization", `Bearer ${resolveLiveToken(token) ?? token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`, {
    method,
    headers: finalHeaders,
    cache: "no-store",
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...rest,
  });

  if (!response.ok) {
    // Sesión caducada a media operación (Eduardo 2026-09-18): se avisa a la
    // app para que ofrezca renovar SIN recargar (recargar pierde lo escrito).
    // Solo si la petición llevaba token: un 401 del login no es "caducó".
    await notifyIfSessionExpired(response, token);
    await parseError(response);
  }

  notifyTournamentMutated(path, method);

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}


async function apiUpload<T = unknown>(
  rawPath: string,
  formData: FormData,
  token?: string | null,
  signal?: AbortSignal,
): Promise<T> {
  const path = withClubId(rawPath);
  const headers = new Headers();
  if (token) {
    headers.set("Authorization", `Bearer ${resolveLiveToken(token) ?? token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`, {
    method: "POST",
    headers,
    cache: "no-store",
    body: formData,
    signal,
  });

  if (!response.ok) {
    await notifyIfSessionExpired(response, token);
    await parseError(response);
  }

  return (await response.json()) as T;
}

/** Por qué el propio navegador interrumpió una subida con avance. */
export type UploadAbortReason = "cancelled" | "stalled";

/**
 * La subida no terminó porque SE CORTÓ AQUÍ: la canceló la persona
 * (`cancelled`) o pasó demasiado tiempo sin avanzar ni responder (`stalled`).
 * No es un `ApiError` (el servidor no contestó) ni un error de red (la
 * conexión no se cayó): cada pantalla decide qué decir en cada caso.
 */
export class UploadAbortedError extends Error {
  reason: UploadAbortReason;

  constructor(reason: UploadAbortReason) {
    super(reason === "stalled" ? "Upload stalled" : "Upload cancelled");
    this.name = "UploadAbortedError";
    this.reason = reason;
  }
}

/** Tiempo sin avance ni respuesta tras el cual una subida se da por detenida. */
export const UPLOAD_STALL_TIMEOUT_MS = 45_000;

export interface UploadWithProgressOptions {
  /** Para el botón «Cancelar»: al abortar, la subida se corta. */
  signal?: AbortSignal;
  /** Milisegundos sin avance ni respuesta antes de cortar. 0 = sin vigilancia. */
  stallTimeoutMs?: number;
}

/**
 * Subida con AVANCE (documentos del jugador desde el celular: una foto de
 * varios MB con datos móviles tarda, y sin avance parece que no pasa nada).
 * `fetch` no informa cuánto se ha enviado, así que aquí se usa XHR; la
 * autenticación, el aviso de sesión caducada y el formato del error son los
 * mismos de `apiUpload`. `onProgress` recibe de 0 a 1; si el navegador no
 * informa el avance, simplemente no se llama.
 *
 * Una subida no se queda colgada para siempre: con señal débil la conexión se
 * atora sin cortarse y la barra se congelaba sin salida. Se puede cancelar con
 * `options.signal`, y si pasan `stallTimeoutMs` sin que avance el envío ni
 * llegue respuesta se corta sola. En ambos casos rechaza con
 * `UploadAbortedError`.
 */
export function apiUploadWithProgress<T = unknown>(
  path: string,
  formData: FormData,
  token?: string | null,
  onProgress?: (fraction: number) => void,
  options: UploadWithProgressOptions = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const { signal } = options;
    const stallMs = options.stallTimeoutMs ?? UPLOAD_STALL_TIMEOUT_MS;
    if (signal?.aborted) {
      reject(new UploadAbortedError("cancelled"));
      return;
    }
    const xhr = new XMLHttpRequest();
    let settled = false;
    let abortReason: UploadAbortReason | null = null;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    const abortWith = (reason: UploadAbortReason) => {
      if (settled) return;
      abortReason = reason;
      xhr.abort();
      // `abort()` avisa por `onabort` en el acto; si el navegador no lo hace
      // (petición que aún no salía), se cierra aquí: nunca queda colgada.
      if (!settled) {
        finish();
        reject(new UploadAbortedError(reason));
      }
    };
    const onSignalAbort = () => abortWith("cancelled");
    /** Hubo señal de vida (avance o respuesta): el reloj vuelve a empezar. */
    const watch = () => {
      if (stallTimer !== null) clearTimeout(stallTimer);
      stallTimer = null;
      if (settled || stallMs <= 0) return;
      stallTimer = setTimeout(() => abortWith("stalled"), stallMs);
    };
    const finish = () => {
      settled = true;
      if (stallTimer !== null) clearTimeout(stallTimer);
      stallTimer = null;
      signal?.removeEventListener("abort", onSignalAbort);
    };

    xhr.open("POST", `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`);
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${resolveLiveToken(token) ?? token}`);
    }
    xhr.upload.onprogress = (event) => {
      watch();
      if (onProgress && event.lengthComputable && event.total > 0) {
        onProgress(Math.min(1, event.loaded / event.total));
      }
    };
    xhr.onprogress = watch;
    // Sin conexión o petición cortada: mismo tipo de error que `fetch`, para
    // que cada pantalla lo trate como «Error de red».
    const networkError = () => {
      finish();
      reject(new TypeError("Network request failed"));
    };
    xhr.onerror = networkError;
    xhr.ontimeout = networkError;
    xhr.onabort = () => {
      const reason = abortReason;
      finish();
      reject(reason ? new UploadAbortedError(reason) : new TypeError("Network request failed"));
    };
    xhr.onload = () => {
      finish();
      const status = xhr.status;
      if (status < 200 || status > 599) {
        reject(new TypeError("Network request failed"));
        return;
      }
      if (status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject(new ApiError({ status, message: "El servidor respondió algo inesperado. Intenta otra vez." }));
        }
        return;
      }
      const response = new Response(xhr.responseText, {
        status,
        headers: { "Content-Type": xhr.getResponseHeader("Content-Type") ?? "text/plain" },
      });
      notifyIfSessionExpired(response, token)
        .then(() => parseError(response))
        .catch(reject);
    };
    signal?.addEventListener("abort", onSignalAbort);
    watch();
    xhr.send(formData);
  });
}

export interface AutoNotice {
  id: number;
  kind: string;
  kind_label: string;
  title: string | null;
  recipient_name: string | null;
  /** Por dónde salió. */
  channel: "whatsapp" | "email";
  /** Enmascarado: "···1234". */
  recipient_phone: string | null;
  /** Enmascarado: "a···@dominio.com". */
  recipient_email: string | null;
  /** sent | failed | pending | held (detenido por el candado de pruebas). */
  state: "sent" | "failed" | "pending" | "held";
  /** sent → delivered → read, según el webhook de Meta. */
  provider_status: string | null;
  error_message: string | null;
  created_at: string | null;
  sent_at: string | null;
}

/** Un aviso que el sistema manda solo: cuándo sale y cómo le llega al jugador. */
export interface AutoNoticeCatalogEntry {
  kind: string;
  label: string;
  when: string;
  detail: string;
  sample_title: string;
  sample_body: string;
}

/** Un mensaje del catálogo del torneo, renderizado con los datos reales. */
export interface TournamentMessage {
  key: string;
  label: string;
  when: string;
  detail: string;
  mode: "auto" | "button";
  channels: Array<"app" | "email" | "whatsapp">;
  available: boolean;
  subject: string;
  title: string;
  body: string;
  button_label: string;
  button_url: string;
  /** El correo tal como le llega al jugador (HTML de marca). */
  html: string;
  text: string;
  whatsapp_text: string;
}

/** «Enviármelo de prueba» (Eduardo 2026-10-02): resultado de UN canal. */
export interface MessageTestChannelResult {
  channel: "email" | "app" | "whatsapp";
  status: "sent" | "skipped" | "failed";
  /** Por qué no salió (no_email, not_configured, whatsapp_off, auto_off,
   * not_connected, no_phone, invalid_phone, not_allowlisted, send_failed). */
  reason?: string;
  detail: string;
  /** Correo o celular enmascarado (···1234) al que llegó. */
  to?: string;
  notification_id?: number;
  /** App: el push a los celulares del admin. */
  push?: {
    status: "sent" | "no_subscription" | "not_configured";
    devices: number;
    detail: string;
  };
}

export interface MessageTestResult {
  key: string;
  label: string;
  channels: MessageTestChannelResult[];
}

export interface TournamentMessagesPayload {
  channels: {
    app: boolean;
    email: boolean;
    whatsapp: boolean;
    auto_generate_announcements: boolean;
    auto_send_welcome: boolean;
    email_configured: boolean;
    group_link: boolean;
  };
  messages: TournamentMessage[];
}

export interface AutoNoticesPayload {
  /** Compatibilidad: WhatsApp encendido en el torneo. */
  enabled: boolean;
  provider_configured: boolean;
  channels: {
    auto_generate_announcements: boolean;
    whatsapp: { enabled: boolean; configured: boolean };
    email: { enabled: boolean; configured: boolean };
  };
  catalog: AutoNoticeCatalogEntry[];
  counts: Record<string, number>;
  notices: AutoNotice[];
}

export interface PlayerOwnAccessInfo {
  user_id: number;
  username: string | null;
  email: string | null;
  status: string;
  last_login_at: string | null;
  granted_at: string | null;
}

export interface PlayerOwnAccessStatus {
  player_profile_id: number;
  // false = este admin no dio de alta al jugador: no puede darle ni quitarle
  // «Acceso propio».
  can_manage_account?: boolean;
  // Para explicar el caso anterior: ¿es de otra organización (true) o de
  // ninguna (false: solo soporte)? ¿Ya tiene con qué entrar?
  account_has_owner?: boolean;
  has_account?: boolean;
  is_family_profile: boolean;
  // Sin nombre ni correo (null) si la cuenta también la usan jugadores que
  // este admin no administra (account_has_foreign_profiles).
  guardian: { full_name: string | null; email: string | null } | null;
  account_has_foreign_profiles?: boolean;
  // Solo los hermanos que este admin administra; los demás, contados.
  family_profiles: string[];
  family_profiles_hidden?: number;
  own_access: PlayerOwnAccessInfo | null;
  // «Pasar a su propia cuenta» (Eduardo 2026-10-04): true = su usuario propio
  // ya es su cuenta principal y ninguna otra cuenta lo ve. Con
  // can_manage_account en false siempre llega false (no se le dice).
  in_own_account?: boolean;
  // Si se ofrece el botón; si no, por qué (texto claro del servidor). Sin ser
  // dueño de la cuenta: code «player_account_not_owned» y sin texto.
  move_to_own_account?: { available: boolean; code: string | null; reason: string | null };
}

export interface PlayerMoveToOwnAccountResult {
  player_profile_id: number;
  /** Ya estaba en su propia cuenta: no cambió nada. */
  already: boolean;
  previous_account_id: number | null;
  own_account: PlayerOwnAccessInfo | null;
  notices_moved: number;
  /** Avisos para el admin (p. ej. el correo de la ficha es el de la cuenta anterior). */
  warnings: string[];
}

export interface PlayerOwnAccessGrant {
  player_profile_id: number;
  pin: string;
  username: string | null;
  email: string | null;
  guardian_keeps_access: boolean;
  own_access: PlayerOwnAccessInfo | null;
}

export interface PlayerMergePreview {
  survivor: { id: number; display_name: string; login: string | null };
  duplicate: { id: number; display_name: string; login: string | null };
  moves: Record<string, number>;
  fills: string[];
  warnings: string[];
  blockers: string[];
}

export interface PlayerMergeResult {
  survivor_id: number;
  duplicate_id: number;
  moved: Record<string, number>;
  filled: string[];
  disabled_login: string | null;
}

/** «soporte» = Soporte Torneos123 (Eduardo 2026-10-29): siempre junto con
 * «admin»; entra a todos los torneos, sin dinero. */
export type RoleCode = "superadmin" | "admin" | "player" | "club" | "soporte";

export const SUPPORT_ROLE_LABEL = "Soporte Torneos123";

/** ¿La cuenta es Soporte Torneos123? (el superadmin nunca se limita). La UI
 * solo esconde botones: el servidor es quien niega el dinero. */
export function isSupportUser(user: { roles?: RoleCode[] | null } | null | undefined): boolean {
  const roles = user?.roles ?? [];
  return roles.includes("soporte") && !roles.includes("superadmin");
}

export interface AuthUser {
  id: number;
  full_name: string;
  email: string;
  username?: string | null;
  phone?: string | null;
  roles: RoleCode[];
  status?: string;
  must_change_password?: boolean;
  /** Solo en la lista de admins del superadmin: último ingreso (UTC) o null —
   * lo más reciente entre el inicio de sesión y el uso real avisado por la
   * app, no la renovación sola. */
  last_access_at?: string | null;
  /** Sub-usuario del Equipo del organizador (co-admin, capturista, finanzas).
   * Si viene, no puede cambiar datos globales del jugador (como «Editar
   * registro»): Inscripciones esconde «Usar datos de la CURP». */
  managed_by_user_id?: number | null;
  /** Perfil del Equipo (2026-10-16): «arbitro» abre «Mis partidos» (/arbitro). */
  team_profile?: string | null;
  /** La cuenta opera al menos un perfil de jugador (propio o de la familia).
   * Con un rol de organizador, al entrar elige «Como organizador» o «Como
   * jugador» y el encabezado le ofrece cambiar de modo (Eduardo 2026-10-04). */
  has_player_profile?: boolean;
}

export interface LoginResponse {
  ok: boolean;
  data: {
    access_token: string;
    token_type: string;
    /** Solo jugadores (sin rol de staff): token de renovación de la sesión
     * larga (90 días, deslizante). Se canjea en `api.auth.renew`. */
    refresh_token?: string | null;
    user: AuthUser;
  };
}

export interface MeResponse {
  ok: boolean;
  data: { user: AuthUser };
}

// change-password revoca el token del caller (password_changed_at), así que
// devuelve un access_token fresco igual que login — no un simple MeResponse.
export type ChangePasswordResponse = LoginResponse;

export interface AdminUserResponse {
  ok: boolean;
  data: { user: AuthUser };
}

export interface AdminImpersonationResponse {
  ok: boolean;
  data: {
    access_token: string;
    token_type: string;
    user: AuthUser;
    impersonation: {
      actor_user_id: number;
      admin_user_id: number;
      expires_in_minutes: number;
    };
  };
}

export interface AdminsListResponse {
  ok: boolean;
  data: { admins: AuthUser[] };
}

export type TournamentType = "ladder" | "round_robin" | "elimination";
export type TournamentPlayMode = "singles" | "doubles";
export type SportKey = "tenis" | "padel" | "pickleball";

export interface TournamentVenue {
  name: string;
  google_maps_link: string | null;
  court_count: number | null;
  is_primary: boolean;
}

/** Sede ADICIONAL para el PUT /venues (la principal viaja en venue_name). */
export interface TournamentVenueInput {
  name: string;
  google_maps_link?: string | null;
  court_count?: number | null;
}

export interface Tournament {
  id: number;
  name: string;
  slug: string;
  tournament_type: TournamentType | string;
  play_mode: TournamentPlayMode | string;
  sport: SportKey | string;
  status: string;
  venue_name: string | null;
  /** Todas las sedes (principal primero); ausente en payloads legacy. */
  venues?: TournamentVenue[];
  start_date: string | null;
  end_date: string | null;
  registration_deadline: string | null;
  /** «Cerrar inscripciones ahora» prendido (el organizador cerró sin mover fechas). */
  registrations_closed_now?: boolean;
  registration_cost: string | null;
  official_ball: string | null;
  google_maps_link: string | null;
  /** En el link público anónimo llega en null (ver has_whatsapp_group). */
  whatsapp_group_link: string | null;
  /** Link público: el torneo tiene grupo de WhatsApp (se entrega al quedar pagado). */
  has_whatsapp_group?: boolean;
  is_public: boolean;
  is_federation_official: boolean;
  description: string | null;
  /** Póster/imagen del torneo (la grande del link público y las tarjetas). */
  logo_url: string | null;
  /** Identidad del organizador (Datos): usada en link público, PDFs y credencial. */
  organizer_name: string | null;
  organizer_logo_url: string | null;
  /** Banners de patrocinador: link público (970x250) y vistas del jugador (1200x300). */
  sponsor_banner_url?: string | null;
  player_banner_url?: string | null;
  /** Solo en el link público de inscripción: todos los patrocinadores
   * (imagen, nombre y link), el primero incluido (Eduardo 2026-10-01). */
  sponsor_banners?: Array<{ image_url: string; name: string | null; link: string | null }>;
  // Convocatoria en PDF subida por el organizador (descarga en el link público)
  call_pdf_url?: string | null;
  created_by: number | null;
  duplicated_from_tournament_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  admin_count?: number;
  /** Lista de Torneos (admin): inscritos no dados de baja. */
  registered?: number;
  /** Lista de Torneos (admin): partidos no cancelados / con resultado final. */
  matches_total?: number;
  matches_played?: number;
  /** Lista de Torneos (admin): reportes de resultado pendientes de revisar. */
  pending_reports?: number;
  /** Lista de Torneos: quien la ve puede duplicar este torneo (dueño,
   * admin completo, coadmin o superadmin; nunca capturista ni finanzas). */
  can_duplicate?: boolean;
  /** Quien la ve puede borrar el torneo (no el coadmin). */
  can_delete?: boolean;
  /** Lista de Torneos: lo que el diálogo «Duplicar» anticipa (null si no
   * puede duplicarlo). */
  duplicate_preview?: TournamentDuplicatePreview | null;
}

/** Antes de duplicar (revisión ronda 4): de quién queda la copia y si se
 * lleva la forma de cobro. Misma regla que el servidor al crearla. */
export interface TournamentDuplicatePreview {
  /** La copia queda a nombre de quien duplica. */
  owner_is_you: boolean;
  /** Si no: el organizador que se queda la copia (y cobra a su cuenta). */
  owner_name: string | null;
  /** La copia cobra igual que el original (su dueño lo creó). Si no, la
   * forma de cobro se elige en el paso 1. */
  payment_copied: boolean;
}

export type LadderFrequency = "weekly" | "biweekly" | "monthly" | "custom";
export type LadderSetFormat =
  | "one_set"
  | "two_sets_super_tiebreak"
  | "best_of_3"
  | "best_of_5";

export type LadderRankingScope = "group_current_rank" | "group_initial_rank" | "manual";
export type SetGameType = "normal_6" | "mini_4";
export type LadderMatchupRule = "adjacent_rank" | "cross_rank" | "manual" | "random";
export type LadderDropoutPolicy =
  | "manual_default"
  | "manual_reassign"
  | "bye"
  | "cancel_matchup";

export type LadderWeeklyPattern =
  | "even_challenge_odd"
  | "odd_challenge_even"
  | "even_challenge_odd_offset"
  | "odd_challenge_even_offset"
  | "manual";

export type LadderPlayoffRoundRule =
  | "top_6_preliminary"
  | "top_6_semifinal_reseed"
  | "top_4_semifinal"
  | "top_8_quarterfinal"
  | "semifinal_winners"
  | "final_winners"
  | "manual";

export interface LadderWeeklyPatternEntry {
  week: number;
  pattern: LadderWeeklyPattern;
  offset: number | null;
  bye: boolean;
}

export interface LadderPlayoffRoundEntry {
  week: number;
  round_index: number;
  rule: LadderPlayoffRoundRule;
}

export interface LadderFormatConfig {
  frequency: LadderFrequency;
  round_count: number;
  start_date: string;
  round_duration_days: number;
  matches_per_player_per_round: number;
  match_sets_format: LadderSetFormat;
  set_game_type: SetGameType;
  with_advantage: boolean;
  set_tiebreak_games: number;
  third_set_super_tiebreak_points: number | null;
  playoffs_enabled: boolean;
  playoff_players_count: number;
  playoff_round_count: number;
  playoff_sets_format: LadderSetFormat;
  playoff_rounds: LadderPlayoffRoundEntry[];
  ranking_scope: LadderRankingScope;
  matchup_rule: LadderMatchupRule;
  allow_repeat_opponents: boolean;
  dropout_policy_after_publish: LadderDropoutPolicy;
  requires_admin_publish: boolean;
  weekly_patterns: LadderWeeklyPatternEntry[];
  standings_rules?: StandingsRulesConfig | null;
  special_scoring?: SpecialScoringConfig | null;
}

export type StandingsRulesMode = "legacy" | "itf" | "custom";
export type StandingsTiebreaker =
  | "h2h"
  | "match_pct"
  | "set_pct"
  | "game_pct"
  | "set_diff"
  | "game_diff"
  | "games_won"
  | "fewest_games_lost";

export interface StandingsPointsConfig {
  win: number;
  loss_completed: number;
  walkover_loss: number;
}

// Criterios de desempate configurables (escalera ITF de Eduardo). Sin la
// clave en format_config_json el backend opera en modo legacy.
export interface SuperTiebreakRange {
  min: number;
  max: number | null;
  winner_games: number;
  loser_games: number;
}

export interface SpecialScoringConfig {
  walkover_value: "one_game" | "score" | "win_only";
  walkover_score: string | null;
  defaulted_value: "same_as_walkover" | "score" | "win_only";
  defaulted_score: string | null;
  walkover_win_points: number | null;
  walkover_loss_points: number | null;
  bonus_bagel_set: number;
  bonus_straight_sets: number;
  bonus_set_in_loss: number;
  exceptions_count_in_pcts: boolean;
  retired_mode: "keep" | "complete" | "score" | "win_only";
  retired_score?: string | null;
  super_tiebreak_value: "one_game" | "points" | "ranges";
  super_tiebreak_ranges?: SuperTiebreakRange[] | null;
}

export interface StandingsRulesConfig {
  mode: StandingsRulesMode;
  points?: StandingsPointsConfig | null;
  tiebreakers?: StandingsTiebreaker[];
}

export type RoundRobinFormatMode = "round_robin_only" | "round_robin_playoffs";
export type RoundRobinRankingCriterion =
  | "matches_won"
  | "sets_difference"
  | "games_difference"
  | "games_won"
  | "head_to_head"
  | "manual";
export type RoundRobinQualificationRule =
  | { type: "top_n_per_group"; positions_from: number; positions_to: number }
  | { type: "best_n_by_group_position"; group_position: number; count: number }
  | { type: "best_n_overall"; count: number };
export type RoundRobinSeedMethod = "overall_performance" | "group_position" | "effectiveness" | "manual";
export type RoundRobinBracketPolicy = "auto_preliminary" | "auto_byes" | "manual";
export type RoundRobinGroupScheduleType = "full_round_robin" | "limited_schedule" | "manual_schedule";

export interface RoundRobinGroupStageConfig {
  group_count: number;
  players_per_group: number;
  schedule_type?: RoundRobinGroupScheduleType;
  rounds_per_pair: 1 | 2;
  matches_per_player?: number | null;
  ranking_criteria: RoundRobinRankingCriterion[];
}

export interface RoundRobinQualificationConfig {
  rules: RoundRobinQualificationRule[];
  seed_method: RoundRobinSeedMethod;
  /** Cómo se comparan jugadores de distintos grupos (comodines). Sin la clave: «standings». */
  cross_group_criterion?: "standings" | "effectiveness";
  manual_override: boolean;
}

export interface RoundRobinPlayoffPhaseConfig {
  type: "single_elimination";
  bracket_policy: RoundRobinBracketPolicy;
  manual_seed_edit: boolean;
  /** «Partido por el 3er lugar»: sin la clave el backend lo toma como sí. */
  third_place_match?: boolean;
  /** «Evitar cruces del mismo grupo en la 1.ª ronda»: sin la clave, sí. */
  avoid_same_group_first_round?: boolean;
  sets_format: LadderSetFormat;
  set_game_type?: SetGameType;
  with_advantage?: boolean;
  set_tiebreak_games?: number;
  third_set_super_tiebreak_points?: number | null;
}

export interface RoundRobinFormatConfig {
  schema_version?: 2;
  /** Cuadro de consolación (solo grupos + eliminatoria). */
  consolation_bracket?: boolean;
  consolation?: ConsolationSettings | null;
  format_mode?: RoundRobinFormatMode;
  group_stage?: RoundRobinGroupStageConfig;
  qualification?: RoundRobinQualificationConfig;
  playoff?: RoundRobinPlayoffPhaseConfig;
  rounds_per_pair: 1 | 2;
  frequency: LadderFrequency;
  round_count: number;
  start_date: string;
  round_duration_days: number;
  match_sets_format: LadderSetFormat;
  set_game_type: SetGameType;
  with_advantage: boolean;
  set_tiebreak_games: number;
  third_set_super_tiebreak_points: number | null;
  playoff_players_count: number;
  playoff_round_count: number;
  playoff_sets_format: LadderSetFormat;
  ranking_scope: string;
  notes: string | null;
  standings_rules?: StandingsRulesConfig | null;
  special_scoring?: SpecialScoringConfig | null;
  team_play?: TeamPlayConfig | null;
}

/** Series por equipos (Mini Copa 2026): bloque opcional dentro del
 * format_config_json de una categoría Round Robin o Eliminatoria. */
export type TeamPlayLineKind = "singles" | "doubles" | "mixed";
export type TeamPlayLineupMode = "draw" | "captain" | "fixed";
export interface TeamPlayLine {
  kind: TeamPlayLineKind;
  label?: string;
}
export interface TeamPlayConfig {
  enabled: boolean;
  team_size: number;
  lines: TeamPlayLine[];
  wins_to_take_tie: number;
  lineup_mode: TeamPlayLineupMode;
  default_score: string;
  default_counts_as_played: boolean;
}

/** Cuadro de consolación (maqueta Formato-Consolacion, 2026-10-17). */
export interface ConsolationSettings {
  entry: "first_round" | "first_two_rounds";
  group_entry: "all" | "best_n";
  best_n: number;
  start: "round_complete" | "progressive";
  third_place: boolean;
  include_walkover: boolean;
}

export interface EliminationFormatConfig {
  bracket_size: number;
  bracket_round_count: number;
  first_round_bye_count: number | null;
  seeding_source: "initial_rank" | "current_rank" | "manual";
  default_generation_mode: "ranking" | "random";
  match_sets_format: LadderSetFormat;
  set_game_type: SetGameType;
  with_advantage: boolean;
  set_tiebreak_games: number;
  third_set_super_tiebreak_points: number | null;
  consolation_bracket: boolean;
  consolation?: ConsolationSettings | null;
  /** «Partido por el 3er lugar»: sin la clave el backend lo toma como sí. */
  third_place_match?: boolean;
  notes: string | null;
  team_play?: TeamPlayConfig | null;
}

export type TournamentFormatConfigJson =
  | LadderFormatConfig
  | RoundRobinFormatConfig
  | EliminationFormatConfig
  | Record<string, unknown>;


export interface TournamentDivision {
  id: number;
  tournament_id: number;
  name: string;
  code: string | null;
  gender: string | null;
  play_mode: TournamentPlayMode;
  age_min: number | null;
  age_max: number | null;
  level_label: string | null;
  category_type: string | null;
  max_participants: number | null;
  registration_cost: string | null;
  /** Cierre de inscripciones propio (AAAA-MM-DD); null = el del torneo. */
  registration_deadline?: string | null;
  format_type: TournamentType | null;
  format_config_json?: Record<string, unknown> | null;
  scoring_config_json?: Record<string, unknown> | null;
  sort_order: number;
  is_active: boolean;
  /** Ajustes del calendario de ESTA categoría (Bloque min, #Canchas, Desde y
   * Último inicio): cada categoría guarda los suyos (Eduardo 2026-10-01). */
  calendar_settings_json?: DivisionCalendarSettings | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Ajustes del calendario de una categoría; todo opcional. */
export interface DivisionCalendarSettings {
  /** Bloque min (10–600): lo que dura cada partido de la categoría. */
  match_duration_minutes?: number | null;
  /** #Canchas (1–50) de la sede principal. */
  court_count?: number | null;
  /** Desde (HH:MM). */
  start_time?: string | null;
  /** Último inicio (HH:MM, inclusive). */
  end_time?: string | null;
  /** Tipo de cancha (Formato): solo se guarda "mini"; Normal = sin la llave.
   * Se manda "normal" (o null) para quitarla. */
  court_type?: "normal" | "mini" | null;
  /** Mini canchas: por sede ("" = principal; las demás con venueKey), las
   * canchas divididas en A y B: { "": ["9", "10"] }. Una lista vacía quita
   * esa sede. */
  split_courts?: Record<string, string[]> | null;
  /** En cuántas mini canchas se parte cada cancha dividida, por sede: solo
   * 3 o 4 ({ "": { "9": 3 } }); sin entrada = 2 (A y B). Lo arma el
   * servidor; para cambiarlo se manda `split_parts_set`. */
  split_parts?: Record<string, Record<string, number>> | null;
}

/** Lo que se manda al guardar los ajustes: los mismos campos y, para una
 * casilla de «Canchas divididas», solo su CAMBIO por sede (agregar o quitar
 * de la lista guardada) en lugar de la lista completa: así una pestaña vieja
 * no borra la cancha que otra acaba de marcar. */
export interface DivisionCalendarSettingsUpdate extends DivisionCalendarSettings {
  split_courts_add?: Record<string, string[]>;
  split_courts_remove?: Record<string, string[]>;
  /** Partes de una cancha dividida (2, 3 o 4) por sede: { "": { "9": 3 } }. */
  split_parts_set?: Record<string, Record<string, number>>;
}

export interface DivisionCalendarSettingsResponse {
  ok: boolean;
  data: {
    division?: TournamentDivision | null;
    calendar_settings_json?: DivisionCalendarSettings | null;
  };
}

export type TournamentDivisionInput = Partial<Omit<TournamentDivision, "id" | "tournament_id" | "created_at" | "updated_at">> & {
  name: string;
  /** Modalidad "Por equipos" desde Datos: {enabled:true} enciende la fórmula
   * Mini Copa en format_config_json; null la apaga. Se afina en el Paso 5. */
  team_play?: { enabled: boolean } | null;
};

export interface TournamentDivisionsResponse {
  ok: boolean;
  data: { divisions: TournamentDivision[] };
}

export interface TournamentDivisionResponse {
  ok: boolean;
  data: {
    division: TournamentDivision;
    /** true = esta categoría (la primera de menores) prendió los datos del
     * papá, mamá o tutor en el formulario (Eduardo 2026-10-03). */
    guardian_fields_auto_enabled?: boolean;
  };
}

export interface TournamentFormatConfig {
  tournament_id: number;
  format_type: TournamentType;
  config_json: TournamentFormatConfigJson;
  created_at: string | null;
  updated_at: string | null;
}

export interface TournamentFormatConfigResponse {
  ok: boolean;
  data: { format_config: TournamentFormatConfig };
}

export interface TournamentFormatConfigUpdateInput {
  format_type: TournamentType;
  config_json?: Record<string, unknown> | null;
}

export interface TournamentSettings {
  tournament_id: number;
  scoring_type: string | null;
  best_of_sets: number | null;
  games_per_set: number | null;
  tie_break_rule: string | null;
  allow_player_result_reporting: boolean;
  uses_court_calendar: boolean;
  result_reporting_mode: string;
  auto_recalc_on_results: boolean;
  auto_recalc_on_join: boolean;
  auto_recalc_on_drop: boolean;
  allow_post_publish_changes: boolean;
  auto_generate_announcements: boolean;
  email_notifications_enabled: boolean;
  whatsapp_notifications_enabled: boolean;
  /** Bienvenida automática al aprobar la inscripción (Eduardo 2026-09-23). */
  auto_send_welcome?: boolean;
  app_first_mode: boolean;
  /** «Aprobar solo cuando los dos reportan lo mismo» (Eduardo 2026-10-06). */
  auto_approve_matching_reports?: boolean;
  publication_policy_json: Record<string, unknown> | null;
  /** «¿Administrar árbitros en la plataforma?» (paso 4, Eduardo 2026-10-17):
   *  valor efectivo (guardado o deducido de si ya hay árbitros). */
  referees_enabled?: boolean;
}

export interface TournamentAdminAssignment {
  id: number;
  tournament_id: number;
  user_id: number;
  role_in_tournament: string | null;
}

export interface TournamentListResponse {
  ok: boolean;
  data: { tournaments: Tournament[] };
}

/** Preview del borrado superadmin (#166): qué se destruiría, sin tocar nada. */
export interface SuperadminDeletionPreview {
  tournament: {
    id: number;
    name: string;
    slug: string;
    status: string;
    start_date: string | null;
    end_date: string | null;
  };
  counts: {
    participants: number;
    matches: number;
    registration_orders: number;
    paid_orders: number;
    ranking_point_events: number;
    ranking_links: number;
    checkout_sessions: number;
    draw_ceremonies: number;
  };
  warnings: string[];
  preserved: string;
}

export interface SuperadminDeletionPreviewResponse {
  ok: boolean;
  data: SuperadminDeletionPreview;
}

export interface SuperadminDeleteTournamentResponse {
  ok: boolean;
  data: {
    deleted: boolean;
    tournament_id: number;
    tournament_name: string;
    deleted_counts: SuperadminDeletionPreview["counts"];
  };
}

export interface TournamentResponse {
  ok: boolean;
  data: { tournament: Tournament };
}

/** Aviso «Copia de …» del torneo duplicado (Eduardo 2026-10-03). También
 * queda en `settings.publication_policy_json.copied_from` de la copia. */
export interface TournamentCopyNotice {
  tournament_id: number;
  name: string;
  /** Grupos de WhatsApp del original que NO pasaron a la copia. */
  whatsapp_groups_removed: number;
  /** El original puntuaba para un ranking (la copia no se liga sola). */
  ranking_linked: boolean;
  /** El original tenía PDF propio de convocatoria (no pasa: trae fechas viejas). */
  call_pdf_removed: boolean;
  /** Años que se movió el inicio; las categorías conservan sus años de nacimiento. */
  birth_year_shift: number | null;
  /** La forma de cobro no se copió: el original lo cobraba otra persona. */
  payment_setup_reset: boolean;
  /** La comisión default no cabía en los costos copiados: Torneos123 la revisa. */
  platform_fee_review: boolean;
}

export interface TournamentDuplicateInput {
  name: string;
  start_date: string;
  end_date: string;
  registration_deadline: string;
}

export interface TournamentDuplicateResponse {
  ok: boolean;
  data: { tournament: Tournament; copy_notice: TournamentCopyNotice };
}

export interface TournamentSetupStatus {
  is_incomplete_setup: boolean;
  next_setup_path: string | null;
  /** Wizard reanudable: paso más avanzado que el admin abrió (1..10). */
  setup_step_reached?: number;
  /** Con `setup_step_reached`: el primer paso que impide publicar
   * (Formato, Grupos o Draw), o null si ya se puede publicar. */
  pending_setup_path?: string | null;
  counts: Record<string, number>;
  pending_division_formats?: string[];
}

/** Patrocinador del torneo (Eduardo 2026-10-01: varios por torneo). El
 * primero es el banner de siempre; `player_image_url` es la imagen de las
 * vistas del jugador (en los extra, la misma). */
export interface TournamentSponsorBanner {
  image_url: string;
  player_image_url?: string | null;
  name: string | null;
  link: string | null;
}

/** Grupo de WhatsApp del torneo (varios por torneo, con nombre opcional). */
export interface TournamentWhatsappGroup {
  label: string | null;
  url: string;
}

export interface SponsorBannersInput {
  first_name: string | null;
  first_link: string | null;
  extra: Array<{ image_url: string; name: string | null; link: string | null }>;
}

export interface WhatsappGroupsInput {
  first_label: string | null;
  extra: Array<{ label: string | null; url: string }>;
}

export interface TournamentDetailResponse {
  ok: boolean;
  data: {
    tournament: Tournament;
    settings: TournamentSettings;
    /** Listas completas, el primero incluido (Eduardo 2026-10-01). */
    sponsor_banners?: TournamentSponsorBanner[];
    whatsapp_groups?: TournamentWhatsappGroup[];
    format_config: TournamentFormatConfig;
    admins: TournamentAdminAssignment[];
    divisions?: TournamentDivision[];
    setup_status: TournamentSetupStatus;
    /** Quien lo ve puede duplicarlo (ver Tournament.can_duplicate). */
    can_duplicate?: boolean;
    duplicate_preview?: TournamentDuplicatePreview | null;
    /** Inscripciones que esperan «Aprobar» (y cuántas ya pagadas): sin
     * aprobar, el jugador no recibe su bienvenida con acceso (2026-10-27). */
    pending_approval?: { total: number; paid: number };
  };
}

/** Cerrar torneo (Eduardo 2026-09-27). */
export interface TournamentFinalResultRow {
  participant_id: number;
  division_id: number | null;
  division_name: string;
  name: string;
  position: number | null;
  label: string;
  /** Campeón/subcampeón/tercero por SIGNIFICADO (final o grupo único terminado); «1º Grupo A» no es campeón. */
  podium?: "champion" | "runner_up" | "third" | null;
}

export interface TournamentLifecycleEvent {
  action: "closed" | "reopened" | "superadmin_edit" | "migrated_from_archived" | string;
  at: string | null;
  user_name: string | null;
  reason: string | null;
  notify_players: boolean | null;
  details: Record<string, unknown>;
}

export interface TournamentClosePreview {
  /** Total real: todo lo que queda «No jugado» al cerrar. */
  pending_matches_total: number;
  pending_matches: Array<{ division_name: string; count: number }>;
  /** «Pendiente» (Eduardo 2026-10-05): jornadas hasta la actual, partidos jugables. */
  due_matches_total?: number;
  due_matches?: Array<{ division_id: number | null; division_name: string; count: number }>;
  later_matches_total?: number;
  later_rounds?: number;
  pending_reports: number;
  pending_orders: number;
  finals_without_winner: string[];
  due_finals_without_winner?: string[];
  champions: TournamentFinalResultRow[];
}

export interface TournamentLifecycle {
  status: string;
  closed: boolean;
  closed_at: string | null;
  closed_by_name: string | null;
  reopened_at: string | null;
  reopened_by_name: string | null;
  reopen_reason: string | null;
  reopen_notify_players: boolean;
  end_date: string | null;
  suggest_close: boolean;
  preview: TournamentClosePreview | null;
  final_results: TournamentFinalResultRow[];
  events: TournamentLifecycleEvent[];
}

export interface TournamentLifecycleResponse {
  ok: boolean;
  data: { lifecycle: TournamentLifecycle };
}

export interface TournamentDeleteResponse {
  ok: boolean;
  data: { deleted: boolean; tournament_id: number };
}

/** Miembro del EQUIPO del torneo (staff con perfil, no confundir con las
 * parejas de dobles de TournamentTeamMember). */
export interface TournamentStaffMember {
  user_id: number;
  full_name: string;
  email: string;
  status: string;
  profile: "owner" | "coadmin" | "capturista" | "finanzas" | "arbitro" | "full" | "soporte";
  profile_label: string;
  managed_by_user_id: number | null;
  /** Viene del Equipo del organizador (nivel admin). */
  from_admin_team?: boolean;
  /** Soporte Torneos123: entra por su rol a todos los torneos, sin dinero. */
  is_platform_support?: boolean;
}

/** Un cambio de Soporte Torneos123 en el torneo (bitácora del Equipo). */
export interface SupportActivityEntry {
  at: string | null;
  user_id: number | null;
  user_name: string | null;
  method: string | null;
  path: string | null;
}

/** Equipo del organizador a nivel admin (/admin/team): aplica a todos sus torneos. */
export interface AdminTeamMember {
  user_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  status: string;
  profile: "coadmin" | "capturista" | "finanzas" | "arbitro";
  profile_label: string;
  must_change_password: boolean;
  tournaments_count: number;
  /** "all" = todos mis torneos (y los nuevos); "selected" = solo tournament_ids. */
  scope: AdminTeamScope;
  tournament_ids: number[];
}

export interface AdminTeamTournament {
  id: number;
  name: string;
  status: string;
  /** Fechas del torneo (YYYY-MM-DD) para distinguirlos en la lista. */
  start_date?: string | null;
  end_date?: string | null;
}

/** Sub-usuario creado torneo por torneo que aún no está en el Equipo:
 * trae en qué torneos del organizador está hoy (para precargarlos). */
export type AdminTeamLegacySubUser = MySubUser & { tournament_ids?: number[] };

/** Alcance de un miembro del Equipo: todos mis torneos (y los nuevos) o
 * solo los elegidos. */
export type AdminTeamScope = "all" | "selected";

/** Alta en el Equipo del organizador con su alcance desde el inicio. */
export type AdminTeamAddInput = TournamentStaffAddInput & {
  scope?: AdminTeamScope;
  tournament_ids?: number[];
};

export interface AdminTeamResponse {
  ok: boolean;
  data: {
    members: AdminTeamMember[];
    sub_users_without_team: AdminTeamLegacySubUser[];
    used: number;
    max_sub_users: number;
    tournaments_count: number;
    tournaments: AdminTeamTournament[];
    /** Invitaciones a cuentas existentes (pendientes y aceptadas). No cuentan en el tope. */
    invitations?: AdminTeamInvitation[];
  };
}

/** Invitación al Equipo para alguien que ya usa Torneos123 (Eduardo 2026-10-20).
 * Pendiente: solo el correo que escribió el dueño. Aceptada: «Cuenta propia». */
export interface AdminTeamInvitation {
  id: string;
  email: string;
  profile: "coadmin" | "capturista" | "finanzas" | "arbitro";
  profile_label: string;
  scope: AdminTeamScope;
  status: "pending" | "accepted";
  expired: boolean;
  created_at: string | null;
  last_sent_at: string | null;
  expires_at: string | null;
  tournament_ids: number[];
  tournaments_count: number;
  user_id?: number;
  full_name?: string;
}

export interface AdminTeamInvitationResponse {
  ok: boolean;
  data: { invitation: AdminTeamInvitation; message?: string };
}

/** Invitación que me llegó (tarjeta de Inicio). */
export interface ReceivedTeamInvitation {
  id: string;
  organizer_name: string;
  profile: string;
  profile_label: string;
  scope: AdminTeamScope;
  tournaments_count: number;
  tournament_names: string[];
  expires_at: string;
}

export interface AdminTeamMemberResponse {
  ok: boolean;
  data: { member: AdminTeamMember & { welcome_email?: StaffWelcomeEmailStatus } };
}

/** Estatus del correo de bienvenida a un sub-usuario nuevo (Eduardo 2026-09-24). */
export type StaffWelcomeEmailStatus = "sent" | "failed" | "not_configured" | "no_email";

export function staffWelcomeMessage(fullName: string, email: string, status: string | null | undefined): string {
  if (status === "sent") return `Le enviamos a ${email} un correo con su usuario y contraseña temporal; al entrar creará la suya.`;
  return `No pudimos enviarle el correo de acceso a ${fullName}: compártele su correo y la contraseña inicial; al entrar creará la suya.`;
}

export interface TournamentStaffListResponse {
  ok: boolean;
  data: { team: TournamentStaffMember[]; my_role: string | null; support_activity?: SupportActivityEntry[] };
}

export interface TournamentStaffMemberResponse {
  ok: boolean;
  data: { member: TournamentStaffMember & { welcome_email?: StaffWelcomeEmailStatus } };
}

export interface TournamentStaffRemoveResponse {
  ok: boolean;
  data: { removed: boolean; user_id: number };
}

export interface MySubUser {
  user_id: number;
  full_name: string;
  email: string;
  status: string;
}

export interface TiebreakPreset {
  tournament_id: number;
  tournament_name: string;
  tiebreakers: StandingsTiebreaker[];
}

export interface TiebreakPresetsResponse {
  ok: boolean;
  data: { presets: TiebreakPreset[] };
}

export interface MySubUsersResponse {
  ok: boolean;
  data: { sub_users: MySubUser[]; used: number; max_sub_users: number };
}

export interface TournamentStaffAddInput {
  profile: string;
  new_user?: {
    full_name: string;
    email: string;
    password: string;
    phone?: string | null;
  };
  user_id?: number;
}

export interface TournamentSettingsResponse {
  ok: boolean;
  data: { settings: TournamentSettings };
}

export interface AssignmentResponse {
  ok: boolean;
  data: { assignment: TournamentAdminAssignment };
}

export interface TournamentCreateInput {
  name: string;
  tournament_type: TournamentType;
  play_mode?: TournamentPlayMode;
  sport?: SportKey;
  venue_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  registration_deadline?: string | null;
  registration_cost?: string | null;
  official_ball?: string | null;
  google_maps_link?: string | null;
  whatsapp_group_link?: string | null;
  is_public?: boolean;
  is_federation_official?: boolean;
  description?: string | null;
  logo_url?: string | null;
}

export type TournamentUpdateInput = Partial<
  TournamentCreateInput & { status: string; organizer_name: string | null }
>;

export type TournamentSettingsUpdateInput = Partial<Omit<TournamentSettings, "tournament_id">>;

export interface PlayerCategory {
  id: number;
  code: string;
  name: string;
  gender: string | null;
  level_order: number | null;
  active: boolean;
}

export interface PlayerProfile {
  id: number;
  user_id: number | null;
  username: string | null;
  /** ¿Este admin puede tocar la CUENTA del jugador (PIN, «Ver como jugador»,
   * «Reactivar cuenta», «Acceso propio», fusionar, eliminar)? Solo quien lo
   * dio de alta o el superadministrador. `false` apaga esos botones; `null` o
   * ausente = la respuesta no sabe quién mira (ver playerAccountOwnership.ts). */
  can_manage_account?: boolean | null;
  /** ¿El jugador es de ALGUNA organización? Con `can_manage_account` en false:
   * true = lo registró otra organización; false = no es de ninguna y su cuenta
   * solo la atiende soporte. Al superadministrador le marca a quién asignar. */
  account_has_owner?: boolean | null;
  /** Con `can_manage_account` en false: ¿ya tiene con qué entrar (cuenta ligada
   * o solo «Acceso propio»)? `null` o ausente en los demás casos. */
  has_account?: boolean | null;
  first_name: string;
  last_name: string | null;
  maternal_last_name: string | null;
  display_name: string;
  /** Foto de perfil subida por el jugador; null = avatar con iniciales. */
  avatar_url?: string | null;
  /** Sin foto de perfil, Ranking/Jugadores muestran la de la credencial si esto está encendido. */
  show_registration_photo?: boolean;
  unique_player_id: string | null;
  phone: string | null;
  email: string | null;
  gender: string | null;
  birthdate: string | null;
  nationality_code: string | null;
  national_federation_id: string | null;
  wtn_tennis_id: string | null;
  wtn_rating: number | null;
  wtn_rating_doubles: number | null;
  dominant_hand: string | null;
  backhand_type: string | null;
  club_name: string | null;
  state_province: string | null;
  city: string | null;
  guardian_name: string | null;
  guardian_phone: string | null;
  guardian_relationship: string | null;
  guardian_email: string | null;
  coach_name: string | null;
  coach_phone: string | null;
  coach_email: string | null;
  data_consent: boolean;
  data_consent_at: string | null;
  notes: string | null;
  active_status: string;
  primary_category: PlayerCategory | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ParticipantStatusHistoryEntry {
  id: number;
  old_status: string | null;
  new_status: string;
  reason: string | null;
  changed_by: number | null;
  changed_at: string | null;
}

export interface ParticipantCategoryAssignment {
  id: number;
  player_category_id: number;
  is_current: boolean;
  assigned_at: string | null;
  assigned_by: number | null;
  category: PlayerCategory | null;
}

export type RankMovement = "up" | "down" | "same" | "new";

export interface TournamentTeamMember {
  id: number;
  team_id: number;
  player_profile_id: number;
  position: number;
  created_at: string | null;
  player: PlayerProfile | null;
}

export interface TournamentTeam {
  id: number;
  tournament_id: number;
  name: string;
  seed_rank: number | null;
  notes: string | null;
  members: TournamentTeamMember[];
  created_at: string | null;
  updated_at: string | null;
}

export interface TournamentParticipant {
  id: number;
  tournament_id: number;
  player_profile_id: number | null;
  team_id: number | null;
  division_id: number | null;
  division: TournamentDivision | null;
  participant_kind: "player" | "team";
  participant_label: string;
  /** Nombre corto legible (dobles: "Alonso / Bruno"). */
  participant_short_label?: string | null;
  current_status: string;
  registration_source: string | null;
  /** Ligas firmadas (1 h) de foto, CURP y comprobante: vacías para quien
   * no los abre (perfil Capturista, «Ver como jugador» en torneo ajeno);
   * `has_*` dice siempre si llegaron. */
  registration_documents?: {
    photo_url?: string | null;
    curp_pdf_url?: string | null;
    payment_receipt_url?: string | null;
    has_photo?: boolean;
    has_curp_pdf?: boolean;
    has_payment_receipt?: boolean;
    has_credential?: boolean;
    credential_url?: string | null;
  } | null;
  registration_custom_answers?: Record<string, unknown> | null;
  tournament_rank: number | null;
  initial_rank: number | null;
  current_rank: number | null;
  /** Escalera: lugar con el que entró al grupo y lugar actual en el grupo. */
  initial_group_rank?: number | null;
  group_position?: number | null;
  ranking_points: number | null;
  rank_delta: number | null;
  rank_movement: RankMovement | null;
  joined_at: string | null;
  dropped_at: string | null;
  notes: string | null;
  player: PlayerProfile | null;
  team: TournamentTeam | null;
  current_category: PlayerCategory | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TournamentParticipantDetail extends TournamentParticipant {
  status_history: ParticipantStatusHistoryEntry[];
  category_history: ParticipantCategoryAssignment[];
  /** Solo de un dado de baja: partidos que le quedan sin jugar (Eduardo
   * 2026-10-05: «Cancelar sus partidos pendientes (N)»). */
  pending_matches_count?: number;
  /** De esos, los de fuera de grupo (eliminatoria, playoffs): se cierran por
   * W.O. a su rival para que avance (revisión 2026-10-05). */
  pending_matches_walkover_count?: number;
}

export function participantDisplayName(
  participant: TournamentParticipant | null | undefined,
): string {
  if (!participant) return "Por definir";
  return (
    participant.participant_label ||
    participant.team?.name ||
    participant.player?.display_name ||
    `Participante #${participant.id}`
  );
}

/** Cuadro de playoffs «por definir»: texto de la referencia del lado
 * («1.º Grupo A», «Ganador SF1») guardado en notes como [pN_ref=…]. */
export function playoffSlotRef(notes: string | null | undefined, side: 1 | 2): string | null {
  const found = (notes ?? "").match(new RegExp(`\\[p${side}_ref=([^\\]]+)\\]`));
  return found ? found[1].trim() : null;
}

/** Seed de PLAYOFF del lado ([pN_seed=3]), guardado al generar el cuadro. */
export function playoffSeedMarker(notes: string | null | undefined, side: 1 | 2): number | null {
  const found = (notes ?? "").match(new RegExp(`\\[p${side}_seed=(\\d+)\\]`));
  return found ? Number(found[1]) : null;
}

/** Lugar con el que clasificó el lado ([pN_q=1.º Grupo A]). */
export function playoffQualMarker(notes: string | null | undefined, side: 1 | 2): string | null {
  const found = (notes ?? "").match(new RegExp(`\\[p${side}_q=([^\\]]+)\\]`));
  return found ? found[1].trim() : (playoffSlotRef(notes, side)?.match(/^\d+\.º /) ? playoffSlotRef(notes, side) : null);
}

export interface PlayoffSeedInfo {
  seed: number | null;
  qual: string | null;
}

/** Seed de playoff y lugar de clasificación por participante, leídos de los
 * partidos de playoffs (primer partido de cada quien). Vacío = cuadro viejo
 * sin marcadores: el que llama usa el comportamiento anterior. */
export function playoffSeedInfoByParticipant(
  matches: ReadonlyArray<{
    notes?: string | null;
    player1_participant_id: number | null;
    player2_participant_id: number | null;
  }>,
): Map<number, PlayoffSeedInfo> {
  const info = new Map<number, PlayoffSeedInfo>();
  for (const match of matches) {
    for (const side of [1, 2] as const) {
      const participantId = side === 1 ? match.player1_participant_id : match.player2_participant_id;
      if (participantId == null || info.has(participantId)) continue;
      const seed = playoffSeedMarker(match.notes, side);
      const qual = playoffQualMarker(match.notes, side);
      if (seed == null && qual == null) continue;
      info.set(participantId, { seed, qual });
    }
  }
  return info;
}

/** «Por definir · 1.º Grupo A» si el lado vacío tiene referencia. */
export function pendingSideLabel(notes: string | null | undefined, side: 1 | 2): string {
  const ref = playoffSlotRef(notes, side);
  return ref ? `Por definir · ${ref}` : "Por definir";
}

/** El partido es del cuadro por definir y le falta algún clasificado. */
export function isUnfilledPlayoffPlaceholder(match: {
  notes?: string | null;
  player1_participant_id: number | null;
  player2_participant_id: number | null;
}): boolean {
  const notes = match.notes ?? "";
  return (
    (/\[p1_key=/.test(notes) && match.player1_participant_id == null)
    || (/\[p2_key=/.test(notes) && match.player2_participant_id == null)
  );
}

export function matchParticipantDisplayName(
  participant: MatchParticipantBrief | null | undefined,
): string {
  if (!participant) return "Por definir";
  return participant.display_name || `Participante #${participant.id}`;
}

export interface PlayerCategoriesResponse {
  ok: boolean;
  data: { categories: PlayerCategory[] };
}

export interface PlayersListResponse {
  ok: boolean;
  // `total` = jugadores que cumplen la búsqueda (para paginar); limit/offset eco.
  data: { players: PlayerProfile[]; total?: number; limit?: number | null; offset?: number };
}

export interface PlayerResponse {
  ok: boolean;
  data: { player: PlayerProfile };
}

export interface PlayerPinResponse {
  ok: boolean;
  data: {
    player: PlayerProfile;
    email: string;
    username: string | null;
    pin: string;
    // "account": la cuenta de siempre del jugador; "own_access": el usuario
    // propio del hijo de una cuenta familiar (el tutor no se toca);
    // "family_account": la cuenta familiar (el admin lo confirmó).
    target?: "account" | "own_access" | "family_account";
  };
}

/** Respuesta de «Reactivar cuenta» (la cuenta familiar que desactivó el
 * error viejo de archivar a un hermano). */
export interface PlayerAccountReactivateResponse {
  ok: boolean;
  data: {
    player: PlayerProfile;
    reactivated: boolean;
    account_status: string;
    // Solo los jugadores de la cuenta que este admin administra.
    family_profiles: string[];
    family_profiles_hidden: number;
  };
}

/** Respuesta de archivar un jugador (Eliminar del catálogo). */
export interface PlayerArchiveResponse {
  ok: boolean;
  data: {
    player: PlayerProfile;
    // ¿Se desactivó la cuenta con la que entraba? No, si es la cuenta
    // familiar del tutor (sigue operando a otros jugadores) o la de un staff.
    account_disabled?: boolean;
    // family_account_disabled: la cuenta familiar YA estaba desactivada (el
    // error viejo); hay que reactivarla desde la ficha de otro jugador.
    account_kept_reason?: "family_account" | "family_account_disabled" | "staff_account" | null;
  };
}

export interface PlayerImpersonationResponse {
  ok: boolean;
  data: {
    access_token: string;
    token_type: string;
    user: AuthUser;
    player: PlayerProfile;
    impersonation: {
      actor_user_id: number;
      player_profile_id: number;
      expires_in_minutes: number;
    };
  };
}

export interface ParticipantsListResponse {
  ok: boolean;
  data: { participants: TournamentParticipant[] };
}

export interface ParticipantResponse {
  ok: boolean;
  /** `rr_rebuild`: solo el PATCH de un participante (cambio de categoría). */
  data: { participant: TournamentParticipant; rr_rebuild?: RrRebuildSummary };
}

export interface PaymentBankAccount {
  id: number;
  user_id: number;
  bank_name: string;
  beneficiary_name: string;
  account_number: string | null;
  clabe: string | null;
  payment_concept: string | null;
  is_active: boolean;
  /** Cuentas de cobro F1: nombre corto para reconocerla. */
  alias?: string | null;
  /** La predeterminada del dueño (a lo sumo una). */
  is_default?: boolean;
  clabe_last4?: string | null;
  account_last4?: string | null;
  /** «BBVA ****4821» (con alias delante si tiene). */
  label?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BankAccountInput {
  bank_name: string;
  beneficiary_name: string;
  account_number?: string | null;
  clabe?: string | null;
  payment_concept?: string | null;
  alias?: string | null;
}

export interface RegistrationFormField {
  key: string;
  kind: "core" | "document" | "custom" | string;
  type: string;
  label: string;
  required: boolean;
  enabled: boolean;
  locked?: boolean;
  show_in_players_table?: boolean;
  visible_in_players_table?: boolean;
  help_text?: string;
  options?: string[];
  conditional_required?: "minor" | string | null;
  order?: number;
  doubles_only?: boolean;
  /** Dónde lo ve la familia en el formulario público (Eduardo 2026-10-01).
   * Solo cuenta en los campos personalizados: los del sistema tienen sección
   * fija (ver `fieldSection` en registrationFields.ts). */
  section?: RegistrationFieldSection | string | null;
}

export type RegistrationFieldSection = "player" | "guardian" | "other";

/** «¿Quién va a jugar?»: el jugador mismo o un hijo (u otra persona). */
export type PublicWhoPlays = "self" | "child";

/** Lo calcula el servidor: preguntar solo con categorías infantiles o con
 * datos del tutor; las infantiles arrancan en «Mi hijo o hija». */
export interface PublicWhoPlaysConfig {
  ask: boolean;
  default: PublicWhoPlays | null;
}

export interface RegistrationFormSchema {
  version: number;
  fields: RegistrationFormField[];
}

export interface RegistrationSettings {
  id: number;
  tournament_id: number;
  currency: string;
  entry_fee_amount: string;
  // null = el torneo no ofrece ese precio; "0.00" = gratis para ese tipo.
  member_fee_amount?: string | null;
  additional_category_fee_amount?: string | null;
  coach_fee_amount?: string | null;
  /** Etiqueta del costo diferenciado para este torneo; null = "Socio" / "Profesor". */
  member_fee_label?: string | null;
  coach_fee_label?: string | null;
  platform_fee_amount: string;
  admin_fee_amount: string;
  admin_fee_status: string;
  admin_fee_notes: string | null;
  organizer_net_amount: string;
  /** Candado de comprobantes: solo el superadmin lo abre (por torneo). */
  manual_payments_allowed?: boolean;
  provider: string;
  payout_account_label: string | null;
  collection_bank_account_id: number | null;
  collection_bank_account: PaymentBankAccount | null;
  notes: string | null;
  /** «Este torneo usa credencial» (Eduardo 2026-10-05). Sin el dato = sí. */
  credential_enabled?: boolean;
  /** Usa credencial pero el formulario no pide la foto (aviso del paso 02, Eduardo 2026-10-30). */
  credential_photo_missing?: boolean;
  credential_template_url?: string | null;
  credential_title?: string | null;
  credential_subtitle?: string | null;
  credential_club?: string | null;
  credential_footer?: string | null;
  credential_birthdate_label?: string | null;
  credential_age_label?: string | null;
  credential_logo_url?: string | null;
  credential_company_name?: string | null;
  credential_fields_json?: string | null;
  registration_form_schema_json?: string | null;
  registration_form_schema?: RegistrationFormSchema;
  /** El sistema prendió los datos del papá, mamá o tutor por la primera
   * categoría de menores y el organizador aún no guarda los campos. */
  guardian_fields_auto_enabled?: boolean;
  credential_accent_color?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface RegistrationSummary {
  total_orders: number;
  paid_orders: number;
  pending_orders: number;
  gross_collected: string;
  platform_fee_accrued: string;
  organizer_net_ready: string;
}

export interface AdminFinanceTournamentOption {
  id: number;
  name: string;
  status: string;
}

export interface AdminFinanceClubOption {
  id: number;
  name: string;
  slug: string;
}

export interface AdminFinanceOrderRow {
  id: number;
  source: "tournament_registration" | "club_membership" | string;
  source_label: string;
  concept: string;
  tournament_id: number | null;
  tournament_name: string | null;
  club_id: number | null;
  club_name: string | null;
  participant_id: number | null;
  player_profile_id: number | null;
  player_name: string;
  status: string;
  raw_status?: string | null;
  currency: string;
  gross_amount: string;
  platform_fee_amount: string;
  organizer_net_amount: string;
  /** Comisión que descontó Mercado Pago (asiento provider_fee); null si el cobro fue por fuera. */
  provider_fee_amount?: string | null;
  /** Neto que Mercado Pago depositó al organizador; null si no aplica. */
  provider_net_amount?: string | null;
  /** Neto REAL: bruto − comisión Torneos123 − comisión Mercado Pago. */
  net_amount?: string;
  provider: string;
  provider_reference: string | null;
  /** Desglose por división de una orden multi-categoría (vacío en membresías). */
  items?: RegistrationOrderItem[];
  receipt_url?: string | null;
  paid_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  payment_link?: string | null;
  payment_reminder?: string | null;
  /** Cuenta a la que llega el pago: "mp", "bank:<id>", "none" o "club". */
  account_key?: string | null;
  /** Etiqueta enmascarada («BBVA ****4821»). */
  account_label?: string | null;
}

export interface AdminFinanceAccountOption {
  key: string;
  label: string;
}

export type AdminFinanceParams = {
  tournament_id?: number;
  source?: string;
  club_id?: number;
  player_profile_id?: number;
  period?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
  provider?: string;
  q?: string;
  /** Filtro «Cuenta»: "mp" o "bank:<id>". */
  account?: string;
  page?: number;
  page_size?: number;
};

function financeQuery(params: AdminFinanceParams): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") qs.set(key, String(value));
  });
  return qs.toString();
}

export interface AdminFinanceDashboardResponse {
  ok: boolean;
  data: {
    filters: {
      tournament_id: number | null;
      source?: string | null;
      club_id?: number | null;
      player_profile_id?: number | null;
      period: string;
      date_from: string | null;
      date_to: string | null;
      status: string | null;
      provider: string | null;
      account?: string | null;
      resolved_date_from: string | null;
      resolved_date_to: string | null;
    };
    summary: {
      total_orders: number;
      paid_orders: number;
      pending_orders: number;
      gross_received: string;
      pending_gross: string;
      platform_fee_received: string;
      /** Fee Torneos123 de lo cobrado con Mercado Pago (ya descontado en el cobro). */
      platform_fee_mp?: string;
      /** Fee Torneos123 de lo cobrado por fuera (lo debes a Torneos123). */
      platform_fee_outside?: string;
      organizer_net_received: string;
      provider_fee_received?: string;
      /** Suma del neto REAL de las órdenes pagadas. */
      net_received?: string;
      /** Devuelto en reembolsos parciales (ya descontado de net_received). */
      refunded_partial?: string;
      /** Bruto de todas las órdenes del filtro (todas las páginas). */
      filter_gross?: string;
      /** Pagos confirmados por cuenta de cobro (todas las páginas). */
      paid_by_provider?: Record<string, string>;
      status_breakdown: Record<string, number>;
    };
    tournaments: AdminFinanceTournamentOption[];
    clubs?: AdminFinanceClubOption[];
    /** Opciones del filtro «Cuenta». */
    accounts?: AdminFinanceAccountOption[];
    orders: AdminFinanceOrderRow[];
    pagination?: { page: number; page_size: number; total: number; total_pages: number };
  };
}

/** Renglón congelado de una orden multi-categoría: división cubierta,
 * participante cubierto (puede NO ser el ancla de la orden) y monto. */
export interface RegistrationOrderItem {
  id: number;
  division_id: number | null;
  division_name: string | null;
  participant_id: number;
  /** Estado del participante de esa categoría (pre_registered, active, dropped…). */
  participant_status?: string | null;
  amount: string;
  /** Monto calculado con el costo por categoría adicional. */
  is_additional_category?: boolean;
}

/** «Aprobar» de Inscripciones: categorías aprobadas y la orden actualizada
 * (el cobro no cambia). */
export interface RegistrationApproveResponse {
  ok: boolean;
  data: {
    participant: TournamentParticipant;
    approved: Array<{ participant_id: number; division_id: number | null; division_name: string | null }>;
    order: RegistrationOrder | null;
  };
}

export interface OrderPaymentInstructions {
  bank_name: string | null;
  beneficiary_name: string | null;
  account_number: string | null;
  clabe: string | null;
  payment_concept: string | null;
  /** true = orden vieja sin foto: datos ACTUALES del torneo. */
  fallback: boolean;
}

export interface RegistrationOrder {
  /** Solo para el jugador: datos bancarios congelados de SU orden. */
  payment_instructions?: OrderPaymentInstructions | null;
  /** Última Bienvenida enviada a esta inscripción; null = nunca. */
  welcome_sent_at?: string | null;
  welcome_count?: number;
  id: number;
  public_token?: string | null;
  tournament_id: number;
  participant_id: number;
  player_profile_id: number | null;
  participant: TournamentParticipant | null;
  /** Desglose por división (multi-categoría). Puede venir vacío en órdenes single. */
  items?: RegistrationOrderItem[];
  status: string;
  currency: string;
  // Lo que DECLARÓ el jugador (general/socio/profesor): el admin valida
  // por fuera si es verdad; el monto ya se cobró según esto.
  fee_category?: string;
  /** Toda la orden se cobró con el costo por categoría adicional. */
  is_additional_category?: boolean;
  /** Saldo por reclasificación: lo pagado se respeta y la diferencia vive aquí. */
  balance_due_amount?: string | null;
  balance_due_reason?: string | null;
  balance_due_status?: "pending" | "paid" | "waived" | null;
  balance_due_paid_at?: string | null;
  gross_amount: string;
  /** Devuelto en reembolsos parciales (la orden sigue pagada). */
  refunded_amount?: string;
  platform_fee_amount: string;
  organizer_net_amount: string;
  /** Comisión Mercado Pago del cobro; null si fue por fuera. */
  provider_fee_amount?: string | null;
  /** Neto REAL: bruto − Torneos123 − Mercado Pago. */
  net_amount?: string;
  provider: string;
  provider_reference: string | null;
  provider_checkout_session_id?: string | null;
  provider_payment_intent_id?: string | null;
  provider_charge_id?: string | null;
  checkout_url?: string | null;
  checkout_expires_at?: string | null;
  payment_confirmed_at?: string | null;
  payment_failure_reason?: string | null;
  paid_at: string | null;
  payout_status: string;
  notes: string | null;
  credential_template_url?: string | null;
  credential_title?: string | null;
  credential_subtitle?: string | null;
  credential_club?: string | null;
  credential_footer?: string | null;
  credential_accent_color?: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Revisión de la CURP contra el nombre registrado. Solo en el tablero del
   * admin; null en órdenes de pareja o sin CURP. */
  curp_check?: CurpCheck | null;
}

/** Paso 1: la cuenta de cobro que el torneo YA tiene (aunque sea del
 * organizador y no de quien abre la pantalla) y si este usuario puede
 * cambiarla por otra (solo el organizador del torneo o el superadmin). */
export interface RegistrationCollectionAccountAccess {
  account: PaymentBankAccount | null;
  can_change: boolean;
  /** Cuentas activas del DUEÑO del torneo (solo si puede cambiarla). */
  options?: PaymentBankAccount[];
  default_account_id?: number | null;
  /** Pagos que ya tiene el torneo (pagados y en proceso). */
  payments?: { paid: number; in_process: number };
}

/** «Usar el formulario de otro torneo»: torneo del admin con formulario propio. */
export interface RegistrationFormSource {
  tournament_id: number;
  name: string;
  field_count: number;
  enabled_field_count: number;
  updated_at: string | null;
}

export interface RegistrationDashboardResponse {
  ok: boolean;
  data: {
    settings: RegistrationSettings;
    summary: RegistrationSummary;
    orders: RegistrationOrder[];
    collection_account?: RegistrationCollectionAccountAccess;
    /** «Usar el formulario de otro torneo»: la foto quedó Activa y Obligatoria por la credencial. */
    photo_forced?: boolean;
  };
}

export interface RegistrationMarkPaidResponse {
  ok: boolean;
  data: { order: RegistrationOrder; summary: RegistrationSummary };
}

export type RegistrationPaymentStatus = "pending" | "paid" | "cancelled" | "refunded";

export interface RegistrationWelcomeResponse {
  ok: boolean;
  data: {
    order: RegistrationOrder;
    outbox_count: number;
    // Resultado del correo de Bienvenida (spec Cobros v1.1 Sección 4):
    // email_status es "outbox" (camino normal sin credenciales),
    // "direct_sent" / "direct_failed" (entrega directa con credenciales)
    // o "no_email" (perfil sin correo real).
    welcome?: {
      credentials_sent: boolean;
      email_status: string;
      payment_link_included: boolean;
      // Por qué no fueron credenciales: sent | already_has_access | no_email |
      // staff_account | family_account | email_in_other_account |
      // account_has_foreign_profiles | not_account_owner |
      // email_not_configured | direct_failed
      credentials_reason?: string;
      last_login_at?: string | null;
    };
  };
}

// División enriquecida SOLO en el payload público de inscripción:
// cupo aprobado y costo efectivo congelado por el backend.
export type PublicRegistrationDivision = TournamentDivision & {
  approved_count: number;
  is_full: boolean;
  /** Cierre efectivo: el propio o el del torneo (null = sin fecha). */
  effective_registration_deadline?: string | null;
  /** Cerrada por fecha, inicio, cupo, torneo terminado o por el organizador. */
  is_closed?: boolean;
  closed_reason?: "started" | "full" | "closed" | "deadline" | "organizer" | null;
  effective_cost: string;
  // true = la división trae costo propio y NO se descuenta por tipo de
  // inscripción (los costos socio/profesor solo sustituyen el base).
  has_own_cost?: boolean;
};

// Tipo de inscripción que el jugador puede declarar (solo los que el
// torneo configuró; general siempre está).
export interface PublicFeeOption {
  category: string;
  label: string;
  amount: string;
}

export interface PublicRegistrationPayload {
  tournament: Tournament;
  convocatoria: Record<string, string>;
  registration_settings: RegistrationSettings;
  registration_form?: RegistrationFormSchema;
  rule: PlayerRulePayload["rule"];
  registration_url: string;
  fee_options?: PublicFeeOption[];
  divisions: PublicRegistrationDivision[];
  // Cierre duro del link: abierto si al menos una categoría está abierta.
  // "deadline" = ya pasó la fecha de cierre (desde 2026-10-24 sí cierra).
  // "organizer" = el organizador usó «Cerrar inscripciones ahora».
  registration_open: boolean;
  closed_reason: "started" | "full" | "closed" | "deadline" | "organizer" | null;
  open_divisions_count?: number;
  extemporaneous: boolean;
  /** Sin la llave (servidor viejo) no se pregunta. */
  who_plays?: PublicWhoPlaysConfig;
}

export interface PublicRegistrationResponse {
  ok: boolean;
  data: PublicRegistrationPayload;
}

// Payload mínimo del hub /multisport/ (tournament_hub_public_dict en backend):
// datos de descubrimiento, no de inscripción. NO reutilizar `Tournament` aquí —
// el backend deliberadamente no envía whatsapp/maps/costos en el listado público.
export interface TournamentHubSummary {
  id: number;
  slug: string;
  name: string;
  sport: SportKey | string;
  tournament_type: string;
  play_mode: string;
  venue_name: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  logo_url: string | null;
}

export interface PublicTournamentsResponse {
  ok: boolean;
  data: { tournaments: TournamentHubSummary[] };
}

/** Directorio de torneos públicos (torneos123.com): solo slugs, sin ids. */
export interface PublicDirectoryCategory {
  name: string;
  gender: string | null;
  play_mode: string;
  level_label: string | null;
  birth_year_from: number | null;
  birth_year_to: number | null;
  is_full: boolean;
  /** Cerrada por fecha/inicio/cupo; registration_deadline = cierre efectivo. */
  is_closed?: boolean;
  registration_deadline?: string | null;
}

export interface PublicDirectoryTournament {
  slug: string;
  name: string;
  sport: SportKey | string;
  tournament_type: string;
  play_mode: string;
  venue_name: string | null;
  google_maps_link: string | null;
  start_date: string | null;
  end_date: string | null;
  registration_deadline: string | null;
  extemporaneous: boolean;
  poster_url: string | null;
  organizer_name: string | null;
  organizer_logo_url: string | null;
  registration_url: string;
  price_from: string | null;
  currency: string;
  categories: PublicDirectoryCategory[];
}

export interface PublicDirectoryResponse {
  ok: boolean;
  data: { tournaments: PublicDirectoryTournament[] };
}

export interface PlayerPublicTournamentCategory {
  id: number;
  name: string;
  status: "eligible" | "registered" | "full" | "age" | "gender" | string;
}

export interface PlayerPublicTournament extends PublicDirectoryTournament {
  eligible_categories: PlayerPublicTournamentCategory[];
  registered_categories: string[];
  already_registered: boolean;
}

export interface PlayerPublicTournamentsResponse {
  ok: boolean;
  data: {
    profile: { id: number; display_name: string; has_birthdate: boolean; has_gender: boolean } | null;
    tournaments: PlayerPublicTournament[];
    hidden_count: number;
  };
}

export interface PublicRegistrationPlayerProfile {
  id: number;
  first_name: string;
  last_name: string;
  maternal_last_name: string;
  display_name: string;
  phone: string;
  email: string;
  birthdate: string;
  gender: string;
  nationality_code: string;
  state_province: string;
  city: string;
  club_name: string;
  national_federation_id: string;
  wtn_tennis_id: string;
  dominant_hand: string;
  backhand_type: string;
  guardian_name: string;
  guardian_phone: string;
  guardian_relationship: string;
  guardian_email: string;
  coach_name: string;
  coach_phone: string;
  coach_email: string;
  is_registered?: boolean;
  participant_id?: number | null;
  registered_division_ids?: number[];
  // Categorías con inscripción vigente a precio completo (costo adicional).
  active_base_registrations?: Array<{ division_id: number | null; name: string }>;
  pending_registration?: {
    participant_id: number;
    participant_status: string;
    payment_receipt_url?: string | null;
    needs_payment_receipt: boolean;
    // orden Mercado Pago pendiente de pago: la UI reanuda en el paso 4 con el botón de pago
    needs_online_payment?: boolean;
    // cobro manual con comprobante ya subido y todavía sin validar por el organizador
    receipt_under_review?: boolean;
    order?: RegistrationOrder | null;
  } | null;
}

export interface PublicRegistrationMeResponse {
  ok: boolean;
  data: {
    player_profile: PublicRegistrationPlayerProfile;
    player_profiles: PublicRegistrationPlayerProfile[];
    /** Titular de la cuenta con sesión: llena «Tus datos» en modo hijo.
     * `email` es null si el correo de la cuenta es interno (sin correo real). */
    account?: { full_name: string | null; email: string | null; phone: string | null } | null;
  };
}

export interface PublicRegistrationSubmitInput {
  // Compat: clientes viejos mandan division_id single; el form nuevo manda
  // division_ids (campo repetido en el multipart).
  division_id?: number | null;
  division_ids?: number[] | null;
  fee_category?: string | null;
  /** «child»: la cuenta es del papá/mamá (correo = guardian_email). Sin
   * mandar = «self», igual que siempre. */
  who_plays?: PublicWhoPlays | null;
  player_profile_id?: number | null;
  first_name: string;
  last_name: string;
  maternal_last_name?: string | null;
  phone: string;
  email: string;
  birthdate: string;
  gender: string;
  nationality_code: string;
  state_province: string;
  city: string;
  club_name: string;
  national_federation_id?: string | null;
  wtn_tennis_id?: string | null;
  dominant_hand: string;
  backhand_type: string;
  guardian_name?: string | null;
  guardian_phone?: string | null;
  guardian_relationship?: string | null;
  guardian_email?: string | null;
  coach_name?: string | null;
  coach_phone?: string | null;
  coach_email?: string | null;
  data_consent: boolean;
  photo_file?: File | null;
  curp_pdf_file?: File | null;
  payment_receipt_file?: File | null;
  custom_answers_json?: string | null;
}

export interface PublicPaymentReceiptUploadResponse {
  ok: boolean;
  data: { order: RegistrationOrder };
}

export interface PublicPaymentStatusResponse {
  ok: boolean;
  data: {
    order_id: number;
    status: string;
    provider: string | null;
    provider_reference: string | null;
    paid_at: string | null;
  };
}

export interface PublicCheckoutStartResponse {
  ok: boolean;
  data: {
    checkout_url: string | null;
    session_id?: string;
    order_status: string;
    message?: string;
  };
}

export interface PublicRegistrationSubmitResponse {
  ok: boolean;
  data: {
    /** Solo si la inscripción quedó pagada o sin costo. */
    whatsapp_group_link?: string | null;
    /** Todos los grupos con su nombre, misma regla (Eduardo 2026-10-01). */
    whatsapp_groups?: TournamentWhatsappGroup[];
    player_profile_id: number;
    participant_id: number;
    division_id?: number | null;
    division?: TournamentDivision | null;
    participant_ids?: number[];
    division_ids?: number[];
    divisions?: TournamentDivision[];
    display_name: string;
    /** Modo con el que quedó la inscripción (servidor nuevo). */
    who_plays?: PublicWhoPlays;
    /** Correo de la cuenta (el que escribió quien inscribe, ya normalizado). */
    account_email?: string | null;
    order: RegistrationOrder;
    payment: {
      online_checkout_available: boolean;
      checkout_provider: string;
      checkout_message: string;
    };
    registration_documents?: {
      photo_url?: string | null;
      curp_pdf_url?: string | null;
      payment_receipt_url?: string | null;
      has_photo?: boolean;
      has_curp_pdf?: boolean;
      has_payment_receipt?: boolean;
      has_credential?: boolean;
      credential_url?: string | null;
    };
  };
}

export interface PublicCurpParseResponse {
  ok: boolean;
  data: {
    parsed: {
      curp?: string | null;
      full_name?: string | null;
      first_name?: string | null;
      last_name?: string | null;
      maternal_last_name?: string | null;
      birthdate?: string | null;
      gender?: string | null;
      nationality_code?: string | null;
      state_province?: string | null;
      /** Copia de los campos de abajo (el servidor los manda en ambos niveles). */
      raw_full_name?: string | null;
      name_split?: PublicCurpNameSplit | null;
    };
    name_matches_curp?: boolean | null;
    /** Renglón del nombre tal como viene en la constancia (sin partir). */
    raw_full_name?: string | null;
    /** El nombre partido con la clave CURP como autoridad. null si ningún
     * corte del renglón reproduce las letras de la clave. */
    name_split?: PublicCurpNameSplit | null;
    /** Varias claves CURP en un PDF (probablemente hermanos). */
    multiple_curps?: boolean;
    confidence: "high" | "partial";
    message: string;
  };
}

/** Nombre(s), apellido paterno y materno según la CURP. */
export interface PublicCurpNameSplit {
  first_name: string | null;
  last_name: string | null;
  maternal_last_name: string | null;
}

function curpParseFormData(file: File): FormData {
  const formData = new FormData();
  formData.append("curp_pdf_file", file);
  return formData;
}

function publicRegistrationFormData(payload: PublicRegistrationSubmitInput): FormData {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      // division_ids: campo repetido en el multipart (así lo lee el backend).
      value.forEach((item) => formData.append(key, String(item)));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
}

export interface RegistrationCredentialTemplateInput {
  template_file?: File | null;
  logo_file?: File | null;
  credential_title?: string | null;
  credential_subtitle?: string | null;
  credential_club?: string | null;
  credential_footer?: string | null;
  credential_birthdate_label?: string | null;
  credential_age_label?: string | null;
  credential_company_name?: string | null;
  credential_fields_json?: string | null;
  credential_accent_color?: string | null;
}

export interface RegistrationCredentialTemplateResponse {
  ok: boolean;
  data: { settings: RegistrationSettings };
}

export interface RegistrationGenerateCredentialResponse {
  ok: boolean;
  data: { order: RegistrationOrder };
}

export type RegistrationDocumentType = "photo" | "curp" | "payment_receipt";

export interface RegistrationDocumentUploadResponse {
  ok: boolean;
  data: { order: RegistrationOrder };
}

// --- Revisión de CURP vs nombre del jugador (avisa, nunca bloquea) ---------

/** Resultado de comparar la CURP con el registro. `unchecked` = hay archivo
 * pero aún no se lee; `requested` = el organizador pidió otra CURP. */
export type CurpCheckState = "unchecked" | "matches" | "similar" | "other_person" | "unreadable" | "requested";

/** Lo mismo, sin los estados del flujo: lo que se le dice al papá al subir. */
export type CurpCheckClassification = "matches" | "similar" | "other_person" | "unreadable";

/** Por qué no se pudo leer el PDF. */
export type CurpUnreadableReason = "pdf_unreadable" | "no_text" | "no_curp" | "not_constancia" | "file_missing";

/** Lo que dice la CURP (sin la clave completa: privacidad). */
export interface CurpCheckCurpData {
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  maternal_last_name: string | null;
  birthdate: string | null;
  sex: "male" | "female" | null;
}

/** Categoría donde el jugador ya no cabría con la fecha de la CURP. */
export interface CurpCheckCategoryConflict {
  division_id: number;
  name: string;
  /** "age" (edad) o "gender" (rama), como en el directorio de torneos. */
  reason: string;
}

export interface CurpCheck {
  state: CurpCheckState;
  /** Parecida, otra persona o sin leer, y nadie la ha marcado revisada. */
  needs_review: boolean;
  reasons: string[];
  notes: string[];
  unreadable_reason: CurpUnreadableReason | null;
  /** El PDF trae varias CURP; se comparó con la que más se parece. */
  multiple: boolean;
  curp: CurpCheckCurpData | null;
  /** Archivo revisado: se manda de vuelta en las acciones (409 si cambió). */
  checked_url: string | null;
  read_at: string | null;
  reviewed: { action: "ok" | "used_curp_data"; at: string | null; by_name: string | null } | null;
  requested: { at: string | null; by_name: string | null } | null;
  category_conflicts_if_applied: CurpCheckCategoryConflict[];
  /** El torneo pide la CURP como obligatoria: «Pedir otra CURP» procede. */
  can_request_again: boolean;
  /** ¿Alguien recibiría «Pedir otra CURP»? (cuenta en la app o correo). Solo
   * viene donde ese botón aparece (por revisar); null en los demás estados.
   * false = el botón se desactiva: el organizador la sube con «Cambiar». */
  can_notify?: boolean | null;
}

/** «Revisar CURPs»: lee por tandas (presupuesto de tiempo); si quedan
 * pendientes, `remaining` > 0 y se vuelve a presionar. */
export interface RegistrationCurpReviewRunResponse {
  ok: boolean;
  data: {
    checked: number;
    needs_review: number;
    unreadable: number;
    already_checked: number;
    missing_files: number;
    remaining: number;
    /** El lector de PDF no respondió (503): no se guardó nada; otro clic. */
    parser_unavailable?: boolean;
  };
}

export interface RegistrationCurpApplyResponse {
  ok: boolean;
  data: {
    order: RegistrationOrder;
    category_warnings: CurpCheckCategoryConflict[];
    /** Lo que sí cambió: "name" y/o "birthdate". */
    applied?: Array<"name" | "birthdate">;
  };
}

export interface RegistrationCurpRequestResponse {
  ok: boolean;
  data: {
    order: RegistrationOrder;
    /** in_app_recipients: cuántas cuentas recibieron el aviso en la app
     * (0 = el jugador no tiene cuenta). */
    message: { email_status: string; whatsapp?: unknown; title: string; in_app_recipients?: number | null };
  };
}

export interface RegistrationCurpOkResponse {
  ok: boolean;
  data: { order: RegistrationOrder };
}

function registrationDocumentFormData(documentType: RegistrationDocumentType, file: File): FormData {
  const formData = new FormData();
  formData.append("document_type", documentType);
  formData.append("file", file);
  return formData;
}

function singleFileFormData(file: File): FormData {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}

function registrationCredentialTemplateFormData(payload: RegistrationCredentialTemplateInput): FormData {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (value instanceof File) {
      formData.append(key, value);
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
}

// Inscripción rápida de mostrador: perfil nuevo O existente, 1..N
// categorías, tipo de costo y pago opcional — un solo paso.
export interface RegistrationQuickEnrollInput {
  division_ids: number[];
  player_profile_id?: number | null;
  new_player?: {
    first_name: string;
    last_name?: string | null;
    phone?: string | null;
    email?: string | null;
    gender?: string | null;
    birthdate?: string | null;
  } | null;
  fee_category?: string | null;
  mark_paid?: boolean;
}

export interface RegistrationSettingsInput {
  currency: string;
  entry_fee_amount: string;
  // PUT semántico: null (u omitido) LIMPIA el costo diferenciado. Todo
  // caller debe re-mandar el valor vigente si no lo está editando.
  member_fee_amount?: string | null;
  additional_category_fee_amount?: string | null;
  coach_fee_amount?: string | null;
  /** Omitido = se conserva; "" = volver a la etiqueta de siempre. */
  member_fee_label?: string | null;
  coach_fee_label?: string | null;
  platform_fee_amount: string;
  admin_fee_amount?: string;
  admin_fee_status?: string;
  admin_fee_notes?: string | null;
  provider: string;
  payout_account_label?: string | null;
  collection_bank_account_id?: number | null;
  notes?: string | null;
}

export interface PaymentConnectedAccount {
  id: number;
  user_id: number;
  provider: string;
  mode: string;
  provider_account_id: string | null;
  account_status: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  country: string | null;
  default_currency: string | null;
  business_name: string | null;
  dashboard_url: string | null;
  last_onboarding_url_created_at: string | null;
  last_synced_at: string | null;
}

export interface AdminPaymentAccountResponse {
  ok: boolean;
  data: {
    provider: string;
    mode: string;
    stripe_configured: boolean;
    account: PaymentConnectedAccount;
    bank_accounts: PaymentBankAccount[];
    mercadopago?: {
      configured: boolean;
      account: PaymentConnectedAccount;
    };
    /** Comisión de Torneos123 en solo lectura (la edita solo el superadmin). */
    platform_fee?: PlatformFeeReadonly;
  };
}

export interface PlatformFeeReadonly {
  mode: string;
  currency_default: string;
  platform_fee_type: string;
  platform_fee_fixed_amount: string;
  platform_fee_percent_bps: number;
  minimum_platform_fee_amount: string;
  is_active: boolean;
}

export interface AdminPaymentConnectResponse {
  ok: boolean;
  data: {
    account: PaymentConnectedAccount;
    onboarding_url: string;
  };
}

export interface PlatformPaymentSettings {
  id: number;
  provider: string;
  mode: string;
  currency_default: string;
  platform_fee_type: string;
  platform_fee_fixed_amount: string;
  platform_fee_percent_bps: number;
  minimum_platform_fee_amount: string;
  is_active: boolean;
}

export interface PlatformPaymentSettingsInput {
  currency_default: string;
  platform_fee_type: string;
  platform_fee_fixed_amount: string;
  platform_fee_percent_bps: number;
  minimum_platform_fee_amount: string;
  is_active: boolean;
}

// Fila del panel central de fees (superadmin): config de cobro + acumulado
// HISTÓRICO (suma de fees de órdenes pagadas, congelado por orden).
export interface TournamentFeeRow {
  tournament_id: number;
  name: string;
  slug: string;
  status: string;
  start_date: string | null;
  provider: string;
  currency: string;
  entry_fee_amount: string;
  platform_fee_amount: string;
  admin_fee_amount: string;
  admin_fee_status: string;
  admin_fee_notes: string | null;
  manual_payments_allowed: boolean;
  paid_orders: number;
  pending_orders: number;
  accrued_platform_fee: string;
  /** Copia cuyo fee default no cabía en sus costos: se quedó el del original. */
  fee_review_needed?: boolean;
}

export interface TournamentFeesResponse {
  ok: boolean;
  data: { settings: PlatformPaymentSettings; tournaments: TournamentFeeRow[] };
}

export interface TournamentFeeUpdateInput {
  platform_fee_amount: string;
  admin_fee_amount: string;
  admin_fee_status: string;
  admin_fee_notes?: string | null;
  /** undefined = no tocar el candado de comprobantes. */
  manual_payments_allowed?: boolean;
}

export interface PlatformPaymentSettingsResponse {
  ok: boolean;
  data: { settings: PlatformPaymentSettings };
}

export interface ParticipantDetailResponse {
  ok: boolean;
  data: { participant: TournamentParticipantDetail };
}

export interface ParticipantHistoryResponse {
  ok: boolean;
  data: { history: ParticipantStatusHistoryEntry[] };
}

export interface PlayerCreateInput {
  first_name: string;
  last_name?: string | null;
  maternal_last_name?: string | null;
  display_name?: string | null;
  unique_player_id?: string | null;
  phone?: string | null;
  email?: string | null;
  gender?: string | null;
  birthdate?: string | null;
  nationality_code?: string | null;
  national_federation_id?: string | null;
  wtn_tennis_id?: string | null;
  dominant_hand?: string | null;
  backhand_type?: string | null;
  club_name?: string | null;
  state_province?: string | null;
  city?: string | null;
  guardian_name?: string | null;
  guardian_phone?: string | null;
  guardian_relationship?: string | null;
  guardian_email?: string | null;
  coach_name?: string | null;
  coach_phone?: string | null;
  coach_email?: string | null;
  data_consent?: boolean;
  notes?: string | null;
  division_id?: number | null;
  category_id?: number | null;
}

export type PlayerUpdateInput = Partial<PlayerCreateInput>;

export interface NewPlayerPayload {
  first_name: string;
  last_name?: string | null;
  maternal_last_name?: string | null;
  display_name?: string | null;
  phone?: string | null;
  email?: string | null;
  gender?: string | null;
  birthdate?: string | null;
  nationality_code?: string | null;
  national_federation_id?: string | null;
  wtn_tennis_id?: string | null;
  dominant_hand?: string | null;
  backhand_type?: string | null;
  club_name?: string | null;
  state_province?: string | null;
  city?: string | null;
}

export interface ParticipantEnrollInput {
  player_profile_id?: number | null;
  new_player?: NewPlayerPayload | null;
  division_id?: number | null;
  category_id?: number | null;
  status?: string | null;
  tournament_rank?: number | null;
  initial_rank?: number | null;
  current_rank?: number | null;
  ranking_points?: number | null;
  notes?: string | null;
  registration_source?: string | null;
}

export interface PairSuggestionBrief {
  participant_id: number;
  player_profile_id: number;
  name: string;
  partner_hint: string | null;
  division_id: number | null;
  division_name: string | null;
}

export interface PairSuggestion {
  participant_a: PairSuggestionBrief;
  participant_b: PairSuggestionBrief;
  reasons: string[];
  mutual: boolean;
}

export interface DoublesTeamInput {
  player_profile_ids: number[];
  division_id?: number | null;
  name?: string | null;
  seed_rank?: number | null;
  status?: string | null;
  notes?: string | null;
}

export interface ParticipantsBulkEnrollInput {
  player_profile_ids: number[];
  division_id?: number | null;
  category_id?: number | null;
  status?: string | null;
  notes?: string | null;
  registration_source?: string | null;
}

export interface ParticipantsBulkEnrollResponse {
  ok: boolean;
  data: {
    added: TournamentParticipant[];
    added_count: number;
    skipped_existing_ids: number[];
    skipped_existing_count: number;
    skipped_missing_ids: number[];
    skipped_missing_count: number;
  };
}

export interface ParticipantIdentifierLookupInput {
  national_federation_ids: string[];
  wtn_tennis_ids: string[];
  player_lookup_rows?: Array<{
    first_name?: string;
    last_name?: string;
    email?: string;
  }>;
  // Multicategoría: con división, already_enrolled es en ESA categoría.
  division_id?: number | null;
}

export interface ParticipantIdentifierLookupResponse {
  ok: boolean;
  data: {
    players: PlayerProfile[];
    matched_player_ids: number[];
    missing_national_federation_ids: string[];
    missing_wtn_tennis_ids: string[];
    already_enrolled_player_ids: number[];
  };
}

export interface ParticipantUpdateInput {
  status?: string | null;
  // Solo aplica cuando el PATCH transiciona status a "dropped".
  pending_matches_action?: PendingMatchesAction | null;
  notes?: string | null;
  division_id?: number | null;
  category_id?: number | null;
  tournament_rank?: number | null;
  initial_rank?: number | null;
  current_rank?: number | null;
  ranking_points?: number | null;
  // "Editar registro": respuestas del formulario público; null/"" borra la clave
  registration_custom_answers?: Record<string, unknown> | null;
}

/** Renglón del guardado en bloque de seeds del paso 6 (Ranking).
 * `initial_rank: null` explícito limpia el seed (S/R); omitir la clave deja
 * el seed intacto (el backend distingue vía model_fields_set). */
export interface ParticipantSeedItemInput {
  participant_id: number;
  initial_rank?: number | null;
  ranking_points?: number;
}

export interface ParticipantSeedsUpdateInput {
  items: ParticipantSeedItemInput[];
}

// R2 lista oficial por torneo: renglón persistido (cruzado o no).
export interface OfficialListEntry {
  id: number;
  position: number;
  name: string | null;
  federation_id: string | null;
  email: string | null;
  matched_participant_id: number | null;
}

// Política de partidos pendientes al dar de baja (paquete de baja 2026-08-28):
// "walkover" = victoria al rival en cada pendiente; "cancel" = cancelarlos;
// omitido = no tocar partidos.
export type PendingMatchesAction = "walkover" | "cancel";

export interface ParticipantActionInput {
  reason?: string | null;
  pending_matches_action?: PendingMatchesAction | null;
}

export interface ParticipantDropSummary {
  group_removed: number;
  walkovers: number;
  cancelled: number;
  /** Partidos del cuadro sin rival definido: siguen vivos y su rival gana por W.O. al llegar. */
  kept_in_bracket?: number;
  /** Partidos en juego con marcador parcial: siguen abiertos, se cierran con W.O. */
  left_in_progress?: number;
}

export interface ParticipantDropResponse {
  ok: boolean;
  data: {
    participant: TournamentParticipant;
    drop_summary: ParticipantDropSummary;
  };
}

export const PARTICIPANT_STATUSES = [
  "pre_registered",
  "active",
  "confirmed",
  "dropped",
] as const;

export type ParticipantStatus = (typeof PARTICIPANT_STATUSES)[number];

export interface TournamentGroupAssignment {
  id: number;
  tournament_id: number;
  group_id: number;
  participant_id: number;
  status: string;
  seed_position: number | null;
  /** Lugar en la hoja del Round Robin (1..n; Eduardo 2026-10-01): define
   * quién juega contra quién en cada jornada. null = grupo sin acomodar. */
  rr_position?: number | null;
  notes: string | null;
  assigned_at: string | null;
  assigned_by_user_id: number | null;
  removed_at: string | null;
  participant: TournamentParticipant | null;
}

export interface TournamentGroup {
  id: number;
  tournament_id: number;
  name: string;
  code: string | null;
  division_id: number | null;
  division?: { id: number; name: string } | null;
  group_type: string;
  sort_order: number;
  capacity: number | null;
  notes: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  participants?: TournamentGroupAssignment[];
  active_participant_count?: number;
  /** Grupo acomodado: algún miembro vigente tiene lugar en la hoja
   * (`rr_position`); quien no lo tiene va después, por id. */
  rr_has_positions?: boolean;
  /** Orden de la hoja (ids de participante, sin bajas): el guardado o, sin
   * lugares guardados, el con el que se armaron las jornadas. null = todavía
   * no hay partidos ni lugares (se usa el orden que ya muestra la pantalla).
   * Solo lo traen el listado de grupos y las respuestas de acomodar/cambiar
   * de grupo. */
  rr_sheet_order?: number[] | null;
  /** Partidos pendientes que sobran en el grupo (torneo 71): de jugadores
   * que ya no están en él (`kind: "ghost"`) o repetidos (`"duplicate"`).
   * Solo lo trae el listado de grupos. */
  ghost_matches?: GroupStrayMatch[];
}

/** Un partido pendiente que sobra en el grupo (ver `ghost_matches`). */
export interface GroupStrayMatch {
  match_id: number;
  round_label: string | null;
  match_number: number | null;
  status: string;
  is_tie: boolean;
  player1: { id: number; name: string };
  player2: { id: number; name: string };
  /** Quiénes ya no están en el grupo (vacío en un repetido). */
  not_in_group: Array<{ id: number; name: string }>;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  kind: "ghost" | "duplicate";
  /** Repetido: el partido de esa pareja que se queda. */
  keeps_match_id: number | null;
  keeps_round_label: string | null;
  /** Repetido: horario del que se queda (para saber cuál es). */
  keeps_scheduled_date?: string | null;
  keeps_scheduled_time?: string | null;
  keeps_court?: string | null;
}

export interface GroupStrayMatchesCancelResult {
  group_id: number;
  group_name: string;
  cancelled: number;
  cancelled_ghosts?: number;
  cancelled_duplicates?: number;
  matches: GroupStrayMatch[];
  missing_matches: number;
  message: string;
}

/** Cómo quedó cada grupo al cambiar quién está en él (Eduardo 2026-10-13:
 * «si hay cambio de grupo entonces desprogramar los juegos en
 * automático»). `regenerate` = se rehízo y quedó sin programar; `started` =
 * ya tenía resultados (solo se cancela lo de quien salió y se agrega lo que
 * falta); `manual` = calendario manual. */
export interface RrRebuildGroup {
  group_id: number;
  group_name: string;
  division_id?: number | null;
  mode: "regenerate" | "started" | "manual" | "skip";
  changed: boolean;
  created: number;
  cancelled: number;
  removed: number;
  unscheduled: number;
  lost_schedule: number;
  renumbered: number;
  kept_played: number;
}

export interface RrRebuildSummary {
  groups: RrRebuildGroup[];
  rebuilt_groups: Array<{ id: number; name: string }>;
  created: number;
  cancelled: number;
  removed: number;
  unscheduled: number;
  lost_schedule: number;
  /** «Se rehicieron los partidos de Grupo X y Grupo Y y quedaron sin
   * programar: vuelve a ponerles fecha, hora y cancha.» o null. */
  message: string | null;
  warnings: string[];
  notified_players: number;
}

export type MembershipChangeAction = "assign" | "move" | "remove" | "change_division" | "deactivate_group";

/** Vista previa (no escribe) antes de confirmar un cambio de grupo. */
export interface MembershipChangePreview {
  action: MembershipChangeAction;
  groups: RrRebuildGroup[];
  lost_schedule: number;
  unscheduled: number;
  will_rebuild: boolean;
  /** Hay partidos con fecha, hora o cancha que se quedarían sin programar. */
  warning: string | null;
  /** Se rehacen partidos (sin horarios que perder). */
  notice: string | null;
}

export interface MembershipChangePreviewParams {
  action: MembershipChangeAction;
  participant_id?: number | null;
  to_group_id?: number | null;
  group_id?: number | null;
  division_id?: number | null;
}

export interface TournamentGroupMovementEntry {
  id: number;
  tournament_id: number;
  participant_id: number;
  from_group_id: number | null;
  to_group_id: number | null;
  action: string;
  reason: string | null;
  performed_by_user_id: number | null;
  created_at: string | null;
}

export interface GroupCreateInput {
  name: string;
  code?: string | null;
  division_id?: number | null;
  group_type?: string | null;
  sort_order?: number | null;
  capacity?: number | null;
  notes?: string | null;
  is_active?: boolean | null;
}

export type GroupUpdateInput = Partial<GroupCreateInput>;

export interface GroupAssignInput {
  participant_id: number;
  seed_position?: number | null;
  notes?: string | null;
  reason?: string | null;
}

export interface GroupMoveInput {
  to_group_id: number;
  reason?: string | null;
}

export interface GroupActionInput {
  reason?: string | null;
}

export interface GroupsListResponse {
  ok: boolean;
  data: { groups: TournamentGroup[] };
}

export interface GroupResponse {
  ok: boolean;
  /** `rr_rebuild`: desactivar/reactivar/editar un grupo. */
  data: { group: TournamentGroup; rr_rebuild?: RrRebuildSummary };
}

export interface GroupAssignmentResponse {
  ok: boolean;
  data: { assignment: TournamentGroupAssignment; rr_rebuild?: RrRebuildSummary };
}

export interface GroupRemovalResponse {
  ok: boolean;
  data: { removed: boolean; assignment?: TournamentGroupAssignment; rr_rebuild?: RrRebuildSummary };
}

export interface GroupMovementHistoryResponse {
  ok: boolean;
  data: { history: TournamentGroupMovementEntry[] };
}

// ── Acomodar la hoja del Round Robin (Eduardo 2026-10-01) ──────────────────
// Subir/bajar dentro del grupo y cambiar de grupo ANTES de que empiece la
// categoría. El lugar 1..n define los cruces de cada jornada (1 y 2 se
// enfrentan en la última). El backend arma el resumen en español; aquí solo
// se muestra tal cual.

/** Candado «la categoría ya empezó» (resultado, partido en juego, reporte
 * pendiente, playoffs o torneo cerrado). */
export interface RrArrangementLock {
  reason: string;
  message: string;
}

export interface RrPreviewPlayer {
  id: number;
  name: string;
}

/** Una fila del re-plan: `same` no cambia, `reassigned` conserva su horario
 * con otros rivales, `new` nace sin horario y `removed` se quita. */
export interface RrPreviewMatch {
  op: "same" | "reassigned" | "new" | "removed";
  /** Tenía fecha, hora o cancha y se queda sin programar (cambio de grupo). */
  unscheduled?: boolean;
  match_id: number | null;
  match_number: number;
  player1: RrPreviewPlayer;
  player2: RrPreviewPlayer;
  before: { player1: RrPreviewPlayer; player2: RrPreviewPlayer } | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  venue_name: string | null;
}

export interface RrPreviewJornada {
  jornada: number;
  label: string;
  matches: RrPreviewMatch[];
  resting: RrPreviewPlayer[];
}

export interface RrCounts {
  same: number;
  reassigned: number;
  created: number;
  removed: number;
  removed_with_schedule: number;
}

export interface GroupOrderPreview {
  group_id: number;
  group_name: string;
  division_id: number | null;
  locked: boolean;
  lock: RrArrangementLock | null;
  participant_ids: number[];
  has_matches: boolean;
  manual_schedule: boolean;
  changes_matches: boolean;
  summary: string;
  jornadas: RrPreviewJornada[];
  counts: RrCounts;
  affected_participant_ids: number[];
  notify_players: boolean;
  warnings: string[];
  /** null cuando la categoría está bloqueada. */
  plan_token: string | null;
}

export interface GroupOrderSaveInput {
  participant_ids: number[];
  plan_token: string;
}

export interface GroupOrderSaveResult {
  group: TournamentGroup;
  counts: RrCounts;
  notified_players: number;
}

export type GroupChangeMode = "swap" | "move";

export interface GroupChangePreviewGroup {
  group_id: number;
  group_name: string;
  before_order: number[];
  after_order: number[];
  jornadas: RrPreviewJornada[];
  counts: RrCounts;
}

export interface GroupChangeCounts {
  reassigned: number;
  created: number;
  created_without_schedule: number;
  removed: number;
  removed_with_schedule: number;
  series_rebuilt: number;
  /** Pendientes que tenían fecha, hora o cancha y se quedan sin programar. */
  unscheduled?: number;
  cancelled?: number;
  /** Todo lo que tenía horario y lo pierde (sin programar o quitado). */
  lost_schedule?: number;
}

export interface GroupChangePreview {
  mode: GroupChangeMode;
  participant: RrPreviewPlayer;
  swap_with: RrPreviewPlayer | null;
  from_group: { id: number; name: string };
  to_group: { id: number; name: string };
  locked: boolean;
  lock: RrArrangementLock | null;
  summary: string;
  groups: GroupChangePreviewGroup[];
  counts: GroupChangeCounts;
  affected_participant_ids: number[];
  notify_players: boolean;
  warnings: string[];
  /** null cuando la categoría está bloqueada. */
  plan_token: string | null;
}

export interface GroupChangePreviewInput {
  to_group_id: number;
  mode: GroupChangeMode;
  swap_with_participant_id?: number | null;
  /** Grupo que se ve en pantalla: si el jugador ya está en otro, 409
   * `rr_order_mismatch` (la pantalla se quedó vieja). */
  from_group_id?: number | null;
}

export interface GroupChangeInput extends GroupChangePreviewInput {
  reason?: string | null;
  plan_token: string;
}

export interface ReplaceOptions {
  can_replace: boolean;
  reason: string | null;
  outgoing: { participant_id: number; name: string };
  group: { id: number; name: string } | null;
  matches: number;
  allows_new_player: boolean;
  candidates: Array<{ participant_id: number; name: string }>;
}

export interface ReplaceResult {
  outgoing: { participant_id: number; name: string };
  incoming: { participant_id: number; name: string; created: boolean };
  group: { id: number; name: string } | null;
  matches_transferred: number;
  summary: string;
}

export interface GroupChangeResult {
  groups: TournamentGroup[];
  counts: GroupChangeCounts;
  notified_players: number;
  rr_rebuild?: RrRebuildSummary;
}

/** Estado de «acomodar» de una categoría de Round Robin. */
export interface GroupArrangementCategory {
  division_id: number | null;
  division_name: string | null;
  locked: boolean;
  reason: string | null;
  message: string | null;
  has_matches: boolean;
  schedule_type: string | null;
  manual_schedule: boolean;
  /** El perfil de quien entra puede acomodar (capturista/finanzas no). */
  can_edit: boolean;
  /** Quienes ya tienen partidos de grupo en la categoría. Con la categoría
   * empezada, a los demás (un jugador tardío) se les ofrece el «Mover a» de
   * siempre. */
  participants_with_group_matches?: number[];
}

/** Series por equipos: elegibilidad al sorteo de una pareja (completa y con ambos pagos). */
export interface DrawEligibility {
  complete: boolean;
  all_paid: boolean;
  eligible: boolean;
  unpaid: string[];
  members: number;
}

export interface GroupUnassignedResponse {
  ok: boolean;
  data: {
    participants: TournamentParticipant[];
    draw_eligibility?: Record<string, DrawEligibility>;
    // Sin grupo que ya no deberían jugar (torneo 112): inscripción rechazada
    // o pareja con un integrante cuyo registro se borró. Id → motivo.
    stale_registrations?: Record<string, string>;
  };
}

export const MATCH_STATUSES = [
  "scheduled",
  "in_progress",
  "completed",
  "cancelled",
  "walkover",
  "no_show",
  "defaulted",
  "retired",
  "postponed",
  "double_walkover",
] as const;

export type MatchStatus = (typeof MATCH_STATUSES)[number];

export interface MatchParticipantBrief {
  id: number;
  player_profile_id: number | null;
  team_id?: number | null;
  participant_kind?: "player" | "team";
  display_name: string | null;
  /** Nombre corto legible (dobles: "Alonso / Bruno") para brackets/grids. */
  short_name?: string | null;
  current_status: string;
  tournament_rank?: number | null;
  initial_rank?: number | null;
  current_rank?: number | null;
}

/** Nombre corto preferente de un brief: parejas legibles en espacios chicos. */
export function matchParticipantShortName(
  participant: MatchParticipantBrief | null | undefined,
): string {
  if (!participant) return "Por definir";
  return (
    participant.short_name?.trim() ||
    participant.display_name?.trim() ||
    `Participante #${participant.id}`
  );
}

export interface TournamentMatchScoreSet {
  set_number: number;
  player1_games: number;
  player2_games: number;
  is_tiebreak: boolean;
  is_super_tiebreak: boolean;
  /** Puntos del PERDEDOR del tiebreak ("7-6(5)" → 5); null si no aplica. */
  tiebreak_points?: number | null;
}

export type MatchKind = "match" | "tie";
export interface LinePlayer {
  id: number;
  name: string;
}

export interface DeletedRegistration {
  id: number;
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_by?: { id: number; name: string } | null;
  participant_id?: number;
  player_profile_id?: number | null;
  name?: string;
  email?: string | null;
  phone?: string | null;
  division_id?: number | null;
  division_name?: string | null;
  status?: string;
  initial_rank?: number | null;
  registered_at?: string | null;
  status_history?: { old_status: string | null; new_status: string; reason: string | null; changed_at: string | null; changed_by: number | null }[];
  orders?: { id: number; status: string; provider: string | null; gross_amount: string | null; currency: string | null; created_at: string | null }[];
}

export interface TournamentMatch {
  id: number;
  /** Series por equipos: "tie" = enfrentamiento padre; las líneas traen parent_match_id. */
  match_kind?: MatchKind;
  parent_match_id?: number | null;
  line_kind?: TeamPlayLineKind | null;
  line_players?: { team1?: LinePlayer[]; team2?: LinePlayer[] } | null;
  /** Solo en una serie (match_kind "tie"): sus líneas, en orden. */
  lines?: TournamentMatch[] | null;
  /** Solo en una serie: integrantes de cada equipo (para alinear líneas). */
  tie_members?: { team1: LinePlayer[]; team2: LinePlayer[] } | null;
  tournament_id: number;
  group_id: number | null;
  division_id: number | null;
  division_name: string | null;
  group_name: string | null;
  round_label: string | null;
  /** Etiqueta canónica derivada cuando la ronda es de playoffs ("Jornada N" en BD); display-only. */
  round_display_label?: string | null;
  match_number: number | null;
  player1_participant_id: number | null;
  player2_participant_id: number | null;
  player1: MatchParticipantBrief | null;
  player2: MatchParticipantBrief | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  status: MatchStatus | string;
  winner_participant_id: number | null;
  winner: MatchParticipantBrief | null;
  score_text: string | null;
  score_sets?: TournamentMatchScoreSet[];
  notes: string | null;
  tournament_name?: string | null;
  /** Sede del partido (null = sede principal/única); en el calendario del
   * club este campo trae la sede del torneo. */
  venue_name?: string | null;
  /** Solo en el calendario del club: sede EFECTIVA del partido (la suya o,
   * sin ella, la principal de su torneo). Si no viene, se asume la sede
   * consultada. */
  match_venue_name?: string | null;
  /** Motivo del último cambio de horario o posposición (lluvia, retraso…). */
  schedule_change_reason?: string | null;
  /** Solo admin: árbitro asignado (2026-10-16). */
  referee_user_id?: number | null;
  referee_name?: string | null;
  /** Publicar horarios (Eduardo 2026-10-01). Admin: true = «sin publicar»
   * (tiene horario y los jugadores aún no lo ven). Jugador: true = el
   * servidor vació fecha/hora/cancha → «Horario por confirmar». Sin el campo
   * = publicado (payloads viejos). */
  schedule_pending?: boolean | null;
  /** Solo admin: cuándo se publicó su horario (null = sin publicar). */
  schedule_published_at?: string | null;
  schedule_published_by_user_id?: number | null;
  /** Minutos que ocupa el partido: el Bloque min guardado de SU categoría
   * (null si la categoría no tiene uno). El grid pinta cada tarjeta con su
   * propia duración, también las de otras categorías y otros torneos. */
  duration_minutes?: number | null;
  tournament_type?: TournamentType | string | null;
  tournament_sport?: SportKey | string | null;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TournamentMatchHistoryEntry {
  id: number;
  tournament_id: number;
  match_id: number | null;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  performed_by_user_id: number | null;
  created_at: string | null;
}

export interface MatchCreateInput {
  group_id?: number | null;
  division_id?: number | null;
  round_label?: string | null;
  match_number?: number | null;
  player1_participant_id?: number | null;
  player2_participant_id?: number | null;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  court?: string | null;
  venue_name?: string | null;
  status?: string | null;
  winner_participant_id?: number | null;
  score_text?: string | null;
  notes?: string | null;
  /** Duración (min) con que el calendario revisa choques por INTERVALO
   * (17:00 de 90 min choca con 17:15). Solo la manda el calendario; sin ella
   * el servidor conserva la revisión por hora exacta. */
  duration_minutes?: number | null;
}

export type MatchUpdateInput = Partial<MatchCreateInput> & {
  /** Motivo del cambio de horario (lluvia, retraso…); solo aplica al mover un partido ya programado. */
  schedule_change_reason?: string | null;
  /** false = el choque de JUGADOR sigue por hora exacta aunque venga
   * `duration_minutes` (Canchas: su bloque es el de reservas del club y
   * mezcla torneos; solo vale para la cancha). Sin el campo: por traslape. */
  check_players_by_overlap?: boolean;
  /** true = el admin confirmó «de todos modos» programarlo antes de que
   * terminen sus partidos previos (sin él: 409 `round_order_conflict`). */
  allow_round_order_conflict?: boolean;
};

export interface MatchResultInput {
  winner_participant_id?: number | null;
  score_text?: string | null;
  notes?: string | null;
  exception_type?: string | null;
}

export interface MatchActionInput {
  reason?: string | null;
}

export interface RoundRobinInput {
  group_id?: number | null;
  division_id?: number | null;
  round_label?: string | null;
  round_count?: number | null;
}

export interface EliminationGenerateInput {
  group_id?: number | null;
  division_id?: number | null;
  regenerate_empty?: boolean;
  generation_mode?: "ranking" | "random" | "manual";
  participant_ids_order?: number[];
}

export interface EliminationNextRoundInput {
  division_id?: number | null;
}

export interface EliminationSkeletonResponse {
  ok: boolean;
  data: {
    created: number;
    round_labels: string[];
    match_ids: number[];
  };
}

export interface ScheduleReadinessRow {
  division_id: number | null;
  name: string;
  format_type: string | null;
  format_saved: boolean;
  participants: number;
  matches: number;
  has_draw: boolean;
  bracket_complete: boolean;
  scheduled: number;
  armed: number;
  resolved: number;
  unranked: number;
  needs_groups: boolean;
  ungrouped: number;
  /** Inscritos activos que no aparecen en ningún partido del draw vigente
   * de su categoría (0 si aún no hay draw). Opcional por caches viejos. */
  out_of_draw?: number;
}

export interface RankingSystemSummary {
  id: number;
  name: string;
  slug: string;
  scope: string;
  preset: string | null;
  /** points_table (FMT) | custom_points (ranking propio del organizador). */
  provider: string;
  is_custom: boolean;
  /** Reglas del ranking propio; null en los sistemas de plataforma. */
  rules: CustomRankingRulesPayload | null;
  owner_user_id: number | null;
  season_start: string | null;
  season_end: string | null;
  is_active: boolean;
  categories: Array<{ id: number; code: string; name: string; best_n: number; doubles_pct: number }>;
  /** Solo rankings propios: agregados para la tarjeta del hub. */
  players_count?: number;
  tournaments_count?: number;
  top3?: Array<{ position: number; player_profile_id: number; display_name: string; points_total: number }>;
}

export interface CustomRankingRulesPayload {
  match_win: number;
  match_loss: number;
  walkover_win: number;
  walkover_loss: number;
  retired_win?: number | null;
  retired_loss?: number | null;
  defaulted_win?: number | null;
  defaulted_loss?: number | null;
  set_win?: number;
  set_loss?: number;
  game_win?: number;
  game_loss?: number;
  tiebreak_games?: number;
  super_tiebreak_as?: "set" | "games";
  super_tiebreak_games?: number;
  bonus: { champion: number; finalist: number; sf: number; qf: number; r16: number; r32: number };
  /** Bono por lugar final del grupo en Round Robin, por tamaño ("3": [1º,2º,3º]). */
  group_bonus?: Record<string, number[]>;
  participation_bonus?: number;
  undefeated_bonus?: number;
  tiebreakers?: Array<"wins" | "sets_diff" | "games_diff" | "head_to_head">;
}

export interface CustomRankingCategoryInput {
  id?: number;
  name: string;
  best_n: number;
}

export interface RankingSystemEvent {
  id: number;
  player_profile_id: number;
  display_name: string;
  tournament_id: number | null;
  tournament_name: string | null;
  kind: string;
  round_label: string | null;
  points: number;
  notes: string | null;
  created_at: string | null;
}

export interface RankingStandingRow {
  position: number;
  player_profile_id: number;
  display_name: string;
  points_total: number;
  points_singles: number;
  points_doubles_counted: number;
  /** Desempates del ranking propio (0090). */
  matches_won?: number;
  matches_lost?: number;
  sets_diff?: number;
  games_diff?: number;
  tournaments_counted: number;
}

export interface TournamentRankingLinkRow {
  link_id: number;
  division_id: number | null;
  draw_kind: string;
  grade: string;
  /** Ranking propio: multiplica los puntos de este torneo. */
  multiplier?: number;
  ranking_system_id: number;
  system_name: string;
  system_slug: string;
  age_category_id: number;
  category_code: string;
  category_name: string;
}

export interface PublicRankingPayload {
  system: { name: string; slug: string };
  category: { id: number; code: string; name: string };
  categories: Array<{ code: string; name: string }>;
  published_at: string | null;
  rows: RankingStandingRow[];
}

export interface BulkProgramInput {
  group_id?: number | null;
  division_id?: number | null;
  match_ids?: number[];
  base_date: string;
  start_time: string;
  end_time?: string;
  duration_minutes: number;
  courts: string[];
  /** Sede destino de la corrida; omitir = sede principal/única. */
  venue_name?: string | null;
  buffer_minutes?: number;
  only_unscheduled?: boolean;
}

export interface BulkProgramResponse {
  ok: boolean;
  data: {
    matches: TournamentMatch[];
    summary: {
      updated: number;
      skipped_protected: number;
      skipped_already_scheduled: number;
      /** «Aplicar orden» no mueve horarios ya publicados (los jugadores ya los ven). */
      skipped_published?: number;
      skipped_occupied_slot?: number;
      deferred_player_busy: number;
      /** Orden de rondas (2026-10-03): quedaron sin programar porque sus
       * partidos previos terminan después (o no tienen horario), con motivo. */
      waiting_previous?: number;
      waiting_previous_matches?: Array<{ match_id: number; reason: string }>;
      courts_used: string[];
    };
  };
}

/** «Publicar horarios» de una categoría en un día (Eduardo 2026-10-01). */
export interface SchedulePublishInput {
  /** null = todas las categorías de ese día (torneo sin categorías). */
  division_id: number | null;
  scheduled_date: string;
}

/** Vista previa (`dry_run`) para el diálogo: cuántos horarios y jugadores. */
export interface SchedulePublishPreview {
  dry_run?: boolean;
  to_publish: number;
  /** Jugadores que recibirían el aviso (0 con los avisos apagados). */
  players: number;
  venues?: string[];
  /** false = los avisos del torneo están apagados (cerrado, reabierto sin
   * avisar o candado del jugador): se publican sin avisar. */
  notices_open?: boolean;
  /** true = torneo cerrado o reabierto «sin avisar». */
  muted?: boolean;
  match_ids?: number[];
  division_id?: number | null;
  scheduled_date?: string;
}

export interface SchedulePublishResult {
  published: number;
  notified_players: number;
  /** true = torneo cerrado o reabierto «sin avisar»: se marcaron sin avisar. */
  muted?: boolean;
  notices_open?: boolean;
  match_ids?: number[];
  division_id?: number | null;
  scheduled_date?: string;
}

export interface BulkUnscheduleSummary {
  updated: number;
  skipped: number;
  /** Publicados que se quitaron: volvieron a «sin publicar». */
  unpublished?: number;
  /** Jugadores que recibieron el aviso «sin horario». */
  notified_players?: number;
}

export interface MatchesListResponse {
  ok: boolean;
  data: { matches: TournamentMatch[] };
}

export interface CourtBlock {
  id: number;
  venue_name: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  court: string;
  reason: string | null;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CourtBlockCreateInput {
  venue_name: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  court: string;
  reason?: string | null;
  notes?: string | null;
  /** Duración de los partidos en el calendario: el bloqueo choca con un
   * partido que siga en juego aunque haya empezado antes. */
  match_duration_minutes?: number | null;
}

export interface ClubCalendarResponse {
  ok: boolean;
  data: {
    venues: string[];
    matches: TournamentMatch[];
    blocks: CourtBlock[];
  };
}

export interface DrawCeremonySeedRow {
  seed: number;
  line: number | null;
  participant_id: number;
  display_name: string;
}

export interface DrawCeremonyBand {
  band: string;
  drawn: boolean;
  seeds: DrawCeremonySeedRow[];
}

export interface DrawCeremony {
  id: number;
  tournament_id: number;
  division_id: number | null;
  group_id: number | null;
  scheduled_at: string | null;
  performed_at: string | null;
  /** true cuando la hora viene del plan del Paso 1 y aún no hay acta. */
  planned?: boolean;
  method: "itf_standard" | "random" | "manual" | "prelim" | string | null;
  /** Tarjeta del jugador: sorteo de GRUPOS (Round Robin) o del cuadro. */
  kind?: "rr_groups" | "bracket";
  draw_size: number | null;
  seed_count: number | null;
  // Ausente en la tarjeta compacta del home (solo hora/estado).
  payload?: {
    standard?: string;
    bands?: DrawCeremonyBand[];
    unseeded_count?: number;
    direct_advancer_count?: number;
    // Sorteo de grupos de Round Robin (method "rr_groups", Eduardo 2026-09-26).
    rule?: string;
    groups?: DrawCeremonyGroup[];
    seeded_count?: number;
    announced?: boolean;
    post_draw_changes?: DrawCeremonyGroupChange[];
  } | null;
}

export interface DrawCeremonyGroupMember {
  participant_id: number;
  display_name: string;
  seed: number | null;
  /** "serpentina" (sembrado) o "sorteo" (S/R). */
  via: "serpentina" | "sorteo" | string;
}

export interface DrawCeremonyGroup {
  group_id: number;
  name: string;
  members: DrawCeremonyGroupMember[];
}

export interface DrawCeremonyGroupChange {
  at: string;
  participant_id: number;
  display_name: string;
  action: "assigned" | "moved" | "removed" | string;
  from_group: string | null;
  to_group: string | null;
}

/** Vista previa del sorteo de grupos (GET /groups/draw-preview). */
export interface GroupDrawPreview {
  division_id: number | null;
  rule: string;
  can_draw: boolean;
  blockers: string[];
  groups: Array<{ id: number; name: string }>;
  seeded: Array<{ participant_id: number; display_name: string; seed: number; group_id: number; group_name: string }>;
  unseeded: Array<{ participant_id: number; display_name: string }>;
  already_assigned_count: number;
  ceremony: DrawCeremony | null;
}

/** Sorteo del cuadro a nivel torneo (Paso 1): si habrá y a qué hora (CDMX). */
export interface DrawCeremonyPlan {
  enabled: boolean;
  scheduled_at: string | null;
}

export interface DrawCeremonyResponse {
  ok: boolean;
  data: { ceremony: DrawCeremony | null; plan?: DrawCeremonyPlan };
}

export interface PlayerTournamentResultsResponse {
  ok: boolean;
  data: {
    tournament: { id: number; name: string };
    settings?: PlayerReportableSettings;
    matches: TournamentMatch[];
    reports: MatchResultReport[];
    my_participant_id: number | null;
    player_visibility?: PlayerStageVisibility;
    draw_ceremonies?: DrawCeremony[];
    /** Formato efectivo del torneo y de cada categoría (Draw del jugador). */
    format_type?: TournamentType | string | null;
    divisions?: { id: number; name: string; format_type?: string | null; is_active?: boolean }[];
    /** Orden de la hoja por grupo (ids de participante), solo de los grupos
     * acomodados; vacío mientras el jugador aún no ve la etapa. */
    group_orders?: Record<string, number[]>;
    /** Fechas de cada jornada (solo torneos sin calendario de canchas), de
     * las jornadas que el jugador ve. */
    round_windows?: JornadaWindow[];
    /** Jornada actual de la categoría del jugador (Eduardo 2026-10-05, la
     * misma regla del servidor que «Descansas esta jornada»): «Juegos» abre
     * ahí. null con calendario de canchas, en la escalera, sin jornadas, con
     * la temporada ya jugada o con la jornada actual ya terminada (abre en
     * su siguiente reto, como antes). */
    current_jornada?: number | null;
    /** Quién descansa cada jornada, por grupo (Eduardo 2026-10-05), solo de
     * los grupos que el jugador ve. */
    rest_by_round?: JornadaRestEntry[];
  };
}

/** Quiénes descansan en una jornada de un grupo de round robin (grupo impar:
 * el rol no les da partido). Sin bajas; escalera y eliminación no traen. */
export interface JornadaRestEntry {
  division_id: number | null;
  division_name: string | null;
  round_number: number;
  round_label: string;
  group_id: number;
  group_name: string;
  participants: { id: number; name: string }[];
}

/** Fechas GUARDADAS de una jornada (no las sugeridas), por categoría. */
export interface JornadaSavedWindow {
  division_id: number | null;
  round_number: number;
  label: string;
  starts_on: string;
  ends_on: string;
}

export interface JornadaOverviewResponse {
  ok: boolean;
  data: {
    saved_windows: JornadaSavedWindow[];
    rest_by_round: JornadaRestEntry[];
  };
}

export interface JornadaWindow {
  division_id: number | null;
  /** round_label de los partidos (espacios juntados): se busca por el par
   * categoría/round_label, así «Playoff 1» también trae sus fechas. */
  round_label: string;
  /** null cuando la etiqueta no es «Jornada N»/«Semana N» (p. ej. «Playoff 1»). */
  round_number: number | null;
  starts_on: string;
  ends_on: string;
}

export interface MatchResponse {
  ok: boolean;
  data: {
    match: TournamentMatch;
    /** Avisos del cuadro tras capturar (p. ej. un W.O. automático que no se
     * cambió porque la ronda siguiente ya se jugó). */
    notices?: string[];
    /** Solo el PATCH del calendario: true = sí salió aviso a los jugadores
     * (Publicar horarios). Sin el campo, no se afirma que se avisó. */
    players_notified?: boolean;
  };
}

export interface MatchHistoryResponse {
  ok: boolean;
  data: { history: TournamentMatchHistoryEntry[] };
}

export interface RoundRobinResponse {
  ok: boolean;
  data: {
    created: number;
    skipped: number;
    updated?: number;
    groups_processed: number;
    round_count?: number;
    round_labels?: string[];
    match_ids: number[];
  };
}

export interface RoundRobinPlayoffsResponse {
  ok: boolean;
  data: {
    created: number;
    skipped: number;
    updated?: number;
    playoff_players_count: number;
    playoff_round_count: number;
    start_jornada: number;
    round_labels?: string[];
    match_ids: number[];
    qualified_participant_ids?: number[];
  };
}

export interface EliminationGenerateResponse {
  ok: boolean;
  data: {
    created: number;
    skipped: number;
    bye_count: number;
    direct_advancer_count?: number;
    draw_size: number;
    requested_draw_size?: number;
    draw_size_auto_adjusted?: boolean;
    next_round_size?: number;
    round_label: string | null;
    match_ids: number[];
    already_had_matches: boolean;
    seeding_source?: string;
    generation_mode?: "ranking" | "random" | "manual";
    draw_ceremony?: DrawCeremony | null;
  };
}

export interface EliminationNextRoundResponse {
  ok: boolean;
  data: {
    created: number;
    skipped: number;
    updated?: number;
    from_round_label: string;
    round_labels?: string[];
    match_ids: number[];
    qualified_participant_ids?: number[];
  };
}

export interface StandingsRow {
  position: number;
  participant_id: number;
  player_profile_id: number | null;
  team_id?: number | null;
  participant_kind?: "player" | "team";
  /** Integrantes de la pareja/equipo, en orden, con su perfil. */
  team_members?: Array<{ player_profile_id: number; display_name: string }> | null;
  display_name: string;
  /** Nombre corto legible (dobles: "Alonso / Bruno"). */
  short_name?: string | null;
  current_status: string;
  tournament_rank: number | null;
  initial_rank: number | null;
  current_rank: number | null;
  /** Lugar vivo dentro de su categoría (1..N), en el orden del ranking. */
  division_position?: number | null;
  /** Escalera: lugar con el que entró al grupo (1..N) y lugar actual en el grupo. */
  initial_group_rank?: number | null;
  group_position?: number | null;
  rank_delta: number | null;
  rank_movement: RankMovement | null;
  group_id: number | null;
  group_name: string | null;
  /** Categoría del participante (Eduardo 2026-09-24: Jugadores por categoría). */
  division_id?: number | null;
  division_name?: string | null;
  /** Puntos de ranking capturados por el admin (torneo rankeado por puntos). */
  ranking_points?: number | null;
  matches_played: number;
  wins: number;
  losses: number;
  draws: number;
  sets_won: number;
  sets_lost: number;
  games_won: number;
  games_lost: number;
  points_for: number;
  points_against: number;
  differential: number;
  points: number;
  win_pct: number | null;
  form: string[];
}

export interface StandingsGroup {
  group_id: number | null;
  group_name: string;
  is_active: boolean;
  sort_order: number;
  // Solo en el payload del JUGADOR (Eduardo 2026-10-01): «Grupo 1» puede
  // repetirse entre categorías y la categoría los distingue.
  division_id?: number | null;
  division_name?: string | null;
  // Presentes SOLO cuando el grupo tiene standings_rules no-legacy; en modo
  // legacy el backend las omite (ausencia = comportamiento histórico).
  standings_rules_mode?: StandingsRulesMode;
  points_enabled?: boolean;
  // Solo lectura en el payload del jugador: corte a playoffs y desempate del
  // formato de la categoría (null cuando el formato no los define).
  qualify_count?: number | null;
  wildcard_count?: number | null;
  wildcard_position?: number | null;
  tiebreakers?: StandingsTiebreaker[] | null;
  standings: StandingsRow[];
}

export interface StandingsSummary {
  total_participants: number;
  matches_with_results: number;
  groups_count: number;
  scoring: {
    win: number;
    draw: number;
    loss: number;
  };
}

export interface LegacyStatsEntry {
  gg: number;
  gp: number;
  w: number;
  l: number;
}

export interface LegacyStatsPayload {
  available: boolean;
  by_week?: Record<string, Record<string, LegacyStatsEntry>>;
}

export interface StandingsPayload {
  tournament_id: number;
  sport: SportKey | string;
  summary: StandingsSummary;
  // Modo de desempate del scope: con points_enabled=false (preset ITF sin
  // liga por puntos) las vistas ocultan la columna PTS. Presentes SOLO en
  // modos no-legacy: en legacy el backend las omite por completo (respuesta
  // histórica exacta; su ausencia = comportamiento histórico).
  standings_rules_mode?: StandingsRulesMode;
  points_enabled?: boolean;
  points_config?: StandingsPointsConfig | null;
  ranking: StandingsRow[];
  groups: StandingsGroup[];
  legacy_stats?: LegacyStatsPayload;
}

export interface RoundRobinBracketPreviewSlot {
  seed: number | null;
  participant_id: number | null;
  display_name: string;
  /** Nombre corto legible (dobles: "Alonso / Bruno"). */
  short_name?: string | null;
  source: "qualifier" | "bye" | "preliminary_winner" | string;
  group_name?: string | null;
  group_position?: number | null;
  qualification_reason?: string | null;
  /** Solo en slots con source="preliminary_winner": # de partido preliminar
   * cuyo ganador ocupará este lado (destino ITF declarado por el backend). */
  from_preliminary_slot?: number;
}

export interface RoundRobinBracketPreviewMatchup {
  slot: number;
  label: string;
  player1: RoundRobinBracketPreviewSlot;
  player2: RoundRobinBracketPreviewSlot;
  is_bye: boolean;
  winner_source: "match" | "bye" | "direct_advancer" | string;
  winner_advances_to: string;
}

export interface RoundRobinBracketPreviewRound {
  key: string;
  label: string;
  matchups: RoundRobinBracketPreviewMatchup[];
}

export interface RoundRobinBracketPreviewPayload {
  enabled: boolean;
  policy: RoundRobinBracketPolicy | string;
  qualifier_count: number;
  target_bracket_size: number;
  preliminary_matches_count: number;
  bye_count: number;
  rounds: RoundRobinBracketPreviewRound[];
  summary: string;
}

export interface RoundRobinManualSeedOverride {
  participant_id: number;
  seed: number;
  reason?: string;
  performed_by_user_id?: number | null;
  created_at?: string | null;
}

export interface RoundRobinQualificationPreviewPayload {
  tournament_id: number;
  division_id: number | null;
  enabled: boolean;
  rules: RoundRobinQualificationRule[];
  seed_method: RoundRobinSeedMethod | null;
  qualifiers: Array<StandingsRow & {
    seed: number;
    qualification_rule: RoundRobinQualificationRule["type"] | string | null;
    qualification_reason: string;
  }>;
  manual_seed_overrides?: RoundRobinManualSeedOverride[];
  standings: StandingsPayload;
  summary: {
    qualifier_count: number;
    groups_count: number;
    matches_with_results: number;
    /** Grupos que dice el Formato vs. grupos reales con jugadores. */
    configured_groups_count?: number | null;
    real_groups_count?: number;
    groups_mismatch?: boolean;
    /** Motivo por el que los playoffs automáticos no se generaron (null = sin bloqueo). */
    playoffs_auto_blocked?: { reason: string | null; at: string | null } | null;
    /** La opción «evitar cruces del mismo grupo» está activa. */
    avoid_same_group_first_round?: boolean;
    /** Aviso si las reglas hacen imposible evitar un cruce del mismo grupo. */
    same_group_warning?: string | null;
  };
  bracket_preview?: RoundRobinBracketPreviewPayload;
}

export interface RoundRobinQualificationPreviewResponse {
  ok: boolean;
  data: RoundRobinQualificationPreviewPayload;
}

export interface RoundRobinQualificationOverridesInput {
  division_id?: number | null;
  reason: string;
  seed_overrides: Array<{ participant_id: number; seed: number }>;
}

/** Eduardo 2026-09-24: hasta que el admin termina el wizard (publicado + grupos
 * completos + draw en toda categoría con inscritos) el jugador solo ve la
 * lista de inscritos: sin Calendario, Draw ni Ranking. */
export interface PlayerStageVisibility {
  setup_complete: boolean;
  reason: "draft" | "groups" | "draw" | null;
}

export interface PlayerStandingsPayload extends StandingsPayload {
  player_visibility?: PlayerStageVisibility;
  /** participant_id → foto que ven los demás jugadores (perfil o credencial), null = iniciales. */
  photos?: Record<string, string | null>;
  my_participant_id: number | null;
  my_position: number | null;
  my_row: StandingsRow | null;
}

export interface StandingsResponse {
  ok: boolean;
  data: StandingsPayload;
}

export interface PlayerStandingsResponse {
  ok: boolean;
  data: PlayerStandingsPayload;
}

export const COMMUNICATION_TYPES = [
  "general",
  "schedule_change",
  "group_change",
  "result",
  "standings",
  "rules",
  "player_change",
] as const;

export type CommunicationType = (typeof COMMUNICATION_TYPES)[number];

export const COMMUNICATION_AUDIENCES = [
  "all_players",
  "group",
  "player",
  "admins",
] as const;

export type CommunicationAudience = (typeof COMMUNICATION_AUDIENCES)[number];

export const COMMUNICATION_CHANNELS = ["web", "email", "whatsapp"] as const;

export type CommunicationChannel = (typeof COMMUNICATION_CHANNELS)[number];

export const COMMUNICATION_STATUSES = ["draft", "published"] as const;

export type CommunicationStatus = (typeof COMMUNICATION_STATUSES)[number];

export interface OutboxSummary {
  total: number;
  by_channel: Record<string, number>;
  by_status: Record<string, number>;
}

export interface TournamentCommunication {
  id: number;
  tournament_id: number;
  title: string;
  body: string;
  communication_type: CommunicationType | string;
  audience_type: CommunicationAudience | string;
  group_id: number | null;
  group_name: string | null;
  player_profile_id: number | null;
  player_display_name: string | null;
  channels: string[];
  status: CommunicationStatus | string;
  published_at: string | null;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  outbox_summary?: OutboxSummary;
}

export interface CommunicationCreateInput {
  title: string;
  body: string;
  communication_type?: CommunicationType | string | null;
  audience_type?: CommunicationAudience | string | null;
  group_id?: number | null;
  player_profile_id?: number | null;
  channels?: string[] | null;
}

export type CommunicationUpdateInput = Partial<CommunicationCreateInput>;

export interface CommunicationsListResponse {
  ok: boolean;
  data: { communications: TournamentCommunication[] };
}

export interface CommunicationResponse {
  ok: boolean;
  data: { communication: TournamentCommunication };
}

export interface DeliverySummaryResponse {
  ok: boolean;
  data: { configured: boolean; sent: number; failed: number; pending: number };
}

export interface ClubCourtHoursEntry {
  weekday: number;
  opens_at: string;
  closes_at: string;
  slot_minutes: number;
}

export interface ClubCourt {
  id: number;
  name: string;
  surface: string | null;
  sport: SportKey | string;
  status: "active" | "maintenance" | string;
  sort_order: number;
  hours: ClubCourtHoursEntry[];
}

export interface ClubInfo {
  id: number;
  name: string;
  slug: string;
  invite_token: string;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  address_line: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  google_maps_url: string | null;
  logo_url: string | null;
  is_active: boolean;
  created_at: string | null;
  staff_count?: number;
  settings?: ClubSettings;
}

export type ClubMembershipPayer = "player" | "club" | "none";
export type ClubJoinMode = "request" | "invite" | "both";
export type ClubReservationConfirmation = "auto" | "club_approves";

/** Configuración del club (etapa 1). Los campos de cobro solo se guardan. */
export interface ClubSettings {
  membership_payer: ClubMembershipPayer;
  grace_days: number;
  after_grace: "no_booking" | "suspended";
  club_fee_mode: "external" | "in_app";
  join_mode: ClubJoinMode;
  invite_requires_approval: boolean;
  reservation_confirmation: ClubReservationConfirmation;
  billing_enforced?: boolean;
}

export type ClubTodayCellKind = "free" | "reserved" | "pending" | "tournament" | "blocked";

export interface ClubTodayCell {
  start_time: string;
  end_time: string;
  kind: ClubTodayCellKind;
  label: string | null;
  detail: string | null;
  reservation_id?: number;
  /** Horario real de la reserva (puede abarcar varios turnos). */
  res_start?: string;
  res_end?: string;
}

export interface ClubTodayCourt {
  id: number;
  name: string;
  status: string;
  open: boolean;
  sport?: string | null;
  slot_minutes?: number | null;
  opens_at?: string | null;
  closes_at?: string | null;
  slots: ClubTodayCell[];
}

export interface ClubToday {
  club: { id: number; name: string };
  date: string;
  reservations_today: number;
  slots_today: number;
  slots_taken: number;
  pending_requests: number;
  pending_reservations: number;
  members_active: number;
  members_by_status: Record<string, number>;
  tournament_matches_today: number;
  tournament_courts: string[];
  courts: ClubTodayCourt[];
  settings: ClubSettings;
}

export interface ClubMember {
  id: number;
  club_id: number;
  player_profile_id: number;
  status: "active" | "suspended" | string;
  credential_url: string | null;
  credential_token: string | null;
  joined_at: string | null;
  player: {
    first_name: string | null;
    last_name: string | null;
    display_name: string | null;
    email: string | null;
    phone: string | null;
  };
}

/** «?player_profile_id=…» del perfil familiar elegido (vacío si no hay). */
function profileQuery(playerProfileId?: number | null): string {
  return playerProfileId ? `?player_profile_id=${encodeURIComponent(String(playerProfileId))}` : "";
}

export interface ClubReservationInfo {
  id: number;
  club_id: number;
  court_id: number;
  court_name: string | null;
  date: string | null;
  start_time: string;
  end_time: string;
  status: "active" | "cancelled" | "no_show" | string;
  source: "club" | "member" | string;
  member_id: number | null;
  member_name: string | null;
  guest_names: string | null;
}

export interface ClubBookingRules {
  max_advance_days: number | null;
  min_advance_hours: number | null;
  max_active_reservations: number | null;
  max_per_day: number | null;
  max_per_week: number | null;
  max_consecutive_blocks: number | null;
  cancel_hours: number | null;
  prime_time_json: Record<string, Array<[string, string]>> | null;
  prime_max_per_week: number | null;
}

export interface PlayerClubBrief {
  id: number;
  name: string;
  slug: string;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  address_line?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  google_maps_url?: string | null;
  is_active?: boolean;
  courts_count?: number;
  relationship_status?: string | null;
  membership_id?: number | null;
  credential_url?: string | null;
  credential_token?: string | null;
  join_mode?: "request" | "invite" | "both" | string;
  /** Club etapa 2: solo se muestra; el cobro llega en la etapa 3. */
  membership?: PlayerClubMembershipInfo | null;
}

export interface PlayerClubMembershipInfo {
  status: string | null;
  joined_at: string | null;
  payer: "player" | "club" | "none" | string | null;
  grace_days: number | null;
  after_grace: string | null;
  billing_enforced: boolean;
}

export interface PlayerClubWeekUsage {
  used: number;
  max_per_week: number | null;
  remaining: number | null;
}

export interface ClubMembershipPaymentSettings {
  club_id: number;
  membership_fee_amount: number;
  currency: string;
  payment_instructions: string | null;
  updated_at: string | null;
}

export interface ClubMembershipPaymentOrder {
  id: number;
  club_id: number;
  club: PlayerClubBrief | null;
  request_id: number | null;
  player_profile_id: number;
  amount: number;
  currency: string;
  status: string;
  payment_instructions: string | null;
  /** Authenticated API path (/club-membership-receipts/{id}); open via openAuthenticatedFile. Null when no receipt yet. */
  receipt_url: string | null;
  receipt_uploaded_at: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  player: PlayerClubApplication["player"] | null;
}

export interface PlayerClubApplication {
  id: number;
  club_id: number;
  club: PlayerClubBrief | null;
  player_profile_id: number;
  status: string;
  /** app = pidió desde Clubes; invite = entró con el link y espera aprobación. */
  source?: "app" | "invite" | string;
  message: string | null;
  created_at: string | null;
  reviewed_at: string | null;
  player: {
    first_name: string | null;
    last_name: string | null;
    display_name: string | null;
    email: string | null;
    phone: string | null;
  };
  payment_order?: ClubMembershipPaymentOrder | null;
}

export interface PlayerClubMarketplaceResponse {
  ok: boolean;
  data: {
    profile?: { id: number; display_name: string | null };
    clubs: PlayerClubBrief[];
    my_clubs?: PlayerClubBrief[];
    requests?: PlayerClubApplication[];
    available_clubs?: PlayerClubBrief[];
    /** Ciudades A→Z de los clubes activos (filtro de «Buscar club»). */
    cities?: string[];
  };
}

export interface PlayerAvailabilityCourt {
  id: number;
  name: string;
  surface: string | null;
  slot_minutes: number;
  slots: Array<{
    start_time: string;
    end_time: string;
    available: boolean;
    /** tournament = partido de torneo; blocked = bloqueo del club; reserved = otra reserva. */
    reason?: "tournament" | "blocked" | "reserved" | null;
  }>;
}

export interface PlayerCommunication {
  id: number;
  tournament_id: number;
  tournament_name: string | null;
  title: string;
  body: string;
  communication_type: CommunicationType | string;
  audience_type: CommunicationAudience | string;
  group_name: string | null;
  published_at: string | null;
  /** Aviso del sorteo (Eduardo 2026-10-05): lleva a los resultados de ESE
   * sorteo. Nulo en los demás avisos o si el sorteo ya no está registrado. */
  draw_results_url?: string | null;
}

export interface PlayerCommunicationsResponse {
  ok: boolean;
  data: { communications: PlayerCommunication[] };
}

export interface PlayerTournamentBrief {
  id: number;
  name: string;
  slug: string;
  tournament_type: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
}

export interface PlayerTournamentsResponse {
  ok: boolean;
  data: { tournaments: PlayerTournamentBrief[] };
}

export const RULE_STATUSES = ["draft", "published"] as const;

export type RuleStatus = (typeof RULE_STATUSES)[number];

export interface RuleAcceptanceSummary {
  total_enrolled: number;
  accepted: number;
  pending: number;
}

export interface RuleAttachment {
  url: string;
  filename: string | null;
}

export interface TournamentRule {
  id: number;
  tournament_id: number;
  title: string;
  content: string;
  original_pdf_url?: string | null;
  original_pdf_filename?: string | null;
  /** Hasta 3 PDFs propios; original_pdf_* espeja el primero. */
  attachments?: RuleAttachment[];
  version: number;
  status: RuleStatus | string;
  published_at: string | null;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  acceptance_count: number;
  acceptance_summary?: RuleAcceptanceSummary;
}

export interface RuleCreateInput {
  title?: string | null;
  content?: string | null;
}

export interface RuleUpdateInput {
  title?: string | null;
  content?: string | null;
}

function rulePdfFormData(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}

export interface RulesListResponse {
  ok: boolean;
  data: { rules: TournamentRule[] };
}

export interface RuleResponse {
  ok: boolean;
  data: { rule: TournamentRule };
}

export interface RuleCurrentResponse {
  ok: boolean;
  data: {
    draft: TournamentRule | null;
    published: TournamentRule | null;
  };
}

export interface RuleAcceptanceEntry {
  id: number;
  tournament_rule_id: number;
  tournament_id: number;
  player_profile_id: number;
  player_display_name: string | null;
  participant_id: number | null;
  accepted_by_user_id: number | null;
  accepted_at: string | null;
  acceptance_source: string;
}

export interface RuleAcceptancesResponse {
  ok: boolean;
  data: {
    rule: TournamentRule;
    summary: RuleAcceptanceSummary;
    acceptances: RuleAcceptanceEntry[];
  };
}

export interface PlayerRulePayload {
  tournament_id: number;
  tournament?: Partial<Tournament> | null;
  format_config?: TournamentFormatConfig | null;
  rule: {
    id: number;
    tournament_id: number;
    title: string;
    content: string;
    version: number;
    status: RuleStatus | string;
    published_at: string | null;
    original_pdf_url?: string | null;
    original_pdf_filename?: string | null;
    attachments?: RuleAttachment[];
  } | null;
  accepted: boolean;
  accepted_at: string | null;
  participant_id: number | null;
}

export interface PlayerRuleResponse {
  ok: boolean;
  data: PlayerRulePayload;
}

export interface PlayerRuleAcceptanceResponse {
  ok: boolean;
  data: { acceptance: RuleAcceptanceEntry };
}

export interface DashboardTournamentBreakdown {
  total: number;
  active: number;
  draft: number;
  archived: number;
  /** Cerrados (terminados, solo lectura). */
  closed?: number;
}

/** Celular con el que se coordina el reto (uno por integrante con celular). */
export interface DashboardMatchContact {
  player_profile_id: number;
  display_name: string | null;
  phone: string;
  /** Eduardo 2026-10-04: el jugador es menor y no tiene celular propio; es el
   * de su papá, mamá o tutor. `display_name` sigue siendo el del jugador. */
  is_guardian?: boolean;
}

export interface DashboardMatchParticipantBrief {
  participant_id: number;
  /** null en parejas y equipos (el lado no es un solo perfil). */
  player_profile_id: number | null;
  team_id?: number | null;
  participant_kind?: "player" | "team";
  display_name: string | null;
  /** Reto del jugador: solo en el lado del rival (el primer contacto). */
  phone: string | null;
  /** Reto del jugador: celulares del rival (pareja: los dos; equipo: el
   * primero). Vacío en el propio lado y cuando no se comparte contacto. */
  contacts?: DashboardMatchContact[];
}

export interface DashboardMatchGroupBrief {
  id: number;
  name: string;
}

export interface DashboardMatchBrief {
  id: number;
  tournament_id: number;
  tournament_name: string | null;
  /** Deporte del torneo (mensaje de WhatsApp al rival). */
  tournament_sport?: SportKey | string | null;
  round_label: string | null;
  /** Etiqueta canónica derivada cuando la ronda es de playoffs ("Jornada N" en BD); display-only. */
  round_display_label?: string | null;
  match_number: number | null;
  group: DashboardMatchGroupBrief | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  /** true = su horario aún no se publica (fecha/hora/cancha vienen vacías). */
  schedule_pending?: boolean | null;
  status: string;
  score_text: string | null;
  notes: string | null;
  winner_participant_id: number | null;
  allow_player_result_reporting: boolean;
  /** false = torneo sin calendario de canchas: los jugadores coordinan entre
   * ellos y el reto siempre trae el celular del rival. */
  uses_court_calendar?: boolean;
  /** Fechas de la jornada (solo sin calendario de canchas y si el organizador
   * las puso): «del lunes 6 al domingo 12 de octubre». */
  round_starts_on?: string | null;
  round_ends_on?: string | null;
  /** Formato de la categoría del partido («ladder», «round_robin»,
   * «elimination»): el título del reto del home depende de él. */
  format_type?: string | null;
  /** Vista del jugador: su lado del partido (en dobles, su pareja). */
  viewer_participant_id?: number | null;
  player1: DashboardMatchParticipantBrief | null;
  player2: DashboardMatchParticipantBrief | null;
}

/** Integrante de SU grupo de round robin con sus celulares (Eduardo
 * 2026-10-07: adelantar jornadas). Misma forma de contacto que el reto. */
export interface PlayerGroupContactMember {
  participant_id: number;
  player_profile_id: number | null;
  team_id: number | null;
  participant_kind: "player" | "team";
  display_name: string | null;
  contacts: DashboardMatchContact[];
}

/** Quien escribe el WhatsApp al rival: el perfil PROPIO elegido (nunca la
 * etiqueta de la pareja o del equipo). */
export interface RetoSender {
  first_name?: string | null;
  last_name?: string | null;
}

export interface PlayerGroupContactsPayload {
  group_id: number | null;
  group_name: string | null;
  members: PlayerGroupContactMember[];
  /** Nombre y apellido del perfil propio que juega (WhatsApp al rival). */
  sender?: RetoSender | null;
}

/** Su reto de una jornada (Eduardo 2026-10-07: selector de jornada del
 * inicio del torneo, round robin sin calendario de canchas). */
export interface PlayerJornadaReto {
  round_number: number;
  round_label: string;
  round_starts_on: string | null;
  round_ends_on: string | null;
  /** Descansa: su grupo juega esa jornada y él no. */
  rest: boolean;
  /** Su partido (mismo brief del reto: celular solo del rival). */
  match: DashboardMatchBrief | null;
  /** El partido ya tiene resultado capturado o aprobado. */
  has_result: boolean;
  /** Su reporte pendiente (marcador guardado desde el jugador 1). */
  my_report: {
    id: number;
    status: string;
    score_text: string | null;
    winner_participant_id: number | null;
  } | null;
  /** El otro lado ya mandó su reporte (sin su marcador). */
  rival_reported: boolean;
}

export interface PlayerJornadaRetosPayload {
  participant_id: number | null;
  jornadas: PlayerJornadaReto[];
  /** Nombre y apellido del perfil propio que juega (WhatsApp al rival). */
  sender?: RetoSender | null;
}

export interface DashboardCommunicationBrief {
  id: number;
  tournament_id: number;
  tournament_name: string | null;
  title: string;
  communication_type: string;
  audience_type: string;
  status: string;
  published_at: string | null;
  /** Solo en el home del jugador: el aviso del sorteo lleva a sus resultados. */
  draw_results_url?: string | null;
}

export interface DashboardAlert {
  code: string;
  severity: "info" | "warning" | "error" | string;
  message: string;
}

export interface TournamentProgressStep {
  key: string;
  label: string;
  done: boolean;
  missing: string | null;
}

/** Checklist de avance del torneo (QA Cowork #2): "X de Y pasos, falta: …".
 * steps_total es dinámico (el paso Grupos solo existe con round robin) y el
 * backend lo manda best-effort — null si no se pudo calcular. */
export interface TournamentProgress {
  steps_total: number;
  steps_done: number;
  missing: string[];
  steps: TournamentProgressStep[];
}

export interface AdminDashboardTournamentRow extends Tournament {
  players_count: number;
  next_match_date: string | null;
  pending_results_count: number;
  progress?: TournamentProgress | null;
  /** Mismos conteos que /admin/tournaments (#475). */
  registered?: number;
  matches_total?: number;
  matches_played?: number;
  pending_reports?: number;
}

export interface AdminDashboardPayload {
  viewer: { user_id: number; is_superadmin: boolean; is_admin: boolean };
  tournaments: DashboardTournamentBreakdown;
  players: { total_active: number };
  matches: { today: number; upcoming: number; pending_results: number };
  communications: {
    draft: number;
    published: number;
    outbox_pending: number;
  };
  rules: { draft_count: number; pending_acceptances: number };
  alerts: DashboardAlert[];
  result_reports?: { pending: number; by_tournament: { tournament_id: number; pending: number }[] };
  payments?: { receipts_to_review: number; mercadopago_connected: boolean };
  recent_tournaments: AdminDashboardTournamentRow[];
  upcoming_matches: DashboardMatchBrief[];
  recent_results: DashboardMatchBrief[];
  recent_communications: DashboardCommunicationBrief[];
}

export interface AdminDashboardResponse {
  ok: boolean;
  data: AdminDashboardPayload;
}

export interface SuperadminDashboardTournamentRow extends Tournament {
  players_count: number;
}

export interface SuperadminDashboardPayload {
  viewer: { user_id: number; is_superadmin: boolean };
  admins: { total: number; tournament_assignments: number };
  tournaments: DashboardTournamentBreakdown;
  players: { total_active: number };
  communications: { outbox_pending: number };
  recent_tournaments: SuperadminDashboardTournamentRow[];
  service: { ok: boolean; checked_at: string };
}

export interface SuperadminDashboardResponse {
  ok: boolean;
  data: SuperadminDashboardPayload;
}

export interface PlayerDashboardRuleAcceptance {
  rule_id: number | null;
  rule_version: number | null;
  rule_published: boolean;
  accepted: boolean;
}

export interface PlayerDashboardTournamentCard {
  tournament_id: number;
  tournament_name: string | null;
  tournament_status: string | null;
  /** Torneo cerrado: su resultado final (campeón, finalista, lugar…). */
  final_result?: { position: number | null; label: string } | null;
  tournament_type: string | null;
  /** Link público del torneo (para "Inscribir familiar" con la sesión familiar). */
  tournament_slug?: string | null;
  tournament_is_public?: boolean;
  /** Fecha de inicio del torneo (AAAA-MM-DD): año y orden en «Mis torneos». */
  tournament_start_date?: string | null;
  participant_id: number;
  participant_status: string;
  /** «Este torneo usa credencial» (Eduardo 2026-10-05). Sin el dato = sí. */
  credential_enabled?: boolean;
  credential_url?: string | null;
  tournament_rank: number | null;
  initial_rank: number | null;
  current_rank: number | null;
  rank_delta: number | null;
  rank_movement: RankMovement | null;
  draw_ceremony?: DrawCeremony | null;
  /** Candado del jugador de este torneo: sin terminar el wizard no trae
   * próximo partido (Eduardo 2026-09-24). */
  player_visibility?: PlayerStageVisibility;
  /** Grupos de WhatsApp del torneo (Eduardo 2026-10-01): solo llegan al
   * inscrito y solo ligas https://chat.whatsapp.com/…; vacío sin grupos o
   * con el pago pendiente. */
  whatsapp_groups?: TournamentWhatsappGroup[];
  /** Hay grupo, pero se comparte al confirmar el pago de la inscripción. */
  whatsapp_groups_after_payment?: boolean;
  next_match: DashboardMatchBrief | null;
  /** Eduardo 2026-10-05: la jornada actual en la que descansa (grupo impar).
   * `next_match` sigue siendo su siguiente reto. */
  rest_round?: PlayerDashboardRestRound | null;
  rule_acceptance: PlayerDashboardRuleAcceptance;
}

export interface PlayerDashboardRestRound {
  round_label: string;
  round_number: number;
  /** Solo sin calendario de canchas y si el organizador puso las fechas. */
  round_starts_on?: string | null;
  round_ends_on?: string | null;
}

export interface PlayerDashboardProfile {
  id: number;
  display_name: string;
  first_name: string;
  last_name: string | null;
  maternal_last_name: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  avatar_url?: string | null;
  show_registration_photo?: boolean;
  wtn_tennis_id?: string | null;
  wtn_rating?: number | null;
  wtn_rating_doubles?: number | null;
  official_rankings?: PlayerOfficialRanking[];
  primary_category: { id: number; name: string; code: string } | null;
}

export interface PlayerFamilyProfileOption {
  id: number;
  display_name: string;
  first_name: string;
  last_name: string | null;
  maternal_last_name: string | null;
}

/** Perfil de la cuenta familiar + torneos donde tiene inscripción activa
 * (GET /player/family-profiles, Eduardo 2026-09-29). */
export interface PlayerFamilyProfileSummary extends PlayerFamilyProfileOption {
  tournament_ids: number[];
}

export interface PlayerFamilyProfilesResponse {
  ok: boolean;
  data: { profiles: PlayerFamilyProfileSummary[] };
}

export interface PlayerDashboardPayload {
  viewer: { user_id: number; linked_player: boolean };
  profile: PlayerDashboardProfile | null;
  family_profiles: PlayerFamilyProfileOption[];
  tournaments: PlayerDashboardTournamentCard[];
  upcoming_matches: DashboardMatchBrief[];
  recent_results: DashboardMatchBrief[];
  recent_communications: DashboardCommunicationBrief[];
  summary: {
    tournaments_active: number;
    next_match_date: string | null;
    pending_rule_acceptances: number;
    recent_communications_count: number;
  };
}

export interface PlayerDashboardResponse {
  ok: boolean;
  data: PlayerDashboardPayload;
}

export interface PlayerPaymentOrder {
  order_id: number;
  public_token: string | null;
  tournament_id: number;
  tournament_name: string | null;
  player_profile_id: number | null;
  player_name: string | null;
  participant_status: string | null;
  currency: string;
  gross_amount: string;
  payment_url: string;
  created_at: string | null;
}

/** Documento de la inscripción que el torneo le pide al jugador (foto, CURP).
 * A propósito NO trae la liga del archivo: solo su estado. */
export interface PlayerRegistrationDocument {
  key: string;
  label: string;
  /** Sin revisión por documento: lo que se sube queda «recibido». */
  status: "pending" | "received";
  kind: "image" | "pdf";
  /** Valor para `accept` del selector de archivo. */
  accept: string;
  /** Formatos en palabras: "JPG, PNG o WebP". */
  formats_label: string;
  /** Texto del servidor cuando el archivo no es del tipo pedido. */
  type_error?: string | null;
  /** Ayuda del documento: para qué es o cómo conseguirlo. */
  hint?: string | null;
  /** Liga de apoyo (p. ej. dónde descargar la CURP). */
  help_link?: { label: string; url: string } | null;
}

export interface PlayerRegistrationDocumentsPayload {
  tournament_id: number;
  tournament_name: string | null;
  /** Perfil por el que responde el servidor (el hijo elegido, o el primero
   * de la cuenta inscrito en el torneo si no se mandó ninguno). */
  player_profile_id: number | null;
  player_name: string | null;
  can_upload: boolean;
  blocked_reason: "tournament_closed" | "not_registered" | null;
  max_bytes: number;
  max_size_label: string;
  pending_count: number;
  documents: PlayerRegistrationDocument[];
  /** Solo en la respuesta de una subida: lo que se acaba de recibir. En la
   * CURP trae además el resultado de compararla (solo el estado: nunca el
   * nombre ni la fecha que dice el archivo). */
  uploaded?: { key: string; label: string; curp_check?: CurpCheckClassification | null };
}

/** Documentos pendientes vistos desde el link público de la orden (sin
 * sesión): lo mismo que la tarjeta del jugador, sin ids de torneo ni de
 * perfil; `player_name` es solo el nombre de pila. */
export type PublicOrderDocumentsPayload = Omit<
  PlayerRegistrationDocumentsPayload,
  "tournament_id" | "player_profile_id"
>;

/** Un renglón del aviso «Te faltan documentos en …» del home del jugador. */
export interface PlayerPendingDocumentsItem {
  player_profile_id: number;
  player_name: string | null;
  tournament_id: number;
  tournament_name: string | null;
  pending: Array<{ key: string; label: string }>;
  /** El organizador ya mandó «Enviar documentos pendientes» a este jugador en
   * este torneo: solo entonces se avisa en el home (la tarjeta del torneo se
   * muestra siempre). Servidores viejos no lo mandan. */
  organizer_reminded?: boolean;
}

export interface PlayerProfileStats {
  matches_played: number;
  wins: number;
  losses: number;
  draws: number;
  sets_for: number;
  sets_against: number;
  games_for: number;
  games_against: number;
  differential: number;
  win_rate: number | null;
  current_win_streak?: number;
}

export interface PlayerProfileSummary {
  tournaments_total: number;
  tournaments_active: number;
  tournaments_history: number;
}

export interface PlayerOfficialRanking {
  ranking_system_id: number;
  system_name: string;
  age_category_id: number;
  category_name: string;
  position: number;
  points_total: number;
}

export interface PlayerProfileCard {
  id: number;
  user_id: number | null;
  wtn_tennis_id?: string | null;
  wtn_rating?: number | null;
  wtn_rating_doubles?: number | null;
  official_rankings?: PlayerOfficialRanking[];
  first_name: string;
  last_name: string | null;
  maternal_last_name: string | null;
  display_name: string;
  /** Foto de perfil subida por el jugador; null = iniciales. */
  avatar_url?: string | null;
  show_registration_photo?: boolean;
  phone: string | null;
  email: string | null;
  username: string | null;
  gender: string | null;
  birthdate: string | null;
  dominant_hand: string | null;
  city: string | null;
  active_status: string;
  primary_category: PlayerCategory | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PlayerProfileLinkedUser {
  id: number;
  // null si la cuenta también la usan jugadores que este admin no
  // administra (has_foreign_profiles): no se muestran sus datos.
  full_name: string | null;
  email: string | null;
  username: string | null;
  phone: string | null;
  status: string;
  last_login_at: string | null;
  has_foreign_profiles?: boolean;
}

export type PlayerProfileMatchResult =
  | "win"
  | "loss"
  | "draw"
  | "pending"
  | "double_walkover"
  | "unknown";

export interface PlayerProfileMatchParticipant {
  participant_id: number;
  player_profile_id: number;
  display_name: string | null;
}

export interface PlayerProfileMatch {
  id: number;
  tournament_id: number;
  tournament_name: string | null;
  group_id: number | null;
  division_id: number | null;
  division_name: string | null;
  group_name: string | null;
  round_label: string | null;
  /** Etiqueta canónica derivada cuando la ronda es de playoffs ("Jornada N" en BD); display-only. */
  round_display_label?: string | null;
  match_number: number | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  /** true = su horario aún no se publica (fecha/hora/cancha vienen vacías). */
  schedule_pending?: boolean | null;
  status: string;
  match_type: "normal" | "playoff" | "final" | string;
  score_text: string | null;
  winner_participant_id: number | null;
  result: PlayerProfileMatchResult;
  is_player1: boolean | null;
  opponent: PlayerProfileMatchParticipant | null;
  player1: PlayerProfileMatchParticipant | null;
  player2: PlayerProfileMatchParticipant | null;
}

export interface PlayerProfileTournamentRow {
  tournament: Tournament | null;
  participant: {
    id: number;
    current_status: string;
    tournament_rank: number | null;
    initial_rank: number | null;
    current_rank: number | null;
    initial_group_rank?: number | null;
    group_position?: number | null;
    rank_delta: number | null;
    rank_movement: RankMovement | null;
    registration_source: string | null;
    joined_at: string | null;
    dropped_at: string | null;
  };
  group: { id: number; name: string } | null;
  current_category: PlayerCategory | null;
  stats: PlayerProfileStats;
  standings_position: number | null;
  /** Torneo cerrado: su resultado final (campeón, finalista, lugar…). */
  final_result?: { position: number | null; label: string } | null;
  /** Vistas del jugador: el organizador sigue armando el torneo (candado
   * del jugador), así que no trae grupo, lugares ni estadísticas. */
  stage_locked?: boolean;
}

export interface PlayerProfileCommunication {
  id: number;
  tournament_id: number;
  tournament_name: string | null;
  title: string;
  communication_type: string;
  audience_type: string;
  status: string;
  published_at: string | null;
}

export interface PlayerProfileAchievement {
  code: string;
  title: string;
  description: string;
  icon: string;
  tone: "gold" | "silver" | "bronze" | "green" | "blue" | string;
  earned: boolean;
  sort_order: number;
}

/** De quién es la cuenta de un jugador (solo lo recibe soporte). */
export interface PlayerAccountOwnership {
  owners: Array<{ user_id: number; full_name: string | null; created_the_player: boolean }>;
  entry_tournament: { id: number; name: string } | null;
}

export interface PlayerProfilePayload {
  profile: PlayerProfileCard;
  // Solo en la ficha del admin: false = no dio de alta a este jugador y no
  // puede tocar su cuenta (ver playerAccountOwnership.ts).
  can_manage_account?: boolean;
  // ¿Es de alguna organización? false = solo lo atiende soporte.
  account_has_owner?: boolean;
  // ¿Ya tiene con qué entrar (cuenta ligada o acceso propio)?
  has_account?: boolean;
  // Solo para el superadministrador: de quién es la cuenta y por qué torneo entró.
  account_ownership?: PlayerAccountOwnership;
  linked_user: PlayerProfileLinkedUser | null;
  summary: PlayerProfileSummary;
  stats: PlayerProfileStats;
  achievements: PlayerProfileAchievement[];
  active_tournaments: PlayerProfileTournamentRow[];
  tournament_history: PlayerProfileTournamentRow[];
  recent_matches: PlayerProfileMatch[];
  recent_communications: PlayerProfileCommunication[];
}

export interface PlayerProfilePayloadResponse {
  ok: boolean;
  data: PlayerProfilePayload;
}

export interface PlayerHistoricalResultsPayload {
  profile: PlayerProfileCard;
  results: PlayerProfileMatch[];
  filters: {
    tournaments: Array<{ id: number; name: string }>;
    years: number[];
    months: number[];
    match_types: string[];
  };
}

export interface PlayerHistoricalResultsResponse {
  ok: boolean;
  data: PlayerHistoricalResultsPayload;
}

// ----- H2H (Head-to-Head) ---------------------------------------------------

export interface H2HPlayerBrief {
  player_profile_id: number;
  display_name: string;
}

export interface H2HMatchEntry {
  id: number;
  tournament_id: number | null;
  tournament_name: string | null;
  group_name: string | null;
  round_label: string | null;
  match_number: number | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  status: string;
  score_text: string | null;
  result: "win" | "loss" | "unknown";
  match_source: "tournament" | "friendly";
  opponent: H2HPlayerBrief;
}

export interface H2HSummary {
  total_matches: number;
  player_wins: number;
  opponent_wins: number;
  tournament_wins: number;
  tournament_losses: number;
  friendly_wins: number;
  friendly_losses: number;
  last_result: "win" | "loss" | null;
  last_match_date: string | null;
  last_score: string | null;
}

export interface H2HPayload {
  player: H2HPlayerBrief;
  opponent: H2HPlayerBrief;
  summary: H2HSummary;
  matches: H2HMatchEntry[];
}

export interface H2HResponse {
  ok: boolean;
  data: H2HPayload;
}

// ----- Friends & Friendly Matches -------------------------------------------

export interface FriendPlayerBrief {
  player_profile_id: number;
  display_name: string;
  unique_player_id: string | null;
  city?: string | null;
  club_name?: string | null;
  primary_category?: string | null;
}

export interface FriendH2HSummary {
  wins: number;
  losses: number;
  draws: number;
  last_score: string | null;
  last_date: string | null;
}

export interface FriendConnectionOut {
  id: number;
  player_profile_id: number;
  friend_profile_id: number;
  status: "pending" | "accepted" | "rejected";
  created_at: string;
  friend: FriendPlayerBrief;
  h2h_summary: FriendH2HSummary;
  friendly_matches_count: number;
}

export interface FriendListResponse {
  friends: FriendConnectionOut[];
  pending_received: FriendConnectionOut[];
  pending_sent: FriendConnectionOut[];
}

export interface FriendSearchResult {
  player_profile_id: number;
  display_name: string;
  unique_player_id: string | null;
  friend_status: "accepted" | "pending_sent" | "pending_received" | null;
}

export interface FriendlyMatchCreate {
  friend_profile_id: number;
  played_at?: string | null;
  notes?: string | null;
}

export interface FriendlyMatchResultUpdate {
  winner_profile_id: number;
  score_p1_sets?: number | null;
  score_p2_sets?: number | null;
  score_text?: string | null;
}

export interface FriendlyMatchOut {
  id: number;
  player1: FriendPlayerBrief;
  player2: FriendPlayerBrief;
  winner_profile_id: number | null;
  score_p1_sets: number | null;
  score_p2_sets: number | null;
  score_text: string | null;
  played_at: string | null;
  notes: string | null;
  created_by: number;
  created_at: string;
  result: "win" | "loss" | "unknown";
}

export const RESULT_REPORT_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "superseded",
] as const;

export type ResultReportStatus = (typeof RESULT_REPORT_STATUSES)[number];

export interface ResultReportParticipantBrief {
  id: number;
  player_profile_id: number;
  display_name: string | null;
  current_status: string;
}

export interface ResultReportUserBrief {
  id: number;
  full_name: string;
  email: string;
  username: string | null;
}

export interface ResultReportMatchSummary {
  id: number;
  round_label: string | null;
  /** Etiqueta canónica derivada cuando la ronda es de playoffs ("Jornada N" en BD); display-only. */
  round_display_label?: string | null;
  match_number: number | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  /** true = su horario aún no se publica (fecha/hora/cancha vienen vacías). */
  schedule_pending?: boolean | null;
  status: string;
  score_text: string | null;
  winner_participant_id: number | null;
  group_id: number | null;
  group_name: string | null;
  player1: ResultReportParticipantBrief | null;
  player2: ResultReportParticipantBrief | null;
}

export interface MatchResultReport {
  id: number;
  tournament_id: number;
  tournament_name: string | null;
  match_id: number;
  match: ResultReportMatchSummary | null;
  reported_by_user_id: number | null;
  reported_by_user: ResultReportUserBrief | null;
  reported_by_participant_id: number | null;
  reported_by_participant: ResultReportParticipantBrief | null;
  score_text: string | null;
  winner_participant_id: number | null;
  winner: ResultReportParticipantBrief | null;
  notes: string | null;
  status: ResultReportStatus | string;
  admin_notes: string | null;
  reviewed_by_user_id: number | null;
  reviewed_by_user: ResultReportUserBrief | null;
  reviewed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AdminResultReportsListResponse {
  ok: boolean;
  data: { reports: MatchResultReport[] };
}

export interface ResultReportResponse {
  ok: boolean;
  data: { report: MatchResultReport };
}

export interface ResultReportApprovalResponse {
  ok: boolean;
  data: { report: MatchResultReport; match: TournamentMatch | null };
}

export interface PlayerReportableSettings {
  allow_player_result_reporting: boolean;
  result_reporting_mode: string;
  allow_post_publish_changes: boolean;
  uses_court_calendar: boolean;
  match_sets_format?: LadderSetFormat | string | null;
  sport?: SportKey | string;
  /** «Aprobar solo cuando los dos reportan lo mismo» (Eduardo 2026-10-06). */
  auto_approve_matching_reports?: boolean;
}

/** Tabla por partido del admin (Eduardo 2026-10-06). */
export type ReportRowState = "auto_approved" | "approved" | "matching" | "mismatch" | "one_report" | "no_report";

export interface ReportRowReport {
  id: number;
  status: ResultReportStatus | string;
  score_text: string | null;
  winner_participant_id: number | null;
  reported_by_participant_id: number | null;
  reported_by_name: string | null;
  /** Quien lo mandó (el árbitro, en su reporte). */
  reported_by_user_name?: string | null;
  notes: string | null;
  admin_notes: string | null;
  auto_approved: boolean;
  /** Quedó resuelto porque el admin capturó o cerró el partido (2026-10-19). */
  resolved_by_official_result?: boolean;
  created_at: string | null;
  updated_at: string | null;
  reviewed_at: string | null;
}

export interface ReportRowMatch {
  id: number;
  division_id: number | null;
  division_name: string | null;
  group_id: number | null;
  group_name: string | null;
  round_label: string | null;
  round_display_label: string | null;
  round_number: number | null;
  match_number: number | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  status: string;
  score_text: string | null;
  winner_participant_id: number | null;
  player1_participant_id: number;
  player2_participant_id: number;
  player1: ResultReportParticipantBrief | null;
  player2: ResultReportParticipantBrief | null;
}

export interface ReportRow {
  match: ReportRowMatch;
  player1_report: ReportRowReport | null;
  player2_report: ReportRowReport | null;
  /** Lo que mandó el árbitro asignado, si está pendiente (2026-10-16). */
  referee_report?: ReportRowReport | null;
  state: ReportRowState;
}

// ----- Árbitros (Eduardo 2026-10-16) -----------------------------------------

export interface RefereeMatchRow {
  id: number;
  tournament_id: number;
  scheduled_date: string | null;
  scheduled_time: string | null;
  court: string | null;
  venue_name: string | null;
  division_id: number | null;
  division_name: string | null;
  division_code: string | null;
  division_gender: string | null;
  group_name: string | null;
  round_label: string | null;
  match_number: number | null;
  player1_participant_id: number | null;
  player2_participant_id: number | null;
  player1_name: string;
  player2_name: string;
  status: string;
  score_text: string | null;
  winner_participant_id: number | null;
  referee_user_id: number | null;
  referee_name: string | null;
}

export interface TournamentReferee {
  user_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  status: string;
  invited_pending: boolean;
  matches_on_day: number;
}

export interface RefereesOverview {
  tournament: { id: number; name: string };
  referees: TournamentReferee[];
  settings: { referee_publishes_directly: boolean; chief_referees: Record<string, string>; referees_enabled?: boolean };
  days: Array<{ date: string; matches: number; unassigned: number }>;
  day: string | null;
  matches: RefereeMatchRow[];
  divisions: Array<{ id: number; name: string; code: string | null; gender: string | null }>;
  pending_referee_reports: number;
}

export interface RefereeNotifySummary {
  referees: number;
  app: number;
  sent: number;
  held: number;
  failed: number;
  email_sent: number;
}

export interface MyRefereeMatch extends RefereeMatchRow {
  tournament_name: string;
  pickleball: boolean;
  referee_publishes_directly: boolean;
  has_result: boolean;
  my_report: { id: number; status: string; score_text: string | null } | null;
}

export interface MyRefereeMatches {
  day: string | null;
  days: Array<{ date: string; matches: number }>;
  matches: MyRefereeMatch[];
  tournaments: Array<{ id: number; name: string; venue_name: string | null }>;
}

export type RefereeException = "walkover" | "retired" | "defaulted";

export interface ReportsByMatchPayload {
  tournament: { id: number; name: string };
  settings: PlayerReportableSettings;
  divisions: Array<{ id: number; name: string; format_type: string; is_active: boolean }>;
  rows: ReportRow[];
  saved_windows: Array<{ division_id: number | null; round_number: number; label: string; starts_on: string; ends_on: string }>;
  rest_by_round: Array<{
    division_id: number | null;
    division_name: string | null;
    round_number: number;
    round_label: string;
    group_id: number;
    group_name: string;
    participants: Array<{ id: number; name: string }>;
  }>;
}

export interface PlayerReportableMatch {
  match: ResultReportMatchSummary & {
    tournament_id: number;
    tournament_name: string | null;
  };
  own_participant_id: number | null;
  settings: PlayerReportableSettings;
  can_report: boolean;
  my_pending_report: MatchResultReport | null;
}

export interface PlayerResultReportsPayload {
  matches: PlayerReportableMatch[];
  reports: MatchResultReport[];
}

export interface PlayerResultReportsResponse {
  ok: boolean;
  data: PlayerResultReportsPayload;
}

export interface PlayerResultReportInput {
  score_text?: string | null;
  winner_participant_id?: number | null;
  notes?: string | null;
}

export interface AdminApproveResultReportInput {
  score_text?: string | null;
  winner_participant_id?: number | null;
  admin_notes?: string | null;
}

export interface AdminRejectResultReportInput {
  admin_notes?: string | null;
}

export const NOTIFICATION_TYPES = [
  "communication",
  "result_reported",
  "result_approved",
  "result_rejected",
  "rules_published",
  "schedule_changed",
  "group_changed",
  "system",
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface AppNotification {
  id: number;
  tournament_id: number | null;
  player_profile_id: number | null;
  title: string;
  body: string;
  notification_type: NotificationType | string;
  action_url: string | null;
  metadata: Record<string, unknown> | null;
  read: boolean;
  read_at: string | null;
  created_at: string | null;
}

export interface NotificationsListPayload {
  notifications: AppNotification[];
  unread_count: number;
  limit: number;
}

export interface NotificationsListResponse {
  ok: boolean;
  data: NotificationsListPayload;
}

export interface NotificationResponse {
  ok: boolean;
  data: { notification: AppNotification };
}

export interface NotificationsReadAllResponse {
  ok: boolean;
  data: { updated: number };
}

export interface PushPublicKeyData {
  configured: boolean;
  public_key: string | null;
}

export interface PushPublicKeyResponse {
  ok: boolean;
  data: PushPublicKeyData;
}

export interface PushSubscribePayload {
  endpoint: string;
  p256dh: string;
  auth: string;
  user_agent?: string;
}

export interface NotificationListParams {
  limit?: number;
  only_unread?: boolean;
  player_profile_id?: number | null;
}

export type RoundStatus = "draft" | "published" | "closed";

export type MatchupStatus =
  | "draft"
  | "published"
  | "bye"
  | "manual"
  | "cancelled";

export interface RoundParticipantBrief {
  id: number;
  player_profile_id: number;
  display_name: string | null;
  current_status: string;
  initial_rank: number | null;
  current_rank: number | null;
}

export interface TournamentRoundMatchup {
  id: number;
  round_id: number;
  tournament_id: number;
  group_id: number | null;
  division_id: number | null;
  division_name: string | null;
  group_name: string | null;
  player1_participant_id: number | null;
  player2_participant_id: number | null;
  challenger_participant_id: number | null;
  challenged_participant_id: number | null;
  player1: RoundParticipantBrief | null;
  player2: RoundParticipantBrief | null;
  pattern_type: string | null;
  position_label: string | null;
  player1_position: number | null;
  player2_position: number | null;
  player1_group_rank: number | null;
  player2_group_rank: number | null;
  challenger_group_rank: number | null;
  challenged_group_rank: number | null;
  sort_order: number;
  status: MatchupStatus | string;
  match_id: number | null;
  notes: string | null;
  credential_template_url?: string | null;
  credential_title?: string | null;
  credential_subtitle?: string | null;
  credential_club?: string | null;
  credential_footer?: string | null;
  credential_accent_color?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TournamentRound {
  id: number;
  tournament_id: number;
  division_id: number | null;
  division_name: string | null;
  round_number: number;
  name: string;
  starts_on: string | null;
  ends_on: string | null;
  status: RoundStatus | string;
  pattern_type: string | null;
  pattern_offset: number | null;
  config_snapshot_json: Record<string, unknown> | null;
  notes: string | null;
  credential_template_url?: string | null;
  credential_title?: string | null;
  credential_subtitle?: string | null;
  credential_club?: string | null;
  credential_footer?: string | null;
  credential_accent_color?: string | null;
  created_at: string | null;
  updated_at: string | null;
  published_at: string | null;
  matchups: TournamentRoundMatchup[];
}

export interface RoundsGenerateSummary {
  created: number[];
  regenerated: number[];
  skipped_published: number[];
  skipped_existing: number[];
}

export interface RoundsGenerateInput {
  round_number?: number | null;
  regenerate_drafts?: boolean;
  division_id?: number | null;
}

export interface RoundsListResponse {
  ok: boolean;
  data: { rounds: TournamentRound[] };
}

/** Fechas de cada jornada de una categoría (Calendario sin canchas). */
export interface JornadaDatesRow {
  round_number: number;
  label: string;
  round_id: number | null;
  starts_on: string | null;
  ends_on: string | null;
  /** Sugerencia del formato (Inicio + «Días entre Jornadas»); no guardada. */
  suggested_starts_on: string | null;
  suggested_ends_on: string | null;
  matches_count: number;
  /** false = jornada de escalera aún no armada (se crea en su sección). */
  can_save: boolean;
}

export interface JornadaDatesSummary {
  changed: {
    division_id: number | null;
    division_name: string | null;
    round_number: number;
    /** «Jornada 3», «Semana 3» o «Playoff 1». */
    label?: string;
    starts_on: string;
    ends_on: string;
  }[];
  divisions_changed: number;
  notified_players: number;
  notices_open: boolean;
}

export interface JornadaDatesPayload {
  division_id: number | null;
  division_name: string | null;
  needs_division: boolean;
  uses_court_calendar?: boolean;
  /** Torneo publicado y visible: guardar avisa a los jugadores. */
  notices_open: boolean;
  can_edit: boolean;
  other_divisions_count: number;
  jornadas: JornadaDatesRow[];
  summary?: JornadaDatesSummary;
}

export interface JornadaDatesResponse {
  ok: boolean;
  data: JornadaDatesPayload;
}

export interface JornadaDatesUpdateInput {
  division_id: number | null;
  round_number: number;
  starts_on: string;
  ends_on: string;
  shift_following?: boolean;
}

export interface RoundsGenerateResponse {
  ok: boolean;
  data: { summary: RoundsGenerateSummary; rounds: TournamentRound[] };
}

export interface LadderPlayoffsGenerateSummary {
  created: number;
  skipped: number;
  playoff_players_count: number;
  playoff_round_count: number;
  round_numbers: number[];
  round_ids: number[];
  qualified_participant_ids: number[];
}

export interface LadderPlayoffsGenerateResponse {
  ok: boolean;
  data: { summary: LadderPlayoffsGenerateSummary; rounds: TournamentRound[] };
}

export interface RoundResponse {
  ok: boolean;
  data: { round: TournamentRound };
}

export interface RoundUpdateInput {
  name?: string | null;
  notes?: string | null;
}

export interface MatchupUpdateInput {
  player1_participant_id?: number | null;
  player2_participant_id?: number | null;
  challenger_participant_id?: number | null;
  challenged_participant_id?: number | null;
  group_id?: number | null;
  position_label?: string | null;
  notes?: string | null;
  status?: MatchupStatus | string;
  clear_player1?: boolean;
  clear_player2?: boolean;
}

export interface MatchupResponse {
  ok: boolean;
  data: { matchup: TournamentRoundMatchup };
}

export interface MatchupBulkUpdateItem {
  matchup_id: number;
  player1_participant_id?: number | null;
  player2_participant_id?: number | null;
  clear_player1?: boolean;
  clear_player2?: boolean;
}

export interface MatchupBulkUpdateInput {
  matchups: MatchupBulkUpdateItem[];
}

export interface MatchupBulkUpdateResponse {
  ok: boolean;
  data: {
    round: TournamentRound;
    updated_count: number;
    deleted_matchup_ids: number[];
  };
}

export interface MatchupCreateInput {
  group_id: number;
}

export interface MatchupValidationError {
  type: "same_player" | "duplicate" | "empty" | string;
  message?: string;
  matchup_id?: number;
  matchup_ids?: number[];
  participant_id?: number;
  count?: number;
}

export interface RoundPublishResponse {
  ok: boolean;
  data: {
    round: TournamentRound;
    created_match_ids: number[];
    already_published: boolean;
  };
}

export interface RoundBackToDraftResponse {
  ok: boolean;
  data: {
    round: TournamentRound;
    already_draft: boolean;
  };
}

function _notificationsQuery(params: NotificationListParams): string {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.only_unread) qs.set("only_unread", "true");
  if (params.player_profile_id) qs.set("player_profile_id", String(params.player_profile_id));
  const query = qs.toString();
  return query ? `?${query}` : "";
}

function playerProfileQuery(playerProfileId?: number | null): string {
  return playerProfileId ? `?player_profile_id=${encodeURIComponent(String(playerProfileId))}` : "";
}

function appendPlayerProfileQuery(path: string, playerProfileId?: number | null): string {
  if (!playerProfileId) return path;
  return `${path}${path.includes("?") ? "&" : "?"}player_profile_id=${encodeURIComponent(String(playerProfileId))}`;
}

export const api = {
  health: () => apiFetch<{ ok: boolean; service: string; version: string }>("/health"),
  ready: () =>
    apiFetch<{ ok: boolean; checks: { database: { ok: boolean; error: string | null } } }>(
      "/ready",
    ),
  auth: {
    login: (identifier: string, password: string) =>
      apiFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body: { email: identifier, password },
      }),
    me: (token: string) =>
      apiFetch<MeResponse>("/auth/me", { token }),
    /** Renueva el token de una sesión vigente (no aplica a suplantación). */
    refresh: (token: string) =>
      apiFetch<LoginResponse>("/auth/refresh", { method: "POST", token }),
    /** Sesión larga del jugador: canjea el token de renovación por una sesión
     * nueva (acceso + renovación rotada) sin pedir contraseña. Sin Bearer. */
    renew: (refreshToken: string) =>
      apiFetch<LoginResponse>("/auth/session/renew", {
        method: "POST",
        body: { refresh_token: refreshToken },
      }),
    /** Avisa que la persona está usando la app («Último ingreso»; ver
     * lib/visitPing). El servidor escribe a lo más una vez por hora. */
    visit: (token: string) =>
      apiFetch<{ ok: boolean; data: { recorded: boolean } }>("/auth/visit", { method: "POST", token }),
    logout: (token: string | null) =>
      apiFetch<{ ok: boolean }>("/auth/logout", { method: "POST", token }),
    changePassword: (
      token: string,
      payload: { current_password?: string | null; new_password: string },
    ) =>
      apiFetch<ChangePasswordResponse>("/auth/change-password", {
        method: "POST",
        token,
        body: payload,
      }),
    /** «Mi cuenta»: guarda el celular de la sesión (vacío = quitarlo). */
    updateOwnPhone: (token: string, phone: string | null) =>
      apiFetch<{ ok: boolean; data: { user: AuthUser } }>("/auth/me/phone", {
        method: "PATCH",
        token,
        body: { phone },
      }),
    passwordResetRequest: (email: string) =>
      apiFetch<{ ok: boolean; data: { message: string } }>(
        "/auth/password-reset/request",
        { method: "POST", body: { email } },
      ),
    passwordResetConfirm: (resetToken: string, newPassword: string) =>
      apiFetch<LoginResponse>("/auth/password-reset/confirm", {
        method: "POST",
        body: { token: resetToken, new_password: newPassword },
      }),
  },
  superadmin: {
    createAdmin: (
      token: string,
      payload: {
        full_name: string;
        email: string;
        phone?: string | null;
        password: string;
      },
    ) =>
      apiFetch<AdminUserResponse>("/superadmin/admins", {
        method: "POST",
        token,
        body: payload,
      }),
    listAdmins: (token: string) =>
      apiFetch<AdminsListResponse>("/superadmin/admins", { token }),
    resetAdminPassword: (token: string, adminId: number, password: string) =>
      apiFetch<AdminUserResponse>(`/superadmin/admins/${adminId}/password`, {
        method: "PATCH",
        token,
        body: { password },
      }),
    setAdminStatus: (token: string, adminId: number, status: "active" | "disabled") =>
      apiFetch<AdminUserResponse>(`/superadmin/admins/${adminId}/status`, {
        method: "PATCH",
        token,
        body: { status },
      }),
    deleteAdmin: (token: string, adminId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean; admin_id: number } }>(
        `/superadmin/admins/${adminId}`,
        { method: "DELETE", token },
      ),
    impersonateAdmin: (token: string, adminId: number) =>
      apiFetch<AdminImpersonationResponse>(
        `/superadmin/admins/${adminId}/impersonation-token`,
        { method: "POST", token },
      ),
    /** Dar o quitar «Soporte Torneos123» (solo superadmin). */
    setAdminSupport: (token: string, adminId: number, enabled: boolean) =>
      apiFetch<AdminUserResponse>(`/superadmin/admins/${adminId}/support`, {
        method: "PUT",
        token,
        body: { enabled },
      }),
    listAllTournaments: (token: string) =>
      apiFetch<TournamentListResponse>("/superadmin/tournaments", { token }),
    tournamentDeletionPreview: (token: string, tournamentId: number) =>
      apiFetch<SuperadminDeletionPreviewResponse>(
        `/admin/tournaments/${tournamentId}/superadmin-delete-preview`,
        { token },
      ),
    deleteTournamentWithHistory: (
      token: string,
      tournamentId: number,
      confirmName: string,
    ) =>
      apiFetch<SuperadminDeleteTournamentResponse>(
        `/admin/tournaments/${tournamentId}/superadmin-delete`,
        { method: "POST", token, body: { confirm_name: confirmName } },
      ),
    assignTournamentAdmin: (
      token: string,
      tournamentId: number,
      payload: { user_id: number; role_in_tournament?: string | null },
    ) =>
      apiFetch<AssignmentResponse>(
        `/superadmin/tournaments/${tournamentId}/assign-admin`,
        { method: "POST", token, body: payload },
      ),
    /** Cuántos inscritos del torneo (todas sus categorías, bajas incluidas) no
     * son de ninguna organización: lo que «Asignar» tomaría, sin cambiar nada. */
    ownerlessPlayers: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { ownerless_count: number; ownerless_player_ids: number[] } }>(
        `/superadmin/tournaments/${tournamentId}/ownerless-players`,
        { token },
      ),
    /** Los inscritos de un torneo que no son de ninguna organización pasan a
     * ser de sus organizadores (Opción A: la cuenta solo la toca su dueño).
     * Con `playerIds` (los que soporte vio y confirmó) solo toma esos. */
    assignOwnerlessPlayers: (token: string, tournamentId: number, playerIds?: number[]) =>
      apiFetch<{ ok: boolean; data: { assigned_count: number; assigned_player_ids: number[] } }>(
        `/superadmin/tournaments/${tournamentId}/assign-ownerless-players`,
        { method: "POST", token, body: playerIds ? { player_ids: playerIds } : undefined },
      ),
    /** Asigna (o quita, con null) el torneo por el que entró un jugador: sus
     * organizadores pasan a ser los dueños de su cuenta. */
    setPlayerEntryTournament: (token: string, playerProfileId: number, tournamentId: number | null) =>
      apiFetch<{
        ok: boolean;
        data: { player_profile_id: number; account_has_owner: boolean; account_ownership: PlayerAccountOwnership };
      }>(`/superadmin/players/${playerProfileId}/entry-tournament`, {
        method: "PUT",
        token,
        body: { tournament_id: tournamentId },
      }),
    /** Solicitudes de "Pedir demo" (bandeja del superadministrador). */
    demoRequests: {
      list: (token: string, params: DemoRequestListParams = {}) => {
        const qs = new URLSearchParams();
        if (params.status) qs.set("status", params.status);
        if (params.limit !== undefined) qs.set("limit", String(params.limit));
        if (params.offset !== undefined) qs.set("offset", String(params.offset));
        const query = qs.toString();
        return apiFetch<DemoRequestListResponse>(
          `/superadmin/demo-requests${query ? `?${query}` : ""}`,
          { token },
        );
      },
      update: (
        token: string,
        demoRequestId: number,
        payload: { status?: DemoRequestStatus; notes?: string | null },
      ) =>
        apiFetch<DemoRequestUpdateResponse>(`/superadmin/demo-requests/${demoRequestId}`, {
          method: "PATCH",
          token,
          body: payload,
        }),
    },
  },
  /** Formulario público "Pedir demo" (sin sesión). Errores: ver
   * `mapDemoRequestError` en lib/demoRequest.ts. `signal` permite cortar el
   * envío si el servidor no contesta (el formulario lo trata como error de red). */
  publicDemoRequests: {
    create: (payload: DemoRequestPayload, options: { signal?: AbortSignal } = {}) =>
      apiFetch<DemoRequestCreateResponse>("/public/demo-requests", {
        method: "POST",
        body: payload,
        signal: options.signal,
      }),
  },
  tournaments: {
    listMine: (token: string) =>
      apiFetch<TournamentListResponse>("/admin/tournaments", { token }),
    create: (token: string, payload: TournamentCreateInput) =>
      apiFetch<TournamentResponse>("/admin/tournaments", {
        method: "POST",
        token,
        body: payload,
      }),
    get: (token: string, tournamentId: number) =>
      apiFetch<TournamentDetailResponse>(
        `/admin/tournaments/${tournamentId}`,
        { token },
      ),
    update: (
      token: string,
      tournamentId: number,
      payload: TournamentUpdateInput,
    ) =>
      apiFetch<TournamentResponse>(`/admin/tournaments/${tournamentId}`, {
        method: "PATCH",
        token,
        body: payload,
      }),
    updateVenues: (
      token: string,
      tournamentId: number,
      payload: { venues: TournamentVenueInput[] },
    ) =>
      apiFetch<TournamentResponse>(`/admin/tournaments/${tournamentId}/venues`, {
        method: "PUT",
        token,
        body: payload,
      }),
    remove: (token: string, tournamentId: number) =>
      apiFetch<TournamentDeleteResponse>(
        `/admin/tournaments/${tournamentId}`,
        { method: "DELETE", token },
      ),
    uploadLogo: (token: string, tournamentId: number, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<TournamentResponse>(
        `/admin/tournaments/${tournamentId}/logo`,
        formData,
        token,
      );
    },
    uploadOrganizerLogo: (token: string, tournamentId: number, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<TournamentResponse>(
        `/admin/tournaments/${tournamentId}/organizer-logo`,
        formData,
        token,
      );
    },
    uploadCallPdf: (token: string, tournamentId: number, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<TournamentResponse>(`/admin/tournaments/${tournamentId}/call-pdf`, formData, token);
    },
    deleteCallPdf: (token: string, tournamentId: number) =>
      apiFetch<TournamentResponse>(`/admin/tournaments/${tournamentId}/call-pdf`, { method: "DELETE", token }),
    uploadSponsorBanner: (token: string, tournamentId: number, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<TournamentResponse>(
        `/admin/tournaments/${tournamentId}/sponsor-banner`,
        formData,
        token,
      );
    },
    /** Quita (desvincula) póster, logo del organizador o un banner. */
    removeImage: (token: string, tournamentId: number, slot: "logo" | "organizer-logo" | "sponsor-banner" | "player-banner") =>
      apiFetch<TournamentResponse>(`/admin/tournaments/${tournamentId}/images/${slot}`, { method: "DELETE", token }),
    uploadPlayerBanner: (token: string, tournamentId: number, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<TournamentResponse>(
        `/admin/tournaments/${tournamentId}/player-banner`,
        formData,
        token,
      );
    },
    /** Imagen de un patrocinador EXTRA: solo sube el archivo y regresa su
     * URL; queda en el torneo al guardar la lista con saveSponsorBanners. */
    uploadExtraSponsorBanner: (token: string, tournamentId: number, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<{ ok: boolean; data: { image_url: string } }>(
        `/admin/tournaments/${tournamentId}/sponsor-banners/upload`,
        formData,
        token,
      );
    },
    saveSponsorBanners: (token: string, tournamentId: number, payload: SponsorBannersInput) =>
      apiFetch<{ ok: boolean; data: { sponsor_banners: TournamentSponsorBanner[] } }>(
        `/admin/tournaments/${tournamentId}/sponsor-banners`,
        { method: "PUT", token, body: payload },
      ),
    saveWhatsappGroups: (token: string, tournamentId: number, payload: WhatsappGroupsInput) =>
      apiFetch<{ ok: boolean; data: { whatsapp_groups: TournamentWhatsappGroup[] } }>(
        `/admin/tournaments/${tournamentId}/whatsapp-groups`,
        { method: "PUT", token, body: payload },
      ),
    getDrawCeremonyPlan: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { plan: DrawCeremonyPlan } }>(
        `/admin/tournaments/${tournamentId}/draw-ceremony-plan`,
        { token },
      ),
    saveDrawCeremonyPlan: (token: string, tournamentId: number, plan: DrawCeremonyPlan) =>
      apiFetch<{ ok: boolean; data: { plan: DrawCeremonyPlan } }>(
        `/admin/tournaments/${tournamentId}/draw-ceremony-plan`,
        { method: "PUT", token, body: plan },
      ),
    /** Wizard reanudable: el paso que el admin acaba de abrir (1..10).
     * `apiFetch` ya serializa el body: con `JSON.stringify` aquí viajaba un
     * string doble (422 silencioso) y el paso nunca se registraba. */
    reportSetupStep: (token: string, tournamentId: number, step: number) =>
      apiFetch<{ ok: boolean; data: { setup_step_reached: number } }>(
        `/admin/tournaments/${tournamentId}/setup/progress`,
        { method: "POST", token, body: { step } },
      ),
    /** Duplicar: copia en borrador con nombre y fechas nuevos. */
    duplicate: (token: string, tournamentId: number, payload: TournamentDuplicateInput) =>
      apiFetch<TournamentDuplicateResponse>(
        `/admin/tournaments/${tournamentId}/duplicate`,
        { method: "POST", token, body: payload },
      ),
    archive: (token: string, tournamentId: number) =>
      apiFetch<TournamentResponse>(
        `/admin/tournaments/${tournamentId}/archive`,
        { method: "POST", token },
      ),
    /** Cerrar torneo: pendientes, posiciones finales, reapertura y bitácora. */
    lifecycle: (token: string, tournamentId: number) =>
      apiFetch<TournamentLifecycleResponse>(`/admin/tournaments/${tournamentId}/lifecycle`, { token }),
    close: (token: string, tournamentId: number) =>
      apiFetch<TournamentLifecycleResponse>(`/admin/tournaments/${tournamentId}/close`, {
        method: "POST",
        token,
        body: {},
      }),
    reopen: (token: string, tournamentId: number, payload: { reason: string; notify_players: boolean }) =>
      apiFetch<TournamentLifecycleResponse>(`/admin/tournaments/${tournamentId}/reopen`, {
        method: "POST",
        token,
        body: payload,
      }),
    getSettings: (token: string, tournamentId: number) =>
      apiFetch<TournamentSettingsResponse>(
        `/admin/tournaments/${tournamentId}/settings`,
        { token },
      ),
    updateSettings: (
      token: string,
      tournamentId: number,
      payload: TournamentSettingsUpdateInput,
    ) =>
      apiFetch<TournamentSettingsResponse>(
        `/admin/tournaments/${tournamentId}/settings`,
        { method: "PATCH", token, body: payload },
      ),
    getFormatConfig: (token: string, tournamentId: number) =>
      apiFetch<TournamentFormatConfigResponse>(
        `/admin/tournaments/${tournamentId}/format-config`,
        { token },
      ),
    updateFormatConfig: (
      token: string,
      tournamentId: number,
      payload: TournamentFormatConfigUpdateInput,
    ) =>
      apiFetch<TournamentFormatConfigResponse>(
        `/admin/tournaments/${tournamentId}/format-config`,
        { method: "PUT", token, body: payload },
      ),
    listTeam: (token: string, tournamentId: number) =>
      apiFetch<TournamentStaffListResponse>(
        `/admin/tournaments/${tournamentId}/team`,
        { token },
      ),
    addTeamMember: (
      token: string,
      tournamentId: number,
      payload: TournamentStaffAddInput,
    ) =>
      apiFetch<TournamentStaffMemberResponse>(
        `/admin/tournaments/${tournamentId}/team`,
        { method: "POST", token, body: payload },
      ),
    updateTeamMember: (
      token: string,
      tournamentId: number,
      memberUserId: number,
      payload: { profile: string },
    ) =>
      apiFetch<TournamentStaffMemberResponse>(
        `/admin/tournaments/${tournamentId}/team/${memberUserId}`,
        { method: "PATCH", token, body: payload },
      ),
    removeTeamMember: (token: string, tournamentId: number, memberUserId: number) =>
      apiFetch<TournamentStaffRemoveResponse>(
        `/admin/tournaments/${tournamentId}/team/${memberUserId}`,
        { method: "DELETE", token },
      ),
    listTiebreakPresets: (token: string, excludeTournamentId?: number) =>
      apiFetch<TiebreakPresetsResponse>(
        `/admin/tournaments/tiebreak-presets${excludeTournamentId ? `?exclude_tournament_id=${excludeTournamentId}` : ""}`,
        { token },
      ),
    listMySubUsers: (token: string) =>
      apiFetch<MySubUsersResponse>("/admin/tournaments/my-sub-users", { token }),
    listMyTeam: (token: string) =>
      apiFetch<AdminTeamResponse>("/admin/tournaments/my-team", { token }),
    addMyTeamMember: (token: string, payload: AdminTeamAddInput) =>
      apiFetch<AdminTeamMemberResponse>("/admin/tournaments/my-team", { method: "POST", token, body: payload }),
    updateMyTeamMember: (token: string, memberUserId: number, payload: { profile: string }) =>
      apiFetch<AdminTeamMemberResponse>(`/admin/tournaments/my-team/${memberUserId}`, { method: "PATCH", token, body: payload }),
    setMyTeamMemberTournaments: (token: string, memberUserId: number, payload: { scope: AdminTeamScope; tournament_ids: number[] }) =>
      apiFetch<AdminTeamMemberResponse>(`/admin/tournaments/my-team/${memberUserId}/tournaments`, { method: "PUT", token, body: payload }),
    removeMyTeamMember: (token: string, memberUserId: number) =>
      apiFetch<{ ok: boolean; data: { removed: boolean; user_id: number; assignments_removed: number } }>(
        `/admin/tournaments/my-team/${memberUserId}`,
        { method: "DELETE", token },
      ),
    inviteToMyTeam: (
      token: string,
      payload: { email: string; profile: string; scope: AdminTeamScope; tournament_ids: number[] },
    ) =>
      apiFetch<AdminTeamInvitationResponse>("/admin/tournaments/my-team/invitations", { method: "POST", token, body: payload }),
    resendMyTeamInvitation: (token: string, invitationId: string) =>
      apiFetch<AdminTeamInvitationResponse>(
        `/admin/tournaments/my-team/invitations/${encodeURIComponent(invitationId)}/resend`,
        { method: "POST", token },
      ),
    updateMyTeamInvitation: (token: string, invitationId: string, payload: { profile: string }) =>
      apiFetch<AdminTeamInvitationResponse>(
        `/admin/tournaments/my-team/invitations/${encodeURIComponent(invitationId)}`,
        { method: "PATCH", token, body: payload },
      ),
    setMyTeamInvitationTournaments: (token: string, invitationId: string, payload: { scope: AdminTeamScope; tournament_ids: number[] }) =>
      apiFetch<AdminTeamInvitationResponse>(
        `/admin/tournaments/my-team/invitations/${encodeURIComponent(invitationId)}/tournaments`,
        { method: "PUT", token, body: payload },
      ),
    deleteMyTeamInvitation: (token: string, invitationId: string) =>
      apiFetch<{ ok: boolean; data: { removed: boolean; status: string; assignments_removed: number } }>(
        `/admin/tournaments/my-team/invitations/${encodeURIComponent(invitationId)}`,
        { method: "DELETE", token },
      ),
    listReceivedTeamInvitations: (token: string) =>
      apiFetch<{ ok: boolean; data: { invitations: ReceivedTeamInvitation[] } }>(
        "/admin/tournaments/team-invitations/received",
        { token },
      ),
    acceptTeamInvitation: (token: string, invitationId: string) =>
      apiFetch<{ ok: boolean; data: { accepted: boolean; organizer_name: string } }>(
        `/admin/tournaments/team-invitations/${encodeURIComponent(invitationId)}/accept`,
        { method: "POST", token },
      ),
    declineTeamInvitation: (token: string, invitationId: string) =>
      apiFetch<{ ok: boolean; data: { declined: boolean } }>(
        `/admin/tournaments/team-invitations/${encodeURIComponent(invitationId)}/decline`,
        { method: "POST", token },
      ),
    listDivisions: (token: string, tournamentId: number) =>
      apiFetch<TournamentDivisionsResponse>(
        `/admin/tournaments/${tournamentId}/divisions`,
        { token },
      ),
    createDivision: (token: string, tournamentId: number, payload: TournamentDivisionInput) =>
      apiFetch<TournamentDivisionResponse>(
        `/admin/tournaments/${tournamentId}/divisions`,
        { method: "POST", token, body: payload },
      ),
    updateDivision: (
      token: string,
      tournamentId: number,
      divisionId: number,
      payload: Partial<TournamentDivisionInput>,
    ) =>
      apiFetch<TournamentDivisionResponse>(
        `/admin/tournaments/${tournamentId}/divisions/${divisionId}`,
        { method: "PATCH", token, body: payload },
      ),
    /** Guarda Bloque min, #Canchas, Desde y Último inicio de UNA categoría
     * (calendario del torneo, Eduardo 2026-10-01). */
    updateDivisionCalendarSettings: (
      token: string,
      tournamentId: number,
      divisionId: number,
      payload: DivisionCalendarSettingsUpdate,
    ) =>
      apiFetch<DivisionCalendarSettingsResponse>(
        `/admin/tournaments/${tournamentId}/divisions/${divisionId}/calendar-settings`,
        { method: "PUT", token, body: payload },
      ),
  },
  common: {
    playerCategories: (token: string) =>
      apiFetch<PlayerCategoriesResponse>("/common/player-categories", { token }),
  },
  players: {
    search: (
      token: string,
      params: { search?: string; limit?: number; offset?: number; account?: "with" | "without" } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set("search", params.search);
      if (params.limit !== undefined) qs.set("limit", String(params.limit));
      if (params.offset) qs.set("offset", String(params.offset));
      if (params.account) qs.set("account", params.account);
      const query = qs.toString();
      return apiFetch<PlayersListResponse>(
        `/admin/players${query ? `?${query}` : ""}`,
        { token },
      );
    },
    create: (token: string, payload: PlayerCreateInput) =>
      apiFetch<PlayerResponse>("/admin/players", {
        method: "POST",
        token,
        body: payload,
      }),
    update: (token: string, playerId: number, payload: PlayerUpdateInput) =>
      apiFetch<PlayerResponse>(`/admin/players/${playerId}`, {
        method: "PATCH",
        token,
        body: payload,
      }),
    remove: (token: string, playerId: number) =>
      apiFetch<PlayerArchiveResponse>(`/admin/players/${playerId}`, {
        method: "DELETE",
        token,
      }),
    /** `confirmFamilyAccount`: el admin confirmó cambiar la contraseña de la
     * CUENTA FAMILIAR (sin él, un hijo de cuenta familiar responde 409). */
    generatePin: (token: string, playerId: number, options: { confirmFamilyAccount?: boolean } = {}) =>
      apiFetch<PlayerPinResponse>(
        `/admin/players/${playerId}/pin${options.confirmFamilyAccount ? "?confirm_family_account=true" : ""}`,
        {
          method: "POST",
          token,
        },
      ),
    reactivateAccount: (token: string, playerId: number) =>
      apiFetch<PlayerAccountReactivateResponse>(`/admin/players/${playerId}/account/reactivate`, {
        method: "POST",
        token,
      }),
    /** Fusión de duplicados: qué se movería, avisos y bloqueos (no toca nada). */
    mergePreview: (token: string, payload: { survivor_id: number; duplicate_id: number }) =>
      apiFetch<{ ok: boolean; data: PlayerMergePreview }>("/admin/players/merge/preview", {
        method: "POST",
        token,
        body: payload,
      }),
    merge: (token: string, payload: { survivor_id: number; duplicate_id: number }) =>
      apiFetch<{ ok: boolean; data: PlayerMergeResult }>("/admin/players/merge", {
        method: "POST",
        token,
        body: payload,
      }),
    /** Acceso propio de un perfil de cuenta familiar (lo otorga el admin; el tutor conserva el suyo). */
    ownAccessStatus: (token: string, playerId: number) =>
      apiFetch<{ ok: boolean; data: PlayerOwnAccessStatus }>(`/admin/players/${playerId}/own-access`, { token }),
    grantOwnAccess: (token: string, playerId: number, payload: { email?: string | null }) =>
      apiFetch<{ ok: boolean; data: PlayerOwnAccessGrant }>(`/admin/players/${playerId}/own-access`, {
        method: "POST",
        token,
        body: payload,
      }),
    revokeOwnAccess: (token: string, playerId: number) =>
      apiFetch<{ ok: boolean; data: { player_profile_id: number; own_access: null } }>(
        `/admin/players/${playerId}/own-access`,
        { method: "DELETE", token },
      ),
    /** «Pasar a su propia cuenta»: su usuario propio queda como su cuenta principal y la cuenta anterior deja de verlo. */
    moveToOwnAccount: (token: string, playerId: number) =>
      apiFetch<{ ok: boolean; data: PlayerMoveToOwnAccountResult }>(
        `/admin/players/${playerId}/own-access/move-to-own-account`,
        { method: "POST", token },
      ),
    resetPinToPhoneLast4: (token: string, playerId: number) =>
      apiFetch<PlayerPinResponse>(`/admin/players/${playerId}/pin/phone-last4`, {
        method: "POST",
        token,
      }),
    impersonate: (token: string, playerId: number) =>
      apiFetch<PlayerImpersonationResponse>(
        `/admin/players/${playerId}/impersonation-token`,
        {
          method: "POST",
          token,
        },
      ),
    downloadLayoutCsv: (token: string) => {
      const url = `${API_BASE_URL}/admin/players/layout-xlsx`;
      return fetch(url, { headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` } });
    },
    downloadExportCsv: (token: string) => {
      const url = `${API_BASE_URL}/admin/players/export-csv`;
      return fetch(url, { headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` } });
    },
    /** Excel formateado (logo, encabezado, solo columnas con datos + respuestas del formulario). */
    downloadExportXlsx: (token: string) => {
      const url = `${API_BASE_URL}/admin/players/export-xlsx`;
      return fetch(url, { headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` } });
    },
    bulkCreateCsv: (token: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return fetch(`${API_BASE_URL}/admin/players/bulk-create-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` },
        body: form,
      });
    },
    bulkUpdateCsv: (token: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      return fetch(`${API_BASE_URL}/admin/players/bulk-update-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` },
        body: form,
      });
    },
    profile: (token: string, playerId: number) =>
      apiFetch<PlayerProfilePayloadResponse>(
        `/admin/players/${playerId}/profile`,
        { token },
      ),
  },
  participants: {
    updateProfile: (token: string, tournamentId: number, participantId: number, payload: PlayerUpdateInput) =>
      apiFetch<PlayerResponse>(
        `/admin/tournaments/${tournamentId}/players/${participantId}/profile`,
        { method: "PATCH", token, body: payload },
      ),
    list: (
      token: string,
      tournamentId: number,
      params: {
        search?: string;
        status?: string;
        category_id?: number;
        division_id?: number;
      } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set("search", params.search);
      if (params.status) qs.set("status", params.status);
      if (params.category_id !== undefined)
        qs.set("category_id", String(params.category_id));
      if (params.division_id !== undefined)
        qs.set("division_id", String(params.division_id));
      const query = qs.toString();
      return apiFetch<ParticipantsListResponse>(
        `/admin/tournaments/${tournamentId}/players${query ? `?${query}` : ""}`,
        { token },
      );
    },
    enroll: (
      token: string,
      tournamentId: number,
      payload: ParticipantEnrollInput,
    ) =>
      apiFetch<ParticipantResponse>(
        `/admin/tournaments/${tournamentId}/players`,
        { method: "POST", token, body: payload },
      ),
    pairSuggestions: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { suggestions: PairSuggestion[] } }>(
        `/admin/tournaments/${tournamentId}/pair-suggestions`,
        { token },
      ),
    createTeam: (
      token: string,
      tournamentId: number,
      payload: DoublesTeamInput,
    ) =>
      apiFetch<ParticipantResponse>(
        `/admin/tournaments/${tournamentId}/teams`,
        { method: "POST", token, body: payload },
      ),
    updateTeam: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: Partial<DoublesTeamInput>,
    ) =>
      apiFetch<ParticipantResponse>(
        `/admin/tournaments/${tournamentId}/teams/${participantId}`,
        { method: "PATCH", token, body: payload },
      ),
    deleteTeam: (
      token: string,
      tournamentId: number,
      participantId: number,
      pendingMatchesAction?: PendingMatchesAction,
    ) =>
      apiFetch<ParticipantResponse>(
        `/admin/tournaments/${tournamentId}/teams/${participantId}${
          pendingMatchesAction ? `?pending_matches_action=${pendingMatchesAction}` : ""
        }`,
        { method: "DELETE", token },
      ),
    bulkEnroll: (
      token: string,
      tournamentId: number,
      payload: ParticipantsBulkEnrollInput,
    ) =>
      apiFetch<ParticipantsBulkEnrollResponse>(
        `/admin/tournaments/${tournamentId}/players/bulk`,
        { method: "POST", token, body: payload },
      ),
    resolveIdentifiers: (
      token: string,
      tournamentId: number,
      payload: ParticipantIdentifierLookupInput,
    ) =>
      apiFetch<ParticipantIdentifierLookupResponse>(
        `/admin/tournaments/${tournamentId}/players/resolve-identifiers`,
        { method: "POST", token, body: payload },
      ),
    get: (token: string, tournamentId: number, participantId: number) =>
      apiFetch<ParticipantDetailResponse>(
        `/admin/tournaments/${tournamentId}/players/${participantId}`,
        { token },
      ),
    update: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: ParticipantUpdateInput,
    ) =>
      apiFetch<ParticipantResponse>(
        `/admin/tournaments/${tournamentId}/players/${participantId}`,
        { method: "PATCH", token, body: payload },
      ),
    // «Aprobar» de Inscripciones: el participante y las demás categorías
    // pre-registradas que paga la misma orden; participant_ids limita a esas.
    approveRegistration: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: { order_id?: number; participant_ids?: number[] } = {},
    ) =>
      apiFetch<RegistrationApproveResponse>(
        `/admin/tournaments/${tournamentId}/players/${participantId}/approve-registration`,
        { method: "POST", token, body: payload },
      ),
    // Borra el registro completo de un jugador dado de baja (torneo sin
    // calendario ni resultados). El perfil del jugador no se toca.
    deleteRegistration: (token: string, tournamentId: number, participantId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean; participant_id: number; player_profile_id: number | null; orders_deleted: number; pairs_dropped?: number[] } }>(
        `/admin/tournaments/${tournamentId}/players/${participantId}/registration`,
        { method: "DELETE", token },
      ),
    // Auditoría de "Eliminar registro" (Eduardo 2026-09-12): quién borró qué
    // registro y cuándo, con las órdenes que se fueron con él.
    listDeletedRegistrations: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { deleted_registrations: DeletedRegistration[] } }>(
        `/admin/tournaments/${tournamentId}/players/registrations/deleted`,
        { token },
      ),
    // Guardado en bloque del paso 6 (Ranking) con candado de ranks únicos
    // por división: 409 si el estado final repite un rank no-nulo.
    updateSeeds: (
      token: string,
      tournamentId: number,
      payload: ParticipantSeedsUpdateInput,
    ) =>
      apiFetch<ParticipantsListResponse>(
        `/admin/tournaments/${tournamentId}/players/seeds`,
        { method: "PATCH", token, body: payload },
      ),
    drop: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: ParticipantActionInput = {},
    ) =>
      apiFetch<ParticipantDropResponse>(
        `/admin/tournaments/${tournamentId}/players/${participantId}/drop`,
        { method: "POST", token, body: payload },
      ),
    /** Cancela los pendientes de grupo de alguien YA dado de baja con la nota
     * «Baja de <nombre>» (Eduardo 2026-10-05); fuera de grupo, W.O. al rival.
     * Idempotente. */
    cancelPendingMatches: (token: string, tournamentId: number, participantId: number) =>
      apiFetch<{ ok: boolean; data: { cancelled: number; walkovers?: number; kept_in_bracket?: number } }>(
        `/admin/tournaments/${tournamentId}/players/${participantId}/cancel-pending-matches`,
        { method: "POST", token, body: {} },
      ),
    reactivate: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: ParticipantActionInput = {},
    ) =>
      apiFetch<ParticipantResponse>(
        `/admin/tournaments/${tournamentId}/players/${participantId}/reactivate`,
        { method: "POST", token, body: payload },
      ),
    history: (token: string, tournamentId: number, participantId: number) =>
      apiFetch<ParticipantHistoryResponse>(
        `/admin/tournaments/${tournamentId}/players/${participantId}/history`,
        { token },
      ),
  },
  publicTournaments: {
    list: () => apiFetch<PublicTournamentsResponse>("/public/tournaments"),
    directory: (sport?: string | null) =>
      apiFetch<PublicDirectoryResponse>(
        `/public/tournaments/directory${sport ? `?sport=${encodeURIComponent(sport)}` : ""}`,
      ),
  },
  publicRegistrations: {
    get: (slugOrId: string) =>
      apiFetch<PublicRegistrationResponse>(
        `/public/tournaments/${encodeURIComponent(slugOrId)}/registration`,
      ),
    me: (slugOrId: string, token: string) =>
      apiFetch<PublicRegistrationMeResponse>(
        `/public/tournaments/${encodeURIComponent(slugOrId)}/registration/me`,
        { token },
      ),
    submit: (slugOrId: string, payload: PublicRegistrationSubmitInput, token?: string | null, signal?: AbortSignal) =>
      apiUpload<PublicRegistrationSubmitResponse>(
        `/public/tournaments/${encodeURIComponent(slugOrId)}/registration`,
        publicRegistrationFormData(payload),
        token,
        signal,
      ),
    parseCurp: (slugOrId: string, file: File) =>
      apiUpload<PublicCurpParseResponse>(
        `/public/tournaments/${encodeURIComponent(slugOrId)}/registration/parse-curp`,
        curpParseFormData(file),
      ),
    uploadPaymentReceipt: (slugOrId: string, orderId: number, file: File, token: string) =>
      apiUpload<PublicPaymentReceiptUploadResponse>(
        `/public/tournaments/${encodeURIComponent(slugOrId)}/registration/orders/${orderId}/payment-receipt`,
        singleFileFormData(file),
        token,
      ),
  },
  publicPayments: {
    /** Documentos pendientes de la orden, sin sesión. El token va en un
     * encabezado (no en la ruta) para que no quede en bitácoras. */
    orderDocuments: (slug: string, orderToken: string) =>
      apiFetch<{ ok: boolean; data: PublicOrderDocumentsPayload }>(
        `/public/tournaments/${encodeURIComponent(slug)}/registration/order-documents`,
        { headers: { "X-Order-Token": orderToken } },
      ),
    /** Sube un documento pendiente desde el link público de la orden (solo lo
     * que falta). El token va en el formulario. */
    uploadOrderDocument: (
      slug: string,
      orderToken: string,
      documentKey: string,
      file: File,
      onProgress?: (fraction: number) => void,
      options?: UploadWithProgressOptions,
    ) => {
      const formData = new FormData();
      formData.append("order_token", orderToken);
      formData.append("document_key", documentKey);
      formData.append("file", file);
      return apiUploadWithProgress<{ ok: boolean; data: PublicOrderDocumentsPayload }>(
        `/public/tournaments/${encodeURIComponent(slug)}/registration/order-documents`,
        formData,
        null,
        onProgress,
        options,
      );
    },
    status: (orderToken: string) =>
      apiFetch<PublicPaymentStatusResponse>(
        `/public/registration/orders/${encodeURIComponent(orderToken)}/payment-status`,
        {},
      ),
    startCheckout: (slugOrId: string | number, orderToken: string, provider: string) =>
      apiFetch<PublicCheckoutStartResponse>(
        provider === "mercadopago_marketplace"
          ? `/public/tournaments/${encodeURIComponent(String(slugOrId))}/registration/orders/${encodeURIComponent(orderToken)}/mercadopago-checkout`
          : `/public/tournaments/${encodeURIComponent(String(slugOrId))}/registration/orders/${encodeURIComponent(orderToken)}/checkout`,
        provider === "mercadopago_marketplace"
          ? { method: "POST" }
          : { method: "POST", body: { provider } },
      ),
  },
  payments: {
    account: (token: string) =>
      apiFetch<AdminPaymentAccountResponse>("/admin/payments/account", { token }),
    finance: (token: string, params: AdminFinanceParams = {}) => {
      const query = financeQuery(params);
      return apiFetch<AdminFinanceDashboardResponse>(`/admin/payments/finance${query ? `?${query}` : ""}`, { token });
    },
    /** Excel con TODAS las órdenes de los filtros actuales (no solo la página). */
    downloadFinanceXlsx: (token: string, params: AdminFinanceParams = {}) => {
      const { page: _page, page_size: _pageSize, ...rest } = params;
      const query = financeQuery(rest);
      return fetch(`${API_BASE_URL}/admin/payments/finance/export${query ? `?${query}` : ""}`, {
        headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` },
      });
    },
    connectAccount: (token: string) =>
      apiFetch<AdminPaymentConnectResponse>("/admin/payments/account/connect", { method: "POST", token }),
    refreshAccount: (token: string) =>
      apiFetch<{ ok: boolean; data: { account: PaymentConnectedAccount } }>("/admin/payments/account/refresh", { method: "POST", token }),
    mpConnect: (token: string) =>
      apiFetch<{ ok: boolean; data: { authorization_url: string; state: string } }>("/admin/payments/mercadopago/connect", { method: "POST", token }),
    mpCallback: (token: string, payload: { code: string; state: string }) =>
      apiFetch<{ ok: boolean; data: { account: PaymentConnectedAccount } }>("/admin/payments/mercadopago/callback", { method: "POST", token, body: payload }),
    mpDisconnect: (token: string) =>
      apiFetch<{ ok: boolean; data: { account: PaymentConnectedAccount } }>("/admin/payments/mercadopago/disconnect", { method: "POST", token }),
    // Revoca la autorización actual en MP y devuelve el OAuth nuevo (misma forma que mpConnect)
    mpChangeAccount: (token: string) =>
      apiFetch<{ ok: boolean; data: { authorization_url: string; state: string } }>("/admin/payments/mercadopago/change-account", { method: "POST", token }),
    createBankAccount: (token: string, payload: BankAccountInput & { make_default?: boolean }) =>
      apiFetch<{ ok: boolean; data: { bank_account: PaymentBankAccount; account: PaymentBankAccount } }>("/admin/payments/bank-accounts", { method: "POST", token, body: payload }),
    updateBankAccount: (token: string, accountId: number, payload: BankAccountInput) =>
      apiFetch<{ ok: boolean; data: { bank_account: PaymentBankAccount } }>(`/admin/payments/bank-accounts/${accountId}`, { method: "PATCH", token, body: payload }),
    setDefaultBankAccount: (token: string, accountId: number) =>
      apiFetch<{ ok: boolean; data: { bank_account: PaymentBankAccount } }>(`/admin/payments/bank-accounts/${accountId}/default`, { method: "POST", token }),
    /** «Quitar»: baja lógica. 409 `bank_account_in_use` si un torneo con
     * inscripción abierta la usa y no se mandó reemplazo. */
    deactivateBankAccount: (token: string, accountId: number, replacementAccountId?: number | null) =>
      apiFetch<{ ok: boolean; data: { account_id: number; moved_tournaments: number } }>(`/admin/payments/bank-accounts/${accountId}/deactivate`, {
        method: "POST",
        token,
        body: { replacement_account_id: replacementAccountId ?? null },
      }),
    platformSettings: (token: string) =>
      apiFetch<PlatformPaymentSettingsResponse>("/superadmin/payments/platform-settings", { token }),
    updatePlatformSettings: (token: string, payload: PlatformPaymentSettingsInput) =>
      apiFetch<PlatformPaymentSettingsResponse>("/superadmin/payments/platform-settings", { method: "PUT", token, body: payload }),
    tournamentFees: (token: string) =>
      apiFetch<TournamentFeesResponse>("/superadmin/payments/tournament-fees", { token }),
    updateTournamentFees: (
      token: string,
      tournamentId: number,
      payload: TournamentFeeUpdateInput,
    ) =>
      apiFetch<{ ok: boolean; data: { tournament: TournamentFeeRow } }>(
        `/superadmin/payments/tournament-fees/${tournamentId}`,
        { method: "PUT", token, body: payload },
      ),
  },
  registrations: {
    get: (token: string, tournamentId: number) =>
      apiFetch<RegistrationDashboardResponse>(
        `/admin/tournaments/${tournamentId}/registrations`,
        { token },
      ),
    quickEnroll: (
      token: string,
      tournamentId: number,
      payload: RegistrationQuickEnrollInput,
    ) =>
      apiFetch<{ ok: boolean; data: { participants: TournamentParticipant[]; order: RegistrationOrder } }>(
        `/admin/tournaments/${tournamentId}/registrations/quick-enroll`,
        { method: "POST", token, body: payload },
      ),
    updateSettings: (
      token: string,
      tournamentId: number,
      payload: RegistrationSettingsInput,
    ) =>
      apiFetch<RegistrationDashboardResponse>(
        `/admin/tournaments/${tournamentId}/registrations/settings`,
        { method: "PUT", token, body: payload },
      ),
    updateFormFields: (
      token: string,
      tournamentId: number,
      registration_form_schema: RegistrationFormSchema,
    ) =>
      apiFetch<RegistrationDashboardResponse>(
        `/admin/tournaments/${tournamentId}/registrations/form-fields`,
        { method: "PUT", token, body: { registration_form_schema } },
      ),
    formSources: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { sources: RegistrationFormSource[] } }>(
        `/admin/tournaments/${tournamentId}/registrations/form-sources`,
        { token },
      ),
    copyFormFrom: (token: string, tournamentId: number, sourceTournamentId: number) =>
      apiFetch<RegistrationDashboardResponse>(
        `/admin/tournaments/${tournamentId}/registrations/form-fields/copy-from/${sourceTournamentId}`,
        { method: "POST", token },
      ),
    reclassifyFee: (
      token: string,
      tournamentId: number,
      orderId: number,
      feeCategory: string,
    ) =>
      apiFetch<{
        ok: boolean;
        data: { order: RegistrationOrder; whatsapp_message: string; email: { sent?: boolean } };
      }>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/reclassify-fee`,
        { method: "POST", token, body: { fee_category: feeCategory } },
      ),
    /** Emitir reembolso de una orden pagada: MP por API, manual solo asiento.
     * Con `amount` es parcial (la orden sigue pagada); sin él, lo que falta. */
    refundOrder: (
      token: string,
      tournamentId: number,
      orderId: number,
      payload: { reason?: string | null; amount?: string | null },
    ) =>
      apiFetch<{
        ok: boolean;
        data: {
          order: RegistrationOrder;
          via: "mercadopago" | "manual";
          refund_id: string | null;
          amount: string;
          partial: boolean;
          refunded_amount: string;
          remaining_amount: string;
          summary: RegistrationMarkPaidResponse["data"]["summary"];
        };
      }>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/refund`,
        { method: "POST", token, body: payload },
      ),
    /** Reemitir una orden pendiente: sin amount → costo vigente; con amount → total manual. */
    reissueOrder: (
      token: string,
      tournamentId: number,
      orderId: number,
      payload: { amount?: string | null; reason?: string | null },
    ) =>
      apiFetch<{
        ok: boolean;
        data: { order: RegistrationOrder; previous_amount: string; regenerated_only?: boolean; expired_checkouts?: number; whatsapp_message: string; email: { sent?: boolean } };
      }>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/reissue`,
        { method: "POST", token, body: payload },
      ),
    resolveBalance: (
      token: string,
      tournamentId: number,
      orderId: number,
      action: "paid" | "waived",
    ) =>
      apiFetch<{ ok: boolean; data: { order: RegistrationOrder } }>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/balance`,
        { method: "POST", token, body: { action } },
      ),
    credentialSample: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { svg: string } }>(
        `/admin/tournaments/${tournamentId}/registrations/credential-sample`,
        { token },
      ),
    /** Interruptor «Este torneo usa credencial» (Eduardo 2026-10-05). */
    closeRegistrationsNow: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { registrations_closed_now: boolean } }>(
        `/admin/tournaments/${tournamentId}/registrations/close-now`,
        { method: "POST", token },
      ),
    reopenRegistrations: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { registrations_closed_now: boolean } }>(
        `/admin/tournaments/${tournamentId}/registrations/reopen`,
        { method: "POST", token },
      ),
    setCredentialEnabled: (token: string, tournamentId: number, credentialEnabled: boolean) =>
      apiFetch<RegistrationCredentialTemplateResponse>(
        `/admin/tournaments/${tournamentId}/registrations/credential-enabled`,
        { method: "PUT", token, body: { credential_enabled: credentialEnabled } },
      ),
    regenerateCredentials: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { regenerated: number; skipped_no_photo: number } }>(
        `/admin/tournaments/${tournamentId}/registrations/regenerate-credentials`,
        { method: "POST", token },
      ),
    updateCredentialTemplate: (
      token: string,
      tournamentId: number,
      payload: RegistrationCredentialTemplateInput,
    ) =>
      apiUpload<RegistrationCredentialTemplateResponse>(
        `/admin/tournaments/${tournamentId}/registrations/credential-template`,
        registrationCredentialTemplateFormData(payload),
        token,
      ),
    generateCredential: (
      token: string,
      tournamentId: number,
      orderId: number,
      photo?: { offset_x?: number; offset_y?: number; scale?: number },
    ) => {
      const params = new URLSearchParams();
      if (photo?.offset_x !== undefined) params.set("photo_offset_x", String(photo.offset_x));
      if (photo?.offset_y !== undefined) params.set("photo_offset_y", String(photo.offset_y));
      if (photo?.scale !== undefined) params.set("photo_scale", String(photo.scale));
      const qs = params.toString();
      return apiFetch<RegistrationGenerateCredentialResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/generate-credential${qs ? `?${qs}` : ""}`,
        { method: "POST", token },
      );
    },
    uploadDocument: (
      token: string,
      tournamentId: number,
      orderId: number,
      documentType: RegistrationDocumentType,
      file: File,
    ) =>
      apiUpload<RegistrationDocumentUploadResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/document`,
        registrationDocumentFormData(documentType, file),
        token,
      ),
    /** «Revisar CURPs»: compara las CURP aún sin revisar con el registro. */
    reviewCurps: (token: string, tournamentId: number, payload: { force?: boolean } = {}) =>
      apiFetch<RegistrationCurpReviewRunResponse>(
        `/admin/tournaments/${tournamentId}/registrations/revisar-curps`,
        { method: "POST", token, body: payload },
      ),
    /** «Usar datos de la CURP»: nombre y fecha de nacimiento del perfil. */
    applyCurpData: (token: string, tournamentId: number, orderId: number, payload: { checked_url: string }) =>
      apiFetch<RegistrationCurpApplyResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/curp/usar-datos`,
        { method: "POST", token, body: payload },
      ),
    /** «Pedir otra CURP»: la quita del registro y avisa al jugador. */
    requestNewCurp: (token: string, tournamentId: number, orderId: number, payload: { checked_url: string }) =>
      apiFetch<RegistrationCurpRequestResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/curp/pedir-otra`,
        { method: "POST", token, body: payload },
      ),
    /** «Está bien así»: la deja como está y sale de «CURP por revisar». */
    markCurpOk: (token: string, tournamentId: number, orderId: number, payload: { checked_url: string }) =>
      apiFetch<RegistrationCurpOkResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/curp/esta-bien`,
        { method: "POST", token, body: payload },
      ),
    markPaid: (
      token: string,
      tournamentId: number,
      orderId: number,
      payload: { provider_reference?: string | null; notes?: string | null } = {},
    ) =>
      apiFetch<RegistrationMarkPaidResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/mark-paid`,
        { method: "POST", token, body: payload },
      ),
    updatePayment: (
      token: string,
      tournamentId: number,
      orderId: number,
      payload: { status: RegistrationPaymentStatus; provider_reference?: string | null; notes?: string | null },
    ) =>
      apiFetch<RegistrationMarkPaidResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/payment`,
        { method: "PATCH", token, body: payload },
      ),
    sendWelcome: (token: string, tournamentId: number, orderId: number) =>
      apiFetch<RegistrationWelcomeResponse>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/send-welcome`,
        { method: "POST", token },
      ),
    /** Botones «Enviar documentos pendientes» / «Enviar recordatorio de pago» (Eduardo 2026-09-23). */
    sendDocsPending: (token: string, tournamentId: number, orderId: number) =>
      apiFetch<{ ok: boolean; data: { email_status: string; missing_documents: string[]; title: string } }>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/send-docs-pending`,
        { method: "POST", token, body: {} },
      ),
    sendPaymentReminder: (token: string, tournamentId: number, orderId: number) =>
      apiFetch<{ ok: boolean; data: { email_status: string; title: string } }>(
        `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/send-payment-reminder`,
        { method: "POST", token, body: {} },
      ),
    createCharge: (
      token: string,
      tournamentId: number,
      payload: {
        player_profile_id?: number | null;
        new_player?: {
          first_name: string;
          last_name?: string | null;
          phone?: string | null;
          email?: string | null;
        } | null;
        amount: string;
      },
    ) =>
      apiFetch<{
        ok: boolean;
        data: { order: RegistrationOrder; created: boolean; payment_url: string };
      }>(`/admin/tournaments/${tournamentId}/registrations/charges`, {
        method: "POST",
        token,
        body: payload,
      }),
    cancelCharge: (token: string, tournamentId: number, orderId: number) =>
      apiFetch<{ ok: boolean; data: { order: RegistrationOrder } }>(
        `/admin/tournaments/${tournamentId}/registrations/charges/${orderId}/cancel`,
        { method: "POST", token },
      ),
  },
  groups: {
    list: (token: string, tournamentId: number, params: { division_id?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.division_id !== undefined) qs.set("division_id", String(params.division_id));
      const query = qs.toString();
      return apiFetch<GroupsListResponse>(
        `/admin/tournaments/${tournamentId}/groups${query ? `?${query}` : ""}`,
        { token },
      );
    },
    create: (token: string, tournamentId: number, payload: GroupCreateInput) =>
      apiFetch<GroupResponse>(
        `/admin/tournaments/${tournamentId}/groups`,
        { method: "POST", token, body: payload },
      ),
    get: (token: string, tournamentId: number, groupId: number) =>
      apiFetch<GroupResponse>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}`,
        { token },
      ),
    update: (
      token: string,
      tournamentId: number,
      groupId: number,
      payload: GroupUpdateInput,
    ) =>
      apiFetch<GroupResponse>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}`,
        { method: "PATCH", token, body: payload },
      ),
    deactivate: (token: string, tournamentId: number, groupId: number) =>
      apiFetch<GroupResponse>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}/deactivate`,
        { method: "POST", token },
      ),
    /** Reactivar un grupo inactivo (Eduardo 2026-09-24); vuelve vacío. */
    reactivate: (token: string, tournamentId: number, groupId: number) =>
      apiFetch<GroupResponse>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}/reactivate`,
        { method: "POST", token },
      ),
    remove: (token: string, tournamentId: number, groupId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean } }>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}`,
        { method: "DELETE", token },
      ),
    unassigned: (token: string, tournamentId: number) =>
      apiFetch<GroupUnassignedResponse>(
        `/admin/tournaments/${tournamentId}/groups/unassigned`,
        { token },
      ),
    history: (token: string, tournamentId: number) =>
      apiFetch<GroupMovementHistoryResponse>(
        `/admin/tournaments/${tournamentId}/groups/history`,
        { token },
      ),
    assign: (
      token: string,
      tournamentId: number,
      groupId: number,
      payload: GroupAssignInput,
    ) =>
      apiFetch<GroupAssignmentResponse>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}/participants`,
        { method: "POST", token, body: payload },
      ),
    move: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: GroupMoveInput,
    ) =>
      apiFetch<GroupAssignmentResponse>(
        `/admin/tournaments/${tournamentId}/groups/participants/${participantId}/move`,
        { method: "POST", token, body: payload },
      ),
    removeParticipant: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: GroupActionInput = {},
    ) =>
      apiFetch<GroupRemovalResponse>(
        `/admin/tournaments/${tournamentId}/groups/participants/${participantId}/remove`,
        { method: "POST", token, body: payload },
      ),
    // Categorías de grupo único (round robin, group_count == 1): crea el
    // grupo si falta y asigna a todos los participantes sin grupo.
    /** Sorteo público de grupos de Round Robin (Eduardo 2026-09-26). */
    drawPreview: (token: string, tournamentId: number, divisionId: number | null) =>
      apiFetch<{ ok: boolean; data: GroupDrawPreview }>(
        `/admin/tournaments/${tournamentId}/groups/draw-preview${divisionId != null ? `?division_id=${divisionId}` : ""}`,
        { token },
      ),
    draw: (token: string, tournamentId: number, divisionId: number | null) =>
      apiFetch<{ ok: boolean; data: { ceremony: DrawCeremony } }>(
        `/admin/tournaments/${tournamentId}/groups/draw`,
        { method: "POST", token, body: { division_id: divisionId } },
      ),
    autoAssignSingle: (
      token: string,
      tournamentId: number,
      payload: { division_id: number | null; confirm_rebuild?: boolean },
    ) =>
      apiFetch<{
        ok: boolean;
        data: {
          group: TournamentGroup;
          group_created: boolean;
          assigned_count: number;
          already_assigned_count: number;
          skipped_ineligible?: { participant_id: number; label: string; reason: string }[];
          rr_rebuild?: RrRebuildSummary | null;
        };
      }>(
        `/admin/tournaments/${tournamentId}/groups/auto-assign-single`,
        { method: "POST", token, body: payload },
      ),
    /** Acomodar la hoja del Round Robin (Eduardo 2026-10-01): candado por
     * categoría. Sin division_id lista todas las categorías de Round Robin. */
    arrangementStatus: (token: string, tournamentId: number, params: { division_id?: number | null } = {}) => {
      const qs = new URLSearchParams();
      if (params.division_id != null) qs.set("division_id", String(params.division_id));
      const query = qs.toString();
      return apiFetch<{ ok: boolean; data: { categories: GroupArrangementCategory[] } }>(
        `/admin/tournaments/${tournamentId}/groups/arrangement-status${query ? `?${query}` : ""}`,
        { token },
      );
    },
    /** Revisión (sin escribir) de un nuevo orden del grupo. */
    orderPreview: (token: string, tournamentId: number, groupId: number, participantIds: number[]) =>
      apiFetch<{ ok: boolean; data: GroupOrderPreview }>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}/order/preview?participant_ids=${participantIds.join(",")}`,
        { token },
      ),
    saveOrder: (token: string, tournamentId: number, groupId: number, payload: GroupOrderSaveInput) =>
      apiFetch<{ ok: boolean; data: GroupOrderSaveResult }>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}/order`,
        { method: "PUT", token, body: payload },
      ),
    /** Revisión de «Intercambiar» / «Mover solo» a otro grupo. */
    changeGroupPreview: (
      token: string,
      tournamentId: number,
      participantId: number,
      params: GroupChangePreviewInput,
    ) => {
      const qs = new URLSearchParams();
      qs.set("to_group_id", String(params.to_group_id));
      qs.set("mode", params.mode);
      if (params.swap_with_participant_id != null) {
        qs.set("swap_with_participant_id", String(params.swap_with_participant_id));
      }
      if (params.from_group_id != null) qs.set("from_group_id", String(params.from_group_id));
      return apiFetch<{ ok: boolean; data: GroupChangePreview }>(
        `/admin/tournaments/${tournamentId}/groups/participants/${participantId}/change-group/preview?${qs.toString()}`,
        { token },
      );
    },
    changeGroup: (token: string, tournamentId: number, participantId: number, payload: GroupChangeInput) =>
      apiFetch<{ ok: boolean; data: GroupChangeResult }>(
        `/admin/tournaments/${tournamentId}/groups/participants/${participantId}/change-group`,
        { method: "POST", token, body: payload },
      ),
    /** «Reemplazar jugador» (Eduardo 2026-10-14): quienes ya jugaron algo. */
    replacePlayed: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { participant_ids: number[] } }>(
        `/admin/tournaments/${tournamentId}/participants/replace/played`,
        { token },
      ),
    replaceOptions: (token: string, tournamentId: number, participantId: number) =>
      apiFetch<{ ok: boolean; data: ReplaceOptions }>(
        `/admin/tournaments/${tournamentId}/participants/${participantId}/replace/options`,
        { token },
      ),
    replaceParticipant: (
      token: string,
      tournamentId: number,
      participantId: number,
      payload: { replacement_participant_id?: number | null; player_profile_id?: number | null },
    ) =>
      apiFetch<{ ok: boolean; data: ReplaceResult }>(
        `/admin/tournaments/${tournamentId}/participants/${participantId}/replace`,
        { method: "POST", token, body: payload },
      ),
    /** Qué pasaría con los partidos al asignar, mover, quitar, cambiar de
     * categoría o desactivar un grupo (no escribe; Eduardo 2026-10-13). */
    membershipPreview: (token: string, tournamentId: number, params: MembershipChangePreviewParams) => {
      const qs = new URLSearchParams();
      qs.set("action", params.action);
      if (params.participant_id != null) qs.set("participant_id", String(params.participant_id));
      if (params.to_group_id != null) qs.set("to_group_id", String(params.to_group_id));
      if (params.group_id != null) qs.set("group_id", String(params.group_id));
      if (params.division_id != null) qs.set("division_id", String(params.division_id));
      return apiFetch<{ ok: boolean; data: MembershipChangePreview }>(
        `/admin/tournaments/${tournamentId}/groups/membership-change/preview?${qs.toString()}`,
        { token },
      );
    },
    /** «Quitar esos partidos»: cancela los pendientes que sobran en el grupo
     * (de quien ya no está o repetidos). */
    /** `matchIds`: los que el organizador vio en la confirmación; si la
     * lista ya cambió, el servidor no cancela nada (409). */
    cancelGhostMatches: (token: string, tournamentId: number, groupId: number, matchIds?: number[]) =>
      apiFetch<{ ok: boolean; data: GroupStrayMatchesCancelResult }>(
        `/admin/tournaments/${tournamentId}/groups/${groupId}/ghost-matches/cancel`,
        { method: "POST", token, body: matchIds ? { match_ids: matchIds } : {} },
      ),
  },
  rankings: {
    listSystems: (token: string) =>
      apiFetch<{ ok: boolean; data: { systems: RankingSystemSummary[] } }>(
        "/admin/ranking-systems",
        { token },
      ),
    createSystem: (token: string, payload: { preset_slug: string; scope?: string; name?: string; slug?: string }) =>
      apiFetch<{ ok: boolean; data: { system: RankingSystemSummary } }>(
        "/admin/ranking-systems",
        { method: "POST", token, body: payload },
      ),
    standings: (token: string, systemId: number, ageCategoryId: number) =>
      apiFetch<{ ok: boolean; data: { standings: RankingStandingRow[] } }>(
        `/admin/ranking-systems/${systemId}/standings?age_category_id=${ageCategoryId}`,
        { token },
      ),
    // Ranking PROPIO del organizador (Eduardo 2026-09-22).
    createCustomSystem: (
      token: string,
      payload: {
        name: string;
        slug?: string | null;
        categories: CustomRankingCategoryInput[];
        rules: CustomRankingRulesPayload;
        season_start?: string | null;
        season_end?: string | null;
      },
    ) =>
      apiFetch<{ ok: boolean; data: { system: RankingSystemSummary } }>(
        "/admin/ranking-systems/custom",
        { method: "POST", token, body: payload },
      ),
    updateCustomSystem: (
      token: string,
      systemId: number,
      payload: {
        name?: string;
        rules?: CustomRankingRulesPayload;
        is_active?: boolean;
        categories?: CustomRankingCategoryInput[];
        season_start?: string | null;
        season_end?: string | null;
      },
    ) =>
      apiFetch<{ ok: boolean; data: { system: RankingSystemSummary; recomputed_tournaments: number } }>(
        `/admin/ranking-systems/${systemId}`,
        { method: "PATCH", token, body: payload },
      ),
    importInitialPoints: (
      token: string,
      systemId: number,
      payload: {
        age_category_id: number;
        rows: Array<{ name?: string | null; email?: string | null; phone?: string | null; federation_id?: string | null; points: number }>;
        replace?: boolean;
      },
    ) =>
      apiFetch<{
        ok: boolean;
        data: { matched: Array<{ player_profile_id: number; display_name: string | null; points: number }>; unmatched: string[] };
      }>(
        `/admin/ranking-systems/${systemId}/initial-points`,
        { method: "POST", token, body: payload },
      ),
    /** Penalización (puntos negativos) contra el total de un jugador, con motivo. */
    sanction: (
      token: string,
      systemId: number,
      payload: { age_category_id: number; player_profile_id: number; points: number; reason: string },
    ) =>
      apiFetch<{ ok: boolean; data: { event_id: number; points: number } }>(
        `/admin/ranking-systems/${systemId}/sanctions`,
        { method: "POST", token, body: payload },
      ),
    recomputeSystem: (token: string, systemId: number) =>
      apiFetch<{ ok: boolean; data: { recomputed_tournaments: number } }>(
        `/admin/ranking-systems/${systemId}/recompute`,
        { method: "POST", token, body: {} },
      ),
    systemEvents: (token: string, systemId: number, ageCategoryId: number, playerProfileId?: number | null) =>
      apiFetch<{ ok: boolean; data: { events: RankingSystemEvent[] } }>(
        `/admin/ranking-systems/${systemId}/events?age_category_id=${ageCategoryId}${
          playerProfileId != null ? `&player_profile_id=${playerProfileId}` : ""
        }`,
        { token },
      ),
    publish: (token: string, systemId: number, payload: { age_category_id: number }) =>
      apiFetch<{ ok: boolean; data: { publication_id: number; published_at: string | null; rows: RankingStandingRow[] } }>(
        `/admin/ranking-systems/${systemId}/publish`,
        { method: "POST", token, body: payload },
      ),
    tournamentLinks: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { links: TournamentRankingLinkRow[] } }>(
        `/admin/tournaments/${tournamentId}/ranking-links`,
        { token },
      ),
    // R1 lista oficial POR TORNEO (paso 6): preview del cruce contra
    // inscritos y, con apply=true, siembra de seeds 1..N por posición.
    officialList: (
      token: string,
      tournamentId: number,
      payload: {
        division_id?: number | null;
        apply: boolean;
        rows: Array<{ position?: number | null; name?: string | null; federation_id?: string | null; email?: string | null }>;
      },
    ) =>
      apiFetch<{
        ok: boolean;
        data: {
          matched: Array<{ participant_id: number; name: string; position: number; seed: number }>;
          unmatched: Array<{ position: number; name: string | null }>;
          unlisted: Array<{ participant_id: number; name: string }>;
          applied: boolean;
        };
      }>(
        `/admin/tournaments/${tournamentId}/ranking/official-list`,
        { method: "POST", token, body: payload },
      ),
    // R2: lista oficial persistida — hidrata el paso 6 entre sesiones y
    // habilita el cruce manual de renglones sin match.
    getOfficialList: (token: string, tournamentId: number, divisionId?: number | null) =>
      apiFetch<{ ok: boolean; data: { entries: OfficialListEntry[] } }>(
        `/admin/tournaments/${tournamentId}/ranking/official-list${
          divisionId != null ? `?division_id=${divisionId}` : ""
        }`,
        { token },
      ),
    matchOfficialListEntry: (
      token: string,
      tournamentId: number,
      entryId: number,
      participantId: number | null,
    ) =>
      apiFetch<{ ok: boolean; data: { entry: OfficialListEntry } }>(
        `/admin/tournaments/${tournamentId}/ranking/official-list/entries/${entryId}/match`,
        { method: "POST", token, body: { participant_id: participantId } },
      ),
    reapplyOfficialList: (token: string, tournamentId: number, divisionId?: number | null) =>
      apiFetch<{
        ok: boolean;
        data: {
          matched: Array<{ participant_id: number; position: number; seed: number }>;
          unlisted: Array<{ participant_id: number; name: string }>;
          applied: boolean;
        };
      }>(
        `/admin/tournaments/${tournamentId}/ranking/official-list/reapply`,
        { method: "POST", token, body: { division_id: divisionId ?? null } },
      ),
    importList: (
      token: string,
      systemId: number,
      payload: { age_category_id: number; rows: Array<{ position?: number | null; name?: string | null; federation_id?: string | null; email?: string | null }> },
    ) =>
      apiFetch<{ ok: boolean; data: { publication_id: number; matched: number; unmatched: string[] } }>(
        `/admin/ranking-systems/${systemId}/import-list`,
        { method: "POST", token, body: payload },
      ),
    importWtn: (
      token: string,
      payload: { rows: Array<{ name?: string | null; federation_id?: string | null; email?: string | null; wtn_id?: string | null; rating?: string | null; rating_doubles?: string | null }> },
    ) =>
      apiFetch<{ ok: boolean; data: { updated: number; unmatched: string[] } }>(
        "/admin/rankings/wtn-import",
        { method: "POST", token, body: payload },
      ),
    linkTournament: (
      token: string,
      tournamentId: number,
      payload: {
        ranking_system_id: number;
        category_code: string;
        grade: string;
        division_id?: number | null;
        draw_kind?: string;
        multiplier?: number | null;
      },
    ) =>
      apiFetch<{ ok: boolean; data: { link_id: number } }>(
        `/admin/tournaments/${tournamentId}/ranking-link`,
        { method: "POST", token, body: payload },
      ),
    recompute: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { events_created: number; links: number; frozen?: boolean } }>(
        `/admin/tournaments/${tournamentId}/ranking/recompute`,
        { method: "POST", token, body: {} },
      ),
    applyPublicationSeeding: (
      token: string,
      tournamentId: number,
      payload: { ranking_system_id: number; age_category_id: number; division_id?: number | null },
    ) =>
      apiFetch<{ ok: boolean; data: { applied: number; without_position: number; publication_id: number } }>(
        `/admin/tournaments/${tournamentId}/ranking/apply-publication-seeding`,
        { method: "POST", token, body: payload },
      ),
    publicRanking: (slug: string, category?: string) =>
      apiFetch<{ ok: boolean; data: PublicRankingPayload }>(
        `/public/rankings/${encodeURIComponent(slug)}${category ? `?category=${encodeURIComponent(category)}` : ""}`,
        {},
      ),
  },
  matches: {
    /** `allow_round_order_conflict`: el admin confirmó «de todos modos» aunque
     * un partido de la siguiente ronda ya programado quede empezando antes de
     * que termine su nuevo previo (sin él: 409 `round_order_conflict`). */
    swapMatchups: (
      token: string,
      tournamentId: number,
      payload: { match_a_id: number; match_b_id: number; allow_round_order_conflict?: boolean },
    ) =>
      apiFetch<{ ok: boolean; data: { swapped: boolean } }>(`/admin/tournaments/${tournamentId}/schedule/swap-matchups`, {
        method: "POST",
        token,
        body: payload,
      }),
    clubCalendar: (
      token: string,
      params: { venue_name?: string; scheduled_date?: string } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.venue_name) qs.set("venue_name", params.venue_name);
      if (params.scheduled_date) qs.set("scheduled_date", params.scheduled_date);
      const query = qs.toString();
      return apiFetch<ClubCalendarResponse>(
        `/admin/tournaments/club-calendar${query ? `?${query}` : ""}`,
        { token },
      );
    },
    createCourtBlock: (token: string, payload: CourtBlockCreateInput) =>
      apiFetch<{ ok: boolean; data: { block: CourtBlock } }>(
        "/admin/tournaments/club-calendar/blocks",
        { method: "POST", token, body: payload },
      ),
    deleteCourtBlock: (token: string, blockId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean } }>(
        `/admin/tournaments/club-calendar/blocks/${blockId}`,
        { method: "DELETE", token },
      ),
    list: (
      token: string,
      tournamentId: number,
      params: {
        group_id?: number;
        status?: string;
        scheduled_date?: string;
        division_id?: number;
        include_all_labels?: boolean;
      } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.group_id !== undefined)
        qs.set("group_id", String(params.group_id));
      if (params.status) qs.set("status", params.status);
      if (params.scheduled_date) qs.set("scheduled_date", params.scheduled_date);
      if (params.division_id !== undefined) qs.set("division_id", String(params.division_id));
      if (params.include_all_labels) qs.set("include_all_labels", "true");
      const query = qs.toString();
      return apiFetch<MatchesListResponse>(
        `/admin/tournaments/${tournamentId}/schedule${query ? `?${query}` : ""}`,
        { token },
      );
    },
    create: (
      token: string,
      tournamentId: number,
      payload: MatchCreateInput,
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule`,
        { method: "POST", token, body: payload },
      ),
    get: (token: string, tournamentId: number, matchId: number) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}`,
        { token },
      ),
    update: (
      token: string,
      tournamentId: number,
      matchId: number,
      payload: MatchUpdateInput,
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}`,
        { method: "PATCH", token, body: payload },
      ),
    remove: (token: string, tournamentId: number, matchId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean } }>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}`,
        { method: "DELETE", token },
      ),
    // Alineación de una línea de serie por equipos (Mini Copa 2026).
    updateLineLineup: (token: string, tournamentId: number, matchId: number, payload: { team1: number[]; team2: number[] }) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/lineup`,
        { method: "PUT", token, body: payload },
      ),
    recordResult: (
      token: string,
      tournamentId: number,
      matchId: number,
      payload: MatchResultInput,
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/result`,
        { method: "POST", token, body: payload },
      ),
    postpone: (
      token: string,
      tournamentId: number,
      matchId: number,
      payload: MatchActionInput = {},
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/postpone`,
        { method: "POST", token, body: payload },
      ),
    cancel: (
      token: string,
      tournamentId: number,
      matchId: number,
      payload: MatchActionInput = {},
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/cancel`,
        { method: "POST", token, body: payload },
      ),
    walkover: (
      token: string,
      tournamentId: number,
      matchId: number,
      payload: MatchResultInput,
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/walkover`,
        { method: "POST", token, body: payload },
      ),
    doubleWalkover: (
      token: string,
      tournamentId: number,
      matchId: number,
      payload: MatchActionInput = {},
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/double-walkover`,
        { method: "POST", token, body: payload },
      ),
    noShow: (
      token: string,
      tournamentId: number,
      matchId: number,
      payload: MatchActionInput = {},
    ) =>
      apiFetch<MatchResponse>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/no-show`,
        { method: "POST", token, body: payload },
      ),
    history: (token: string, tournamentId: number) =>
      apiFetch<MatchHistoryResponse>(
        `/admin/tournaments/${tournamentId}/schedule/history`,
        { token },
      ),
    generateRoundRobin: (
      token: string,
      tournamentId: number,
      payload: RoundRobinInput = {},
    ) =>
      apiFetch<RoundRobinResponse>(
        `/admin/tournaments/${tournamentId}/schedule/generate-round-robin`,
        { method: "POST", token, body: payload },
      ),
    generateRoundRobinPlayoffs: (
      token: string,
      tournamentId: number,
      payload: RoundRobinInput = {},
    ) =>
      apiFetch<RoundRobinPlayoffsResponse>(
        `/admin/tournaments/${tournamentId}/schedule/generate-round-robin-playoffs`,
        { method: "POST", token, body: payload },
      ),
    createRoundRobinPlayoffPlaceholders: (
      token: string,
      tournamentId: number,
      payload: RoundRobinInput = {},
    ) =>
      apiFetch<{ ok: boolean; data: { created: number; playoff_slots_count: number; round_labels: string[] } }>(
        `/admin/tournaments/${tournamentId}/schedule/round-robin-playoffs/placeholders`,
        { method: "POST", token, body: payload },
      ),
    regenerateRoundRobinPlayoffs: (
      token: string,
      tournamentId: number,
      payload: { division_id?: number | null; confirm_published?: boolean } = {},
    ) =>
      apiFetch<{ ok: boolean; data: { created: number; deleted: number; unpublished: number; mode: "qualifiers" | "placeholders" } }>(
        `/admin/tournaments/${tournamentId}/schedule/round-robin-playoffs/regenerate`,
        { method: "POST", token, body: payload },
      ),
    fillRoundRobinPlayoffPlaceholders: (
      token: string,
      tournamentId: number,
      payload: RoundRobinInput = {},
    ) =>
      apiFetch<{ ok: boolean; data: { filled: number; matches: number } }>(
        `/admin/tournaments/${tournamentId}/schedule/round-robin-playoffs/fill`,
        { method: "POST", token, body: payload },
      ),
    generateElimination: (
      token: string,
      tournamentId: number,
      payload: EliminationGenerateInput = {},
    ) =>
      apiFetch<EliminationGenerateResponse>(
        `/admin/tournaments/${tournamentId}/schedule/generate-elimination`,
        { method: "POST", token, body: payload },
      ),
    generateEliminationNextRound: (
      token: string,
      tournamentId: number,
      payload: EliminationNextRoundInput = {},
    ) =>
      apiFetch<EliminationNextRoundResponse>(
        `/admin/tournaments/${tournamentId}/schedule/generate-elimination-next-round`,
        { method: "POST", token, body: payload },
      ),
    materializeEliminationSkeleton: (
      token: string,
      tournamentId: number,
      payload: EliminationNextRoundInput = {},
    ) =>
      apiFetch<EliminationSkeletonResponse>(
        `/admin/tournaments/${tournamentId}/schedule/materialize-elimination-skeleton`,
        { method: "POST", token, body: payload },
      ),
    scheduleReadiness: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: { divisions: ScheduleReadinessRow[] } }>(
        `/admin/tournaments/${tournamentId}/schedule/readiness`,
        { token },
      ),
    getDrawCeremony: (token: string, tournamentId: number, divisionId?: number | null) =>
      apiFetch<DrawCeremonyResponse>(
        `/admin/tournaments/${tournamentId}/draw-ceremony${divisionId != null ? `?division_id=${divisionId}` : ""}`,
        { token },
      ),
    scheduleDrawCeremony: (
      token: string,
      tournamentId: number,
      payload: { division_id?: number | null; group_id?: number | null; scheduled_at: string | null },
    ) =>
      apiFetch<DrawCeremonyResponse>(
        `/admin/tournaments/${tournamentId}/draw-ceremony`,
        { method: "PUT", token, body: payload },
      ),
    bulkProgram: (
      token: string,
      tournamentId: number,
      payload: BulkProgramInput,
    ) =>
      apiFetch<BulkProgramResponse>(
        `/admin/tournaments/${tournamentId}/schedule/bulk-program`,
        { method: "POST", token, body: payload },
      ),
    bulkUnschedule: (
      token: string,
      tournamentId: number,
      /** `reason` solo cuenta para horarios ya publicados: va en su aviso. */
      payload: { match_ids: number[]; reason?: string | null },
    ) =>
      apiFetch<{ ok: boolean; data: { summary: BulkUnscheduleSummary } }>(
        `/admin/tournaments/${tournamentId}/schedule/bulk-unschedule`,
        { method: "POST", token, body: payload },
      ),
    /** «Despublicar» el horario de UN partido: conserva fecha, hora y cancha
     * pero vuelve a borrador. Sus jugadores reciben «quedó sin horario». */
    unpublishMatchSchedule: (token: string, tournamentId: number, matchId: number, payload: { reason?: string | null } = {}) =>
      apiFetch<{ ok: boolean; data: { match: TournamentMatch; notified_players: number } }>(
        `/admin/tournaments/${tournamentId}/schedule/${matchId}/unpublish-schedule`,
        { method: "POST", token, body: payload },
      ),
    /** Cuántos horarios y jugadores tocaría «Publicar horarios» (no escribe nada). */
    previewSchedulePublish: (token: string, tournamentId: number, payload: SchedulePublishInput) =>
      apiFetch<{ ok: boolean; data: SchedulePublishPreview }>(
        `/admin/tournaments/${tournamentId}/schedule/publish`,
        { method: "POST", token, body: { ...payload, dry_run: true } },
      ),
    /** «Publicar horarios»: los jugadores ya ven esos horarios y cada uno
     * recibe UN aviso con los suyos de ese día. */
    publishSchedule: (token: string, tournamentId: number, payload: SchedulePublishInput) =>
      apiFetch<{ ok: boolean; data: SchedulePublishResult }>(
        `/admin/tournaments/${tournamentId}/schedule/publish`,
        { method: "POST", token, body: payload },
      ),
  },
  rounds: {
    list: (token: string, tournamentId: number, params: { division_id?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.division_id !== undefined) qs.set("division_id", String(params.division_id));
      const query = qs.toString();
      return apiFetch<RoundsListResponse>(
        `/admin/tournaments/${tournamentId}/rounds${query ? `?${query}` : ""}`,
        { token },
      );
    },
    generate: (
      token: string,
      tournamentId: number,
      payload: RoundsGenerateInput = {},
    ) =>
      apiFetch<RoundsGenerateResponse>(
        `/admin/tournaments/${tournamentId}/rounds/generate`,
        { method: "POST", token, body: payload },
      ),
    /** Playoffs de Escalera. Con `divisionId` se generan para ESA categoría
     * (su propio formato, ranking y jornadas); sin él, a nivel torneo. */
    generatePlayoffs: (token: string, tournamentId: number, divisionId?: number | null) =>
      apiFetch<LadderPlayoffsGenerateResponse>(
        `/admin/tournaments/${tournamentId}/rounds/generate-playoffs`,
        { method: "POST", token, body: divisionId != null ? { division_id: divisionId } : {} },
      ),
    get: (token: string, tournamentId: number, roundId: number) =>
      apiFetch<RoundResponse>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}`,
        { token },
      ),
    /** Fechas de cada jornada de una categoría (torneos sin calendario de canchas). */
    jornadaDates: (token: string, tournamentId: number, divisionId: number | null) =>
      apiFetch<JornadaDatesResponse>(
        `/admin/tournaments/${tournamentId}/jornada-dates${divisionId != null ? `?division_id=${divisionId}` : ""}`,
        { token },
      ),
    setJornadaDates: (token: string, tournamentId: number, payload: JornadaDatesUpdateInput) =>
      apiFetch<JornadaDatesResponse>(
        `/admin/tournaments/${tournamentId}/jornada-dates`,
        { method: "PUT", token, body: payload },
      ),
    copyJornadaDatesToDivisions: (token: string, tournamentId: number, divisionId: number) =>
      apiFetch<JornadaDatesResponse>(
        `/admin/tournaments/${tournamentId}/jornada-dates/copy-to-divisions`,
        { method: "POST", token, body: { division_id: divisionId } },
      ),
    /** Fechas guardadas y quién descansa en cada jornada, de todas las
     * categorías (Eduardo 2026-10-05: PDF y listas por jornada). */
    jornadaOverview: (token: string, tournamentId: number) =>
      apiFetch<JornadaOverviewResponse>(`/admin/tournaments/${tournamentId}/jornada-overview`, { token }),
    update: (
      token: string,
      tournamentId: number,
      roundId: number,
      payload: RoundUpdateInput,
    ) =>
      apiFetch<RoundResponse>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}`,
        { method: "PATCH", token, body: payload },
      ),
    updateMatchup: (
      token: string,
      tournamentId: number,
      roundId: number,
      matchupId: number,
      payload: MatchupUpdateInput,
    ) =>
      apiFetch<MatchupResponse>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}/matchups/${matchupId}`,
        { method: "PATCH", token, body: payload },
      ),
    bulkUpdateMatchups: (
      token: string,
      tournamentId: number,
      roundId: number,
      payload: MatchupBulkUpdateInput,
    ) =>
      apiFetch<MatchupBulkUpdateResponse>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}/matchups/bulk`,
        { method: "PATCH", token, body: payload },
      ),
    deleteMatchup: (
      token: string,
      tournamentId: number,
      roundId: number,
      matchupId: number,
    ) =>
      apiFetch<{
        ok: boolean;
        data: { deleted: boolean; matchup_id: number };
      }>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}/matchups/${matchupId}`,
        { method: "DELETE", token },
      ),
    createMatchup: (
      token: string,
      tournamentId: number,
      roundId: number,
      payload: MatchupCreateInput,
    ) =>
      apiFetch<MatchupResponse>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}/matchups`,
        { method: "POST", token, body: payload },
      ),
    publish: (token: string, tournamentId: number, roundId: number) =>
      apiFetch<RoundPublishResponse>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}/publish`,
        { method: "POST", token },
      ),
    backToDraft: (token: string, tournamentId: number, roundId: number) =>
      apiFetch<RoundBackToDraftResponse>(
        `/admin/tournaments/${tournamentId}/rounds/${roundId}/back-to-draft`,
        { method: "POST", token },
      ),
  },
  standings: {
    get: (token: string, tournamentId: number, params: { division_id?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.division_id !== undefined) qs.set("division_id", String(params.division_id));
      const query = qs.toString();
      return apiFetch<StandingsResponse>(
        `/admin/tournaments/${tournamentId}/standings${query ? `?${query}` : ""}`,
        { token },
      );
    },
    qualificationPreview: (token: string, tournamentId: number, params: { division_id?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.division_id !== undefined) qs.set("division_id", String(params.division_id));
      const query = qs.toString();
      return apiFetch<RoundRobinQualificationPreviewResponse>(
        `/admin/tournaments/${tournamentId}/standings/qualification-preview${query ? `?${query}` : ""}`,
        { token },
      );
    },
    saveQualificationOverrides: (token: string, tournamentId: number, payload: RoundRobinQualificationOverridesInput) =>
      apiFetch<RoundRobinQualificationPreviewResponse>(
        `/admin/tournaments/${tournamentId}/standings/qualification-overrides`,
        { method: "POST", token, body: payload },
      ),
    recalculate: (token: string, tournamentId: number) =>
      apiFetch<StandingsResponse>(
        `/admin/tournaments/${tournamentId}/standings/recalculate`,
        { method: "POST", token },
      ),
    forPlayer: (token: string, tournamentId: number, playerProfileId?: number | null) =>
      apiFetch<PlayerStandingsResponse>(
        `/player/tournaments/${tournamentId}/standings${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    /** Celulares de SU grupo de round robin (solo sin calendario de canchas). */
    groupContactsForPlayer: (token: string, tournamentId: number, playerProfileId?: number | null) =>
      apiFetch<{ ok: boolean; data: PlayerGroupContactsPayload }>(
        `/player/tournaments/${tournamentId}/group-contacts${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    /** Su reto de cada jornada (solo round robin sin calendario de canchas). */
    jornadaRetosForPlayer: (token: string, tournamentId: number, playerProfileId?: number | null) =>
      apiFetch<{ ok: boolean; data: PlayerJornadaRetosPayload }>(
        `/player/tournaments/${tournamentId}/jornada-retos${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    /** Menú del jugador: ¿ya se ven Calendario/Draw/Ranking? (Eduardo 2026-09-24). */
    playerVisibility: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: PlayerStageVisibility }>(
        `/player/tournaments/${tournamentId}/visibility`,
        { token },
      ),
  },
  resultReports: {
    listForPlayer: (token: string, playerProfileId?: number | null) =>
      apiFetch<PlayerResultReportsResponse>(`/player/result-reports${playerProfileQuery(playerProfileId)}`, {
        token,
      }),
    listTournamentResultsForPlayer: (token: string, tournamentId: number, playerProfileId?: number | null) =>
      apiFetch<PlayerTournamentResultsResponse>(
        `/player/tournaments/${tournamentId}/results${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    listTournamentResultsForAdmin: (token: string, tournamentId: number) =>
      apiFetch<PlayerTournamentResultsResponse>(
        `/admin/tournaments/${tournamentId}/results`,
        { token },
      ),
    submitForMatch: (
      token: string,
      matchId: number,
      payload: PlayerResultReportInput,
      playerProfileId?: number | null,
    ) =>
      apiFetch<ResultReportResponse>(
        `/player/matches/${matchId}/result-report${playerProfileQuery(playerProfileId)}`,
        { method: "POST", token, body: payload },
      ),
    listForAdmin: (
      token: string,
      tournamentId: number,
      params: { status?: string } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.status) qs.set("status", params.status);
      const query = qs.toString();
      return apiFetch<AdminResultReportsListResponse>(
        `/admin/tournaments/${tournamentId}/result-reports${
          query ? `?${query}` : ""
        }`,
        { token },
      );
    },
    approve: (
      token: string,
      tournamentId: number,
      reportId: number,
      payload: AdminApproveResultReportInput = {},
    ) =>
      apiFetch<ResultReportApprovalResponse>(
        `/admin/tournaments/${tournamentId}/result-reports/${reportId}/approve`,
        { method: "POST", token, body: payload },
      ),
    reject: (
      token: string,
      tournamentId: number,
      reportId: number,
      payload: AdminRejectResultReportInput = {},
    ) =>
      apiFetch<ResultReportResponse>(
        `/admin/tournaments/${tournamentId}/result-reports/${reportId}/reject`,
        { method: "POST", token, body: payload },
      ),
    /** Tabla por partido (Eduardo 2026-10-06). */
    listByMatch: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: ReportsByMatchPayload }>(
        `/admin/tournaments/${tournamentId}/result-reports/by-match`,
        { token },
      ),
    /** «Guardar y aprobar»: aprueba el reporte pendiente con lo capturado o,
     * sin reportes, captura el resultado directo. */
    approveMatch: (token: string, tournamentId: number, matchId: number, payload: AdminApproveResultReportInput) =>
      apiFetch<{ ok: boolean; data: { match: TournamentMatch | null } }>(
        `/admin/tournaments/${tournamentId}/result-reports/by-match/${matchId}/approve`,
        { method: "POST", token, body: payload },
      ),
    /** «Pedir que reporten de nuevo»: rechaza los reportes pendientes. */
    rejectMatch: (token: string, tournamentId: number, matchId: number, payload: AdminRejectResultReportInput = {}) =>
      apiFetch<{ ok: boolean; data: { rejected: number } }>(
        `/admin/tournaments/${tournamentId}/result-reports/by-match/${matchId}/reject`,
        { method: "POST", token, body: payload },
      ),
  },
  referees: {
    overview: (token: string, tournamentId: number, date?: string | null) =>
      apiFetch<{ ok: boolean; data: RefereesOverview }>(
        `/admin/tournaments/${tournamentId}/referees${date ? `?date=${encodeURIComponent(date)}` : ""}`,
        { token },
      ),
    assign: (token: string, tournamentId: number, assignments: Array<{ match_id: number; referee_user_id: number | null }>) =>
      apiFetch<{ ok: boolean; data: { updated: number; matches: RefereeMatchRow[] } }>(
        `/admin/tournaments/${tournamentId}/referees/assignments`,
        { method: "PUT", token, body: { assignments } },
      ),
    updateSettings: (
      token: string,
      tournamentId: number,
      payload: { referee_publishes_directly?: boolean; chief_referee_date?: string; chief_referee_name?: string },
    ) =>
      apiFetch<{ ok: boolean; data: { referee_publishes_directly: boolean; chief_referees: Record<string, string> } }>(
        `/admin/tournaments/${tournamentId}/referees/settings`,
        { method: "PATCH", token, body: payload },
      ),
    notify: (token: string, tournamentId: number, date: string) =>
      apiFetch<{ ok: boolean; data: RefereeNotifySummary }>(`/admin/tournaments/${tournamentId}/referees/notify`, {
        method: "POST",
        token,
        body: { date },
      }),
  },
  referee: {
    myMatches: (token: string, date?: string | null) =>
      apiFetch<{ ok: boolean; data: MyRefereeMatches }>(
        `/referee/matches${date ? `?date=${encodeURIComponent(date)}` : ""}`,
        { token },
      ),
    submit: (
      token: string,
      matchId: number,
      payload: { score_text: string | null; winner_participant_id: number | null; exception?: RefereeException | null },
    ) =>
      apiFetch<{ ok: boolean; data: { status: "pending" | "published"; report_id: number; match_id: number } }>(
        `/referee/matches/${matchId}/result`,
        { method: "POST", token, body: payload },
      ),
  },
  player: {
    publicTournaments: (token: string, playerProfileId?: number | null) =>
      apiFetch<PlayerPublicTournamentsResponse>(`/player/public-tournaments${playerProfileQuery(playerProfileId)}`, {
        token,
      }),
    profile: (token: string, playerProfileId?: number | null) =>
      apiFetch<PlayerProfilePayloadResponse>(`/player/profile${playerProfileQuery(playerProfileId)}`, { token }),
    historicalResults: (token: string, playerProfileId?: number | null) =>
      apiFetch<PlayerHistoricalResultsResponse>(`/player/profile/results${playerProfileQuery(playerProfileId)}`, { token }),
    globalProfile: (token: string, playerProfileId: number) =>
      apiFetch<PlayerProfilePayloadResponse>(`/player/profiles/${playerProfileId}`, { token }),
    uploadAvatar: (token: string, file: File, playerProfileId?: number | null) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<{ ok: boolean; data: { profile: PlayerProfile } }>(
        `/player/profile/avatar${playerProfileQuery(playerProfileId)}`,
        formData,
        token,
      );
    },
    deleteAvatar: (token: string, playerProfileId?: number | null) =>
      apiFetch<{ ok: boolean; data: { profile: PlayerProfile } }>(
        `/player/profile/avatar${playerProfileQuery(playerProfileId)}`,
        { method: "DELETE", token },
      ),
    setPhotoVisibility: (token: string, showRegistrationPhoto: boolean, playerProfileId?: number | null) =>
      apiFetch<{ ok: boolean; data: { profile: PlayerProfile } }>(
        `/player/profile/photo-visibility${playerProfileQuery(playerProfileId)}`,
        { method: "POST", token, body: { show_registration_photo: showRegistrationPhoto } },
      ),
    listTournaments: (token: string) =>
      apiFetch<PlayerTournamentsResponse>("/player/tournaments", { token }),
    /** Documentos que el torneo le pide al perfil y cuáles faltan. */
    registrationDocuments: (token: string, tournamentId: number, playerProfileId?: number | null) =>
      apiFetch<{ ok: boolean; data: PlayerRegistrationDocumentsPayload }>(
        `/player/tournaments/${tournamentId}/registration-documents${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    /** Documentos pendientes de todos los perfiles de la cuenta, por torneo. */
    pendingRegistrationDocuments: (token: string) =>
      apiFetch<{ ok: boolean; data: { items: PlayerPendingDocumentsItem[] } }>(
        "/player/registration-documents/pending",
        { token },
      ),
    /** Sube un documento pendiente; `onProgress` recibe el avance de 0 a 1 y
     * `options.signal` permite cancelarla. */
    uploadRegistrationDocument: (
      token: string,
      tournamentId: number,
      playerProfileId: number,
      documentKey: string,
      file: File,
      onProgress?: (fraction: number) => void,
      options?: UploadWithProgressOptions,
    ) => {
      const formData = new FormData();
      formData.append("document_key", documentKey);
      formData.append("player_profile_id", String(playerProfileId));
      formData.append("file", file);
      return apiUploadWithProgress<{ ok: boolean; data: PlayerRegistrationDocumentsPayload }>(
        `/player/tournaments/${tournamentId}/registration-documents`,
        formData,
        token,
        onProgress,
        options,
      );
    },
    listCommunications: (token: string, playerProfileId?: number | null) =>
      apiFetch<PlayerCommunicationsResponse>(
        `/player/communications${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    listTournamentCommunications: (token: string, tournamentId: number, playerProfileId?: number | null) =>
      apiFetch<PlayerCommunicationsResponse>(
        `/player/tournaments/${tournamentId}/communications${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    getTournamentRule: (token: string, tournamentId: number, playerProfileId?: number | null) =>
      apiFetch<PlayerRuleResponse>(
        `/player/tournaments/${tournamentId}/rules${playerProfileQuery(playerProfileId)}`,
        { token },
      ),
    acceptTournamentRule: (
      token: string,
      tournamentId: number,
      ruleId: number,
      playerProfileId?: number | null,
    ) =>
      apiFetch<PlayerRuleAcceptanceResponse>(
        `/player/tournaments/${tournamentId}/rules/${ruleId}/accept${playerProfileQuery(playerProfileId)}`,
        { method: "POST", token },
      ),
    branding: (token: string, tournamentId: number) =>
      apiFetch<{
        ok: boolean;
        data: {
          name: string;
          logo_url: string | null;
          organizer_logo_url: string | null;
          player_banner_url: string | null;
          /** Todos los patrocinadores; el primero es player_banner_url. */
          player_banners?: Array<{ image_url: string; name: string | null; link: string | null }>;
          /** «Este torneo usa credencial»: sin el dato = sí (pestaña «Credencial»). */
          credential_enabled?: boolean;
        };
      }>(`/player/tournaments/${tournamentId}/branding`, { token }),
    h2h: (token: string, opponentProfileId: number, myProfileId?: number | null) =>
      apiFetch<H2HResponse>(
        `/player/h2h/${opponentProfileId}${myProfileId ? `?my_profile_id=${encodeURIComponent(String(myProfileId))}` : ""}`,
        { token },
      ),
    // Friends
    friends: {
      list: (token: string, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; data: FriendListResponse }>(`/player/friends${playerProfileQuery(playerProfileId)}`, { token }),
      search: (token: string, q: string, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; data: FriendSearchResult[] }>(
          appendPlayerProfileQuery(`/player/friends/search?q=${encodeURIComponent(q)}`, playerProfileId),
          { token },
        ),
      sendRequest: (token: string, targetProfileId: number, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; message: string }>(
          `/player/friends/request/${targetProfileId}${playerProfileQuery(playerProfileId)}`,
          { method: "POST", token },
        ),
      respond: (token: string, friendId: number, action: "accept" | "reject", playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; message: string }>(
          `/player/friends/respond/${friendId}${playerProfileQuery(playerProfileId)}`,
          { method: "POST", token, body: { action } },
        ),
      remove: (token: string, targetProfileId: number, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; message: string }>(
          `/player/friends/${targetProfileId}${playerProfileQuery(playerProfileId)}`,
          { method: "DELETE", token },
        ),
    },
    // Friendly matches
    friendlyMatches: {
      create: (token: string, body: FriendlyMatchCreate, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; data: FriendlyMatchOut }>(
          `/player/friendly-matches${playerProfileQuery(playerProfileId)}`,
          { method: "POST", token, body },
        ),
      reportResult: (token: string, matchId: number, body: FriendlyMatchResultUpdate, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; data: FriendlyMatchOut }>(
          `/player/friendly-matches/${matchId}/result${playerProfileQuery(playerProfileId)}`,
          { method: "PATCH", token, body },
        ),
      list: (token: string, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; data: FriendlyMatchOut[] }>(
          `/player/friendly-matches${playerProfileQuery(playerProfileId)}`,
          { token },
        ),
      listWith: (token: string, friendProfileId: number, playerProfileId?: number | null) =>
        apiFetch<{ ok: boolean; data: FriendlyMatchOut[] }>(
          `/player/friendly-matches/with/${friendProfileId}${playerProfileQuery(playerProfileId)}`,
          { token },
        ),
    },
  },
  communications: {
    /** Catálogo de mensajes del torneo con el correo real renderizado (Eduardo 2026-09-23). */
    messages: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: TournamentMessagesPayload }>(
        `/admin/tournaments/${tournamentId}/communications/messages`,
        { token },
      ),
    /** «Enviármelo de prueba»: correo, app y WhatsApp (si está conectado), solo a mí. */
    messageTestSend: (token: string, tournamentId: number, key: string) =>
      apiFetch<{ ok: boolean; data: MessageTestResult }>(
        `/admin/tournaments/${tournamentId}/communications/messages/${encodeURIComponent(key)}/test-send`,
        { method: "POST", token, body: {} },
      ),
    messageSuggest: (token: string, tournamentId: number, key: string, text: string) =>
      apiFetch<{ ok: boolean; data: { ticket_id: number } }>(
        `/admin/tournaments/${tournamentId}/communications/messages/${encodeURIComponent(key)}/suggest`,
        { method: "POST", token, body: { text } },
      ),
    /** Avisos automáticos por WhatsApp del torneo: a quién, de qué y si llegó. */
    autoNotices: (token: string, tournamentId: number) =>
      apiFetch<{ ok: boolean; data: AutoNoticesPayload }>(
        `/admin/tournaments/${tournamentId}/communications/auto-notices`,
        { token },
      ),
    /** Qué canales tiene conectados la plataforma (WhatsApp de Meta, email). */
    providersStatus: (token: string, tournamentId: number) =>
      apiFetch<{
        ok: boolean;
        data: { providers: Record<"web" | "whatsapp" | "email", { configured: boolean; provider?: string }> };
      }>(`/admin/tournaments/${tournamentId}/communications/providers/status`, { token }),
    list: (
      token: string,
      tournamentId: number,
      params: {
        status?: string;
        communication_type?: string;
        audience_type?: string;
      } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.status) qs.set("status", params.status);
      if (params.communication_type)
        qs.set("communication_type", params.communication_type);
      if (params.audience_type) qs.set("audience_type", params.audience_type);
      const query = qs.toString();
      return apiFetch<CommunicationsListResponse>(
        `/admin/tournaments/${tournamentId}/communications${
          query ? `?${query}` : ""
        }`,
        { token },
      );
    },
    create: (
      token: string,
      tournamentId: number,
      payload: CommunicationCreateInput,
    ) =>
      apiFetch<CommunicationResponse>(
        `/admin/tournaments/${tournamentId}/communications`,
        { method: "POST", token, body: payload },
      ),
    get: (token: string, tournamentId: number, communicationId: number) =>
      apiFetch<CommunicationResponse>(
        `/admin/tournaments/${tournamentId}/communications/${communicationId}`,
        { token },
      ),
    update: (
      token: string,
      tournamentId: number,
      communicationId: number,
      payload: CommunicationUpdateInput,
    ) =>
      apiFetch<CommunicationResponse>(
        `/admin/tournaments/${tournamentId}/communications/${communicationId}`,
        { method: "PATCH", token, body: payload },
      ),
    publish: (
      token: string,
      tournamentId: number,
      communicationId: number,
    ) =>
      apiFetch<CommunicationResponse>(
        `/admin/tournaments/${tournamentId}/communications/${communicationId}/publish`,
        { method: "POST", token },
      ),
    deliverEmail: (
      token: string,
      tournamentId: number,
      communicationId: number,
    ) =>
      apiFetch<DeliverySummaryResponse>(
        `/admin/tournaments/${tournamentId}/communications/${communicationId}/deliver-email`,
        { method: "POST", token },
      ),
    deliverWhatsapp: (
      token: string,
      tournamentId: number,
      communicationId: number,
    ) =>
      apiFetch<DeliverySummaryResponse>(
        `/admin/tournaments/${tournamentId}/communications/${communicationId}/deliver-whatsapp`,
        { method: "POST", token },
      ),
    remove: (token: string, tournamentId: number, communicationId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean } }>(
        `/admin/tournaments/${tournamentId}/communications/${communicationId}`,
        { method: "DELETE", token },
      ),
  },
  clubs: {
    // superadmin
    list: (token: string) =>
      apiFetch<{ ok: boolean; data: { clubs: ClubInfo[] } }>("/superadmin/clubs", {
        token,
      }),
    create: (
      token: string,
      payload: {
        name: string;
        contact_name?: string | null;
        contact_phone?: string | null;
        contact_email?: string | null;
        address_line?: string | null;
        neighborhood?: string | null;
        city?: string | null;
        state?: string | null;
        google_maps_url?: string | null;
      },
    ) =>
      apiFetch<{ ok: boolean; data: { club: ClubInfo } }>("/superadmin/clubs", {
        method: "POST",
        token,
        body: payload,
      }),
    assignStaff: (
      token: string,
      clubId: number,
      payload: {
        user_id?: number;
        new_user?: { full_name: string; email: string; password: string };
      },
    ) =>
      apiFetch<{
        ok: boolean;
        data: { user: { id: number; email: string; full_name: string } };
      }>(`/superadmin/clubs/${clubId}/staff`, {
        method: "POST",
        token,
        body: payload,
      }),
    // club staff
    me: (token: string) =>
      apiFetch<{ ok: boolean; data: { club: ClubInfo } }>("/club/me", { token }),
    myClubs: (token: string) =>
      apiFetch<{ ok: boolean; data: { clubs: Array<{ id: number; name: string; is_active: boolean }> } }>(
        "/club/my-clubs",
        { token },
      ),
    updateMe: (
      token: string,
      payload: Partial<
        Pick<
          ClubInfo,
          | "name"
          | "contact_name"
          | "contact_phone"
          | "contact_email"
          | "address_line"
          | "neighborhood"
          | "city"
          | "state"
          | "google_maps_url"
          | "logo_url"
        >
      >,
    ) =>
      apiFetch<{ ok: boolean; data: { club: ClubInfo } }>("/club/me", {
        method: "PATCH",
        token,
        body: payload,
      }),
    uploadLogo: (token: string, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<{ ok: boolean; data: { club: ClubInfo } }>("/club/me/logo", formData, token);
    },
    listCourts: (token: string) =>
      apiFetch<{ ok: boolean; data: { courts: ClubCourt[] } }>("/club/courts", {
        token,
      }),
    createCourt: (
      token: string,
      payload: { name: string; surface?: string | null; sport?: SportKey },
    ) =>
      apiFetch<{ ok: boolean; data: { court: ClubCourt } }>("/club/courts", {
        method: "POST",
        token,
        body: payload,
      }),
    updateCourt: (
      token: string,
      courtId: number,
      payload: Partial<Pick<ClubCourt, "name" | "surface" | "status" | "sort_order" | "sport">>,
    ) =>
      apiFetch<{ ok: boolean; data: { court: ClubCourt } }>(
        `/club/courts/${courtId}`,
        { method: "PATCH", token, body: payload },
      ),
    removeCourt: (token: string, courtId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean } }>(
        `/club/courts/${courtId}`,
        { method: "DELETE", token },
      ),
    setCourtHours: (token: string, courtId: number, hours: ClubCourtHoursEntry[]) =>
      apiFetch<{ ok: boolean; data: { court: ClubCourt } }>(
        `/club/courts/${courtId}/hours`,
        { method: "PUT", token, body: { hours } },
      ),
    listMembers: (token: string, clubId?: number | null) =>
      apiFetch<{ ok: boolean; data: { members: ClubMember[] } }>(withClubId("/club/members", clubId ?? null), {
        token,
      }),
    addMember: (
      token: string,
      payload: {
        player_profile_id?: number;
        new_player?: {
          first_name: string;
          last_name?: string | null;
          email?: string | null;
          phone?: string | null;
        };
      },
    ) =>
      apiFetch<{ ok: boolean; data: { member: ClubMember } }>("/club/members", {
        method: "POST",
        token,
        body: payload,
      }),
    updateMemberStatus: (token: string, memberId: number, status: string) =>
      apiFetch<{ ok: boolean; data: { member: ClubMember } }>(
        `/club/members/${memberId}`,
        { method: "PATCH", token, body: { status } },
      ),
    generateMemberCredential: (token: string, memberId: number) =>
      apiFetch<{ ok: boolean; data: { member: ClubMember } }>(
        `/club/members/${memberId}/generate-credential`,
        { method: "POST", token },
      ),
    inviteInfo: (slug: string, inviteToken: string) =>
      apiFetch<{
        ok: boolean;
        data: { club: { name: string; slug: string; contact_phone: string | null } };
      }>(`/public/clubs/${encodeURIComponent(slug)}/${encodeURIComponent(inviteToken)}`),
    inviteJoin: (token: string, slug: string, inviteToken: string, playerProfileId?: number | null) =>
      apiFetch<{
        ok: boolean;
        data: { member: ClubMember | null; already_member: boolean; pending_approval?: boolean };
      }>(
        `/public/clubs/${encodeURIComponent(slug)}/${encodeURIComponent(inviteToken)}/join${profileQuery(playerProfileId)}`,
        { method: "POST", token },
      ),
    superadminUpdate: (
      token: string,
      clubId: number,
      payload: { name?: string; contact_email?: string | null; is_active?: boolean } & Partial<ClubSettings>,
    ) =>
      apiFetch<{ ok: boolean; data: { club: ClubInfo } }>(`/superadmin/clubs/${clubId}`, {
        method: "PATCH",
        token,
        body: payload,
      }),
    createMine: (token: string, payload: { name: string; contact_email?: string | null; city?: string | null }) =>
      apiFetch<{ ok: boolean; data: { club: ClubInfo } }>("/club/mine", { method: "POST", token, body: payload }),
    getSettings: (token: string) =>
      apiFetch<{ ok: boolean; data: { settings: ClubSettings } }>("/club/settings", { token }),
    updateSettings: (token: string, payload: Partial<ClubSettings>) =>
      apiFetch<{ ok: boolean; data: { settings: ClubSettings } }>("/club/settings", {
        method: "PUT",
        token,
        body: payload,
      }),
    today: (token: string, date: string) =>
      apiFetch<{ ok: boolean; data: ClubToday }>(`/club/today?date=${encodeURIComponent(date)}`, { token }),
    pendingReservations: (token: string) =>
      apiFetch<{ ok: boolean; data: { reservations: ClubReservationInfo[] } }>("/club/reservations/pending", {
        token,
      }),
    approveReservation: (token: string, reservationId: number) =>
      apiFetch<{ ok: boolean; data: { reservation: ClubReservationInfo } }>(
        `/club/reservations/${reservationId}/approve`,
        { method: "POST", token },
      ),
    rejectReservation: (token: string, reservationId: number) =>
      apiFetch<{ ok: boolean; data: { reservation: ClubReservationInfo } }>(
        `/club/reservations/${reservationId}/reject`,
        { method: "POST", token },
      ),
    calendarClubs: (token: string) =>
      apiFetch<{
        ok: boolean;
        data: { clubs: Array<{ id: number; name: string; slug: string }> };
      }>("/club/calendar/clubs", { token }),
    calendarLayout: (token: string, clubId: number, date: string) =>
      apiFetch<{
        ok: boolean;
        data: {
          club: { id: number; name: string; slug: string };
          courts: Array<{
            id: number;
            name: string;
            surface: string | null;
            sport: SportKey | string;
            opens_at: string;
            closes_at: string;
            slot_minutes: number;
          }>;
          window: { opens_at: string; closes_at: string; slot_minutes: number } | null;
          reservations: ClubReservationInfo[];
        };
      }>(`/club/calendar/clubs/${clubId}/layout?date=${encodeURIComponent(date)}`, {
        token,
      }),
    createReservation: (
      token: string,
      payload: {
        court_id: number;
        date: string;
        start_time: string;
        end_time: string;
        member_id?: number | null;
        guest_names?: string | null;
      },
      clubId?: number | null,
    ) =>
      apiFetch<{ ok: boolean; data: { reservation: ClubReservationInfo } }>(
        withClubId("/club/reservations", clubId ?? null),
        { method: "POST", token, body: payload },
      ),
    cancelReservation: (token: string, reservationId: number, clubId?: number | null) =>
      apiFetch<{ ok: boolean; data: { reservation: ClubReservationInfo } }>(
        withClubId(`/club/reservations/${reservationId}/cancel`, clubId ?? null),
        { method: "POST", token },
      ),
    inviteRegister: (
      slug: string,
      inviteToken: string,
      payload: { full_name: string; email: string; password: string; phone?: string | null },
    ) =>
      apiFetch<{
        ok: boolean;
        data: { member: ClubMember | null; already_member: boolean; pending_approval?: boolean };
      }>(
        `/public/clubs/${encodeURIComponent(slug)}/${encodeURIComponent(inviteToken)}/register`,
        { method: "POST", body: payload },
      ),
    getRules: (token: string) =>
      apiFetch<{ ok: boolean; data: { rules: ClubBookingRules } }>("/club/rules", {
        token,
      }),
    updateRules: (token: string, payload: Partial<ClubBookingRules>) =>
      apiFetch<{ ok: boolean; data: { rules: ClubBookingRules } }>("/club/rules", {
        method: "PUT",
        token,
        body: payload,
      }),
    /** Cuenta familiar: clubes, solicitudes y membresías del hijo elegido. */
    playerClubs: (
      token: string,
      playerProfileId?: number | null,
      filters: { q?: string; city?: string } = {},
    ) => {
      const params = new URLSearchParams();
      if (playerProfileId) params.set("player_profile_id", String(playerProfileId));
      if (filters.q?.trim()) params.set("q", filters.q.trim());
      if (filters.city?.trim()) params.set("city", filters.city.trim());
      const query = params.toString();
      return apiFetch<PlayerClubMarketplaceResponse>(`/player/clubs${query ? `?${query}` : ""}`, { token });
    },
    playerApplyToClub: (
      token: string,
      clubId: number,
      payload: { message?: string | null } = {},
      playerProfileId?: number | null,
    ) =>
      apiFetch<{ ok: boolean; data: { request: PlayerClubApplication } }>(
        `/player/clubs/${clubId}/apply${playerProfileId ? `?player_profile_id=${encodeURIComponent(String(playerProfileId))}` : ""}`,
        { method: "POST", token, body: payload },
      ),

    getMembershipPaymentSettings: (token: string) =>
      apiFetch<{ ok: boolean; data: { settings: ClubMembershipPaymentSettings } }>(
        "/club/membership-payment-settings",
        { token },
      ),
    updateMembershipPaymentSettings: (
      token: string,
      payload: { membership_fee_amount: number; currency: string; payment_instructions?: string | null },
    ) =>
      apiFetch<{ ok: boolean; data: { settings: ClubMembershipPaymentSettings } }>(
        "/club/membership-payment-settings",
        { method: "PUT", token, body: payload },
      ),
    listMembershipPaymentOrders: (token: string) =>
      apiFetch<{ ok: boolean; data: { orders: ClubMembershipPaymentOrder[] } }>(
        "/club/membership-payment-orders",
        { token },
      ),
    confirmMembershipPaymentOrder: (token: string, orderId: number) =>
      apiFetch<{ ok: boolean; data: { order: ClubMembershipPaymentOrder } }>(
        `/club/membership-payment-orders/${orderId}/confirm`,
        { method: "POST", token },
      ),
    rejectMembershipPaymentOrder: (token: string, orderId: number) =>
      apiFetch<{ ok: boolean; data: { order: ClubMembershipPaymentOrder } }>(
        `/club/membership-payment-orders/${orderId}/reject`,
        { method: "POST", token },
      ),
    listMembershipRequests: (token: string) =>
      apiFetch<{ ok: boolean; data: { requests: PlayerClubApplication[] } }>(
        "/club/membership-requests",
        { token },
      ),
    approveMembershipRequest: (token: string, requestId: number) =>
      apiFetch<{ ok: boolean; data: { request: PlayerClubApplication } }>(
        `/club/membership-requests/${requestId}/approve`,
        { method: "POST", token },
      ),
    rejectMembershipRequest: (token: string, requestId: number) =>
      apiFetch<{ ok: boolean; data: { request: PlayerClubApplication } }>(
        `/club/membership-requests/${requestId}/reject`,
        { method: "POST", token },
      ),

    playerUploadMembershipReceipt: (token: string, orderId: number, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiUpload<{ ok: boolean; data: { order: ClubMembershipPaymentOrder } }>(
        `/player/clubs/membership-payment-orders/${orderId}/receipt`,
        formData,
        token,
      );
    },
    playerAvailability: (token: string, clubId: number, date: string, playerProfileId?: number | null) =>
      apiFetch<{
        ok: boolean;
        data: {
          club: { id: number; name: string; slug: string };
          date: string;
          rules: ClubBookingRules;
          reservation_confirmation?: "auto" | "club_approves" | string;
          week_usage?: PlayerClubWeekUsage;
          courts: PlayerAvailabilityCourt[];
        };
      }>(
        `/player/clubs/${clubId}/availability?date=${encodeURIComponent(date)}${
          playerProfileId ? `&player_profile_id=${encodeURIComponent(String(playerProfileId))}` : ""
        }`,
        { token },
      ),
    playerBook: (
      token: string,
      clubId: number,
      payload: { court_id: number; date: string; start_time: string },
      playerProfileId?: number | null,
    ) =>
      apiFetch<{ ok: boolean; data: { reservation: ClubReservationInfo } }>(
        `/player/clubs/${clubId}/reservations${profileQuery(playerProfileId)}`,
        { method: "POST", token, body: payload },
      ),
    playerMyReservations: (token: string, clubId: number, playerProfileId?: number | null) =>
      apiFetch<{ ok: boolean; data: { reservations: ClubReservationInfo[] } }>(
        `/player/clubs/${clubId}/my-reservations${profileQuery(playerProfileId)}`,
        { token },
      ),
    playerCancelReservation: (token: string, reservationId: number) =>
      apiFetch<{
        ok: boolean;
        data: { reservation: ClubReservationInfo; late: boolean };
      }>(`/player/reservations/${reservationId}/cancel`, { method: "POST", token }),
  },
  rules: {
    list: (token: string, tournamentId: number) =>
      apiFetch<RulesListResponse>(
        `/admin/tournaments/${tournamentId}/rules`,
        { token },
      ),
    current: (token: string, tournamentId: number) =>
      apiFetch<RuleCurrentResponse>(
        `/admin/tournaments/${tournamentId}/rules/current`,
        { token },
      ),
    create: (
      token: string,
      tournamentId: number,
      payload: RuleCreateInput = {},
    ) =>
      apiFetch<RuleResponse>(
        `/admin/tournaments/${tournamentId}/rules`,
        { method: "POST", token, body: payload },
      ),
    get: (token: string, tournamentId: number, ruleId: number) =>
      apiFetch<RuleResponse>(
        `/admin/tournaments/${tournamentId}/rules/${ruleId}`,
        { token },
      ),
    update: (
      token: string,
      tournamentId: number,
      ruleId: number,
      payload: RuleUpdateInput,
    ) =>
      apiFetch<RuleResponse>(
        `/admin/tournaments/${tournamentId}/rules/${ruleId}`,
        { method: "PATCH", token, body: payload },
      ),
    publish: (token: string, tournamentId: number, ruleId: number) =>
      apiFetch<RuleResponse>(
        `/admin/tournaments/${tournamentId}/rules/${ruleId}/publish`,
        { method: "POST", token },
      ),
    uploadOriginalPdf: (token: string, tournamentId: number, ruleId: number, file: File) =>
      apiUpload<RuleResponse>(
        `/admin/tournaments/${tournamentId}/rules/${ruleId}/original-pdf`,
        rulePdfFormData(file),
        token,
      ),
    removeAttachment: (token: string, tournamentId: number, ruleId: number, index: number) =>
      apiFetch<RuleResponse>(
        `/admin/tournaments/${tournamentId}/rules/${ruleId}/original-pdf/${index}`,
        { method: "DELETE", token },
      ),
    remove: (token: string, tournamentId: number, ruleId: number) =>
      apiFetch<{ ok: boolean; data: { deleted: boolean } }>(
        `/admin/tournaments/${tournamentId}/rules/${ruleId}`,
        { method: "DELETE", token },
      ),
    acceptances: (token: string, tournamentId: number, ruleId: number) =>
      apiFetch<RuleAcceptancesResponse>(
        `/admin/tournaments/${tournamentId}/rules/${ruleId}/acceptances`,
        { token },
      ),
  },
  dashboards: {
    admin: (token: string) =>
      apiFetch<AdminDashboardResponse>("/admin/dashboard", { token }),
    superadmin: (token: string) =>
      apiFetch<SuperadminDashboardResponse>("/superadmin/dashboard", { token }),
    /** `tournamentId` (home y credencial del torneo): sin perfil elegido, el
     * servidor usa al hijo de la cuenta inscrito en ESE torneo. */
    player: (token: string, playerProfileId?: number | null, options: { tournamentId?: number | null } = {}) => {
      const params = new URLSearchParams();
      if (playerProfileId) params.set("player_profile_id", String(playerProfileId));
      if (options.tournamentId) params.set("tournament_id", String(options.tournamentId));
      const query = params.toString();
      return apiFetch<PlayerDashboardResponse>(`/player/dashboard${query ? `?${query}` : ""}`, { token });
    },
    /** Perfiles de la cuenta familiar y en qué torneos juega cada uno. */
    playerFamilyProfiles: (token: string) =>
      apiFetch<PlayerFamilyProfilesResponse>("/player/family-profiles", { token }),
    playerPaymentOrders: (token: string) =>
      apiFetch<{ ok: boolean; data: { orders: PlayerPaymentOrder[] } }>(
        "/player/payment-orders",
        { token },
      ),
  },
  notifications: {
    listForPlayer: (token: string, params: NotificationListParams = {}) =>
      apiFetch<NotificationsListResponse>(
        `/player/notifications${_notificationsQuery(params)}`,
        { token },
      ),
    markPlayerRead: (token: string, notificationId: number) =>
      apiFetch<NotificationResponse>(
        `/player/notifications/${notificationId}/read`,
        { method: "POST", token },
      ),
    markPlayerAllRead: (token: string, playerProfileId?: number | null) =>
      apiFetch<NotificationsReadAllResponse>(
        `/player/notifications/read-all${playerProfileQuery(playerProfileId)}`,
        { method: "POST", token },
      ),
    listForAdmin: (token: string, params: NotificationListParams = {}) =>
      apiFetch<NotificationsListResponse>(
        `/admin/notifications${_notificationsQuery(params)}`,
        { token },
      ),
    markAdminRead: (token: string, notificationId: number) =>
      apiFetch<NotificationResponse>(
        `/admin/notifications/${notificationId}/read`,
        { method: "POST", token },
      ),
    markAdminAllRead: (token: string) =>
      apiFetch<NotificationsReadAllResponse>(
        "/admin/notifications/read-all",
        { method: "POST", token },
      ),
  },
  supportTickets: {
    list: (token: string, status?: "open" | "resolved") =>
      apiFetch<{ ok: boolean; data: { tickets: SupportTicket[] } }>(
        `/ayuda123/tickets${status ? `?status=${status}` : ""}`,
        { token },
      ),
    update: (token: string, ticketId: number, payload: { status: "open" | "resolved"; resolution_note?: string | null }) =>
      apiFetch<{ ok: boolean; data: { ticket: SupportTicket } }>(`/ayuda123/tickets/${ticketId}`, {
        method: "PATCH",
        token,
        body: payload,
      }),
  },
  push: {
    publicKey: () => apiFetch<PushPublicKeyResponse>("/push/public-key"),
    subscribe: (token: string, payload: PushSubscribePayload) =>
      apiFetch<{ ok: boolean }>("/push/subscriptions", {
        method: "POST",
        token,
        body: payload,
      }),
    unsubscribe: (token: string, endpoint: string) =>
      apiFetch<{ ok: boolean }>("/push/subscriptions", {
        method: "DELETE",
        token,
        body: { endpoint },
      }),
  },
};
